import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useOutletContext, Link } from 'react-router-dom';
import {
  Shuffle, CheckCircle, XCircle, Clock, MapPin, Users,
  Send, Zap, MessageSquare, ChevronLeft, AlertTriangle, Trophy,
  MoreHorizontal, Mail, X, DollarSign, Lock, Unlock,
  Image as ImageIcon,
} from 'lucide-react';
import api from '../services/api.js';
import { useAuth } from '../context/AuthContext.jsx';
import { usePWAContext } from '../context/PWAContext.jsx';
import { useTimeFormat } from '../hooks/useTimeFormat.js';
import { useTeamRole } from '../hooks/useTeamRole.js';
import { useDeviceDetect } from '../hooks/useDeviceDetect.js';
import { useKeyboardSafeSheet } from '../hooks/useKeyboardSafeSheet.js';
import { useGameVersionPoll } from '../hooks/useGameVersionPoll.js';
// Apr 30 2026 (App_Change_List #20) — RinkDiagram removed from
// the balancing view. Import dropped to keep the bundle lean.
// import RinkDiagram from '../components/RinkDiagram.jsx';
import Avatar from '../components/shared/Avatar.jsx';
import MessageChannelPicker, { DEFAULT_CHANNELS as DEFAULT_MSG_CHANNELS } from '../components/shared/MessageChannelPicker.jsx';
import ArenaSearch from '../components/shared/ArenaSearch.jsx';
import ModalCloseButton from '../components/shared/ModalCloseButton.jsx';
// 2026-09-19 (Phase 7A) - per-game stat tables + entry grid.
import GameStatsPanel from '../components/stats/GameStatsPanel.jsx';
// 2026-09-22 — bot ribbing for repeat no-shows who check in again.
import { noShowQuip, noShowBadgeLabel } from '../constants/noShowQuips.js';
import { mapsUrl } from '../utils/mapsLink.js';
// Apr 2026 (Tom_Notes B12) — re-use the dashboard's reminders card
// inside the GamePage so coaches can see WHAT'S queued up for THIS
// game without leaving the page (e.g. "the auto-jab fires in 4h").
// The card is already collapsible and has a per-gameId filter so
// the integration is essentially "fetch + render".
import { UpcomingRemindersCard, DrinksDashboardTile } from './DashboardPage.jsx';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { formatDuration } from '../utils/formatDuration.js';
import { computeEndTime } from '../utils/computeEndTime.js';

// ── Pure JS confetti (no dependencies) ──
function launchConfetti(color = '#00d4ff') {
  const canvas = document.createElement('canvas');
  canvas.id = 'confetti-canvas';
  canvas.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:9999;width:100%;height:100%';
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;

  const colors = [color, '#00ff88', '#ff9500', '#ffffff', '#7dd3b8', '#ff6b35'];
  const particles = Array.from({ length: 120 }, () => ({
    x: Math.random() * canvas.width,
    y: -10 - Math.random() * 100,
    vx: (Math.random() - 0.5) * 4,
    vy: 2 + Math.random() * 4,
    size: 4 + Math.random() * 8,
    color: colors[Math.floor(Math.random() * colors.length)],
    rot: Math.random() * Math.PI * 2,
    rotV: (Math.random() - 0.5) * 0.2,
    shape: Math.random() > 0.5 ? 'rect' : 'circle',
    gravity: 0.08 + Math.random() * 0.05,
    alpha: 1,
  }));

  let frame;
  const animate = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    let alive = 0;
    particles.forEach(p => {
      p.x += p.vx; p.y += p.vy; p.vy += p.gravity; p.rot += p.rotV;
      if (p.y > canvas.height * 0.7) p.alpha -= 0.025;
      if (p.alpha <= 0 || p.y > canvas.height + 20) return;
      alive++;
      ctx.save(); ctx.globalAlpha = Math.max(0, p.alpha);
      ctx.translate(p.x, p.y); ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      if (p.shape === 'rect') ctx.fillRect(-p.size/2, -p.size/4, p.size, p.size/2);
      else { ctx.beginPath(); ctx.arc(0, 0, p.size/2, 0, Math.PI*2); ctx.fill(); }
      ctx.restore();
    });
    if (alive > 0) frame = requestAnimationFrame(animate);
    else { canvas.remove(); }
  };
  frame = requestAnimationFrame(animate);
  setTimeout(() => { cancelAnimationFrame(frame); canvas.remove(); }, 4000);
}

// ── Menu item helper for the per-player action popover ────────────
// Small icon+label row. Dims on disabled (e.g. Mark Out when already out)
// so the coach sees the current state without clutter.
function MenuItem({ icon, label, onClick, disabled }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        width: '100%', padding: '8px 10px',
        background: 'transparent', border: 'none',
        color: disabled ? 'var(--text-3)' : 'var(--text)',
        fontSize: 13, fontWeight: 500, textAlign: 'left',
        cursor: disabled ? 'default' : 'pointer',
        borderRadius: 6,
        opacity: disabled ? 0.5 : 1,
        transition: 'background 0.1s',
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = 'var(--surface-2)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
      <span style={{ fontSize: 14, width: 16, display: 'inline-flex', justifyContent: 'center' }}>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

// Extract a user-facing error string from an axios/fetch error.
// Priority: server-provided `message` (we use this for helpful
// server-side copy like "This team's coach is still setting up
// their Stripe account") > server-provided `error` > caller's
// fallback. Never leaks stack traces or internal details.
const friendlyError = (err, fallback = 'Something went wrong. Try again.') => {
  const data = err?.response?.data;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  if (typeof data?.error === 'string' && data.error.trim())     return data.error;
  if (typeof err?.message === 'string' && err.message.trim() && err.message !== 'Network Error') {
    return err.message;
  }
  return fallback;
};

// 2026-07-17 (coach requirement) — shared recipient-list renderer for the
// "Message Players" modal. Used for BOTH the server-resolved preview
// (all scopes except "Pick Players") and the "Pick Players" scope (which
// is already exact client-side) so the two render identically. Shows the
// first `previewCount` names inline with a "+X more" toggle that expands
// to a scrollable, height-capped full list — keeps the modal from
// growing unbounded on a large "Everyone" blast, especially on phones.
function GroupMessageRecipientList({ items, expanded, onToggleExpand, previewCount = 6 }) {
  if (!items || items.length === 0) return null;
  const shown = expanded ? items : items.slice(0, previewCount);
  const remaining = items.length - previewCount;
  return (
    <div>
      <div
        style={expanded ? { maxHeight: 160, overflowY: 'auto', lineHeight: 1.7 } : { lineHeight: 1.5 }}
      >
        {shown.map((r, idx) => (
          // 📨 marks pending-invite rows (isInvite) — people who don't
          // have an account yet, resolved from team_invites rather than
          // users. Key falls back to name+index since invite rows have
          // no userId.
          <span key={r.userId || `${r.name}-${idx}`}>
            {r.name}{r.isInvite ? ' 📨' : ''}{idx < shown.length - 1 ? ', ' : ''}
          </span>
        ))}
      </div>
      {!expanded && remaining > 0 && (
        <button
          type="button"
          onClick={onToggleExpand}
          style={{
            background: 'none', border: 'none', padding: 0, marginTop: 2,
            color: 'var(--ice)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>
          + {remaining} more
        </button>
      )}
      {expanded && items.length > previewCount && (
        <button
          type="button"
          onClick={onToggleExpand}
          style={{
            background: 'none', border: 'none', padding: 0, marginTop: 4,
            color: 'var(--ice)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>
          Show less
        </button>
      )}
    </div>
  );
}

// 2026-07-17 — recipient-count banner wrapping GroupMessageRecipientList.
// Three states:
//   - no data yet (items is null/undefined)         → spinner + "resolving…"
//   - data loaded but empty (count === 0)            → orange "no match" warning
//   - data loaded with recipients                    → ice banner + name list,
//     with a small "resolving…" tag layered on top while a re-fetch for a
//     newer scope/audience is in flight (stale-while-revalidate UX so the
//     modal doesn't flash empty between scope switches).
function GroupMessageRecipientPreview({ total, items, loading, expanded, onToggleExpand, emptyLabel }) {
  const hasData = Array.isArray(items);
  const count = hasData ? (total ?? items.length) : null;
  const isEmpty = hasData && count === 0;
  return (
    <div
      className="text-xs mb-4 px-3 py-2 rounded-lg"
      style={{
        background: isEmpty ? 'rgba(255,149,0,0.08)' : 'var(--ice-a5)',
        border: `1px solid ${isEmpty ? 'rgba(255,149,0,0.2)' : 'var(--ice-a15)'}`,
        color: isEmpty ? 'var(--puck)' : 'var(--ice)',
      }}>
      {!hasData ? (
        <div className="flex items-center gap-2" style={{ color: 'var(--text-3)' }}>
          <div className="spinner" style={{ width: 12, height: 12 }} /> resolving recipients…
        </div>
      ) : isEmpty ? (
        `⚠ ${emptyLabel}`
      ) : (
        <>
          <div className="font-semibold mb-1 flex items-center gap-2 flex-wrap">
            <span>✉ {count} recipient{count !== 1 ? 's' : ''}</span>
            {loading && (
              <span style={{ color: 'var(--text-3)', fontWeight: 400, fontSize: 11 }}>resolving…</span>
            )}
          </div>
          <div style={{ color: 'var(--text-2)', fontWeight: 400 }}>
            <GroupMessageRecipientList items={items} expanded={expanded} onToggleExpand={onToggleExpand} />
          </div>
        </>
      )}
    </div>
  );
}

export default function GamePage() {
  const { gameId } = useParams();
  const { user } = useAuth();
  const { offline } = usePWAContext();
  const navigate = useNavigate();
  const location = useLocation();
  // 2026-07-21 (multi-team email-link landing audit) — the layout that
  // renders this page (Desktop/Mobile/Tablet/CapacitorLayout, all via
  // DeviceRouter) provides { teams, activeTeam, setActiveTeam, ... } via
  // Outlet context. Used below in resolveGame() to correct a stale
  // activeTeam once we know which team THIS game actually belongs to.
  // Optional chaining everywhere it's read — GamePage always mounts
  // inside that Outlet in practice, but this keeps it from throwing if
  // that ever isn't true (e.g. an isolated test render).
  const outletCtx = useOutletContext();

  // May 4 2026 — scroll-on-hash: when the page lands with a #section
  // hash (e.g. #drinks-duty from the dashboard's drinks card), scroll
  // the matching id into view. Runs after a small delay so the
  // section we're targeting has actually rendered (the drinks panel
  // is mounted only after teamId/gameId resolve, ~one tick after
  // first paint). Re-runs whenever the hash changes so back/forward
  // nav within the page still scrolls.
  useEffect(() => {
    const hash = location.hash;
    if (!hash) return;
    const id = hash.replace(/^#/, '');
    if (!id) return;
    let cancelled = false;
    // Two attempts: one quick (handles most cases), one delayed
    // (handles the case where the section hadn't yet rendered).
    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const t1 = setTimeout(tryScroll, 100);
    const t2 = setTimeout(tryScroll, 600);
    return () => { cancelled = true; clearTimeout(t1); clearTimeout(t2); };
  }, [location.hash]);

  // Safely extract YYYY-MM-DD from game_date regardless of type (string or Date object)
  const safeDate = (d) => {
    if (!d) return '';
    if (typeof d === 'string') return d.slice(0, 10);
    if (d instanceof Date) return d.toISOString().slice(0, 10);
    return String(d).slice(0, 10);
  };

  // ── Handle Stripe cancelled return ─────────────────────────────
  // Success now redirects to /payment/success — only cancelled comes back here.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'cancelled') {
      toast('Payment cancelled — your spot was not reserved.', { icon: '⚠️', duration: 4000 });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const [game, setGame]           = useState(null);
  const [teamId, setTeamId]       = useState(null);
  const teamIdRef                 = useRef(null); // ref so startAiPoll always has current value
  const { isCoach, isManager }    = useTeamRole(teamId);
  // Apr 2026 (Tom_Notes B12) — per-game reminders cache. Lazy
  // loaded only for coaches once teamId is resolved. The card
  // renders nothing on an empty list so we don't fire the request
  // for non-coach viewers (they wouldn't see the card anyway and
  // the endpoint 403s them).
  const [gameReminders, setGameReminders] = useState([]);
  const { isMobilePhone }         = useDeviceDetect();
  // Narrow shorthand — the availability header re-stacks on phones so
  // count pills don't wrap onto 4+ lines competing with the title and
  // Collapse/Edit buttons for horizontal space.
  const isPhone                   = isMobilePhone;
  const { formatTime }            = useTimeFormat();
  const [teams, setTeams]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [balancing, setBalancing] = useState(false);
  // v2.5.102 — manual "Send Teams" button. Coach feedback after a game
  // where the auto-balance cron failed to fire: "Create a button that
  // manually sends out the teams (emails everyone). Put it on the
  // game page before the Lock Teams button." Calls
  // POST /:gameId/send-team-assignments which reads the existing
  // game_rosters and emails each player without re-balancing. Safe
  // on locked games (the common case).
  const [sendingTeams, setSendingTeams] = useState(false);
  const [aiPolling, setAiPolling] = useState(false);
  const pollRef                   = useRef(null);
  // ── Stale-data poll baseline (Apr 2026) ──
  // Holds the server-side `version` timestamp from the most recent full
  // game load. useGameVersionPoll compares it against the /version
  // endpoint every 20s and flips `stale` on when someone else makes a
  // change (check-in, promote, pay, arena edit, etc.). After every
  // local refresh we re-seed this from the response so the user's own
  // actions never false-trigger the banner.
  const gameVersionRef = useRef(null);
  // ── Silent auto-refresh (Apr 2026) ──
  // When ANY user action advances the server's game fingerprint (a
  // spare checks in, a coach on another device promotes someone, a
  // payment lands), the poll calls refreshRef.current() which pulls
  // the full game and re-renders the availability list in place.
  // Ref-indirection is required because `refresh` isn't defined yet
  // at the point we need to pass it here (it's declared later in the
  // component). The ref is populated on the line right after refresh
  // is defined; the hook reads it lazily via a stable arrow so the
  // useCallback in the hook doesn't need `refresh` as a dependency.
  const refreshRef = useRef(() => {});
  useGameVersionPoll(teamId, gameId, gameVersionRef, {
    onChanged: () => refreshRef.current(),
  });

  const [showResult, setShowResult]           = useState(false);
  const [result, setResult]                   = useState({ homeScore: '', awayScore: '' });
  const [showArenaChange, setShowArenaChange] = useState(false);
  const [arenaForm, setArenaForm]             = useState({ newArena: '', newAddress: '', reason: '' });
  const [myStatus, setMyStatus]               = useState(null);
  const [myNotes,  setMyNotes]                = useState('');  // own RSVP note
  const [myFeedback, setMyFeedback]           = useState(null);
  // Apr 2026 (Tom_Notes K2) — busy flag to prevent double-clicks on
  // the I'm In / I'm Out / Maybe buttons. Multiple rapid taps used
  // to fire multiple POSTs, which the backend handled idempotently
  // but left the user staring at "is it processing?" UX. Setting
  // this true at the start of setAvailability and false in the
  // finally block disables the buttons for the round-trip.
  const [rsvpBusy, setRsvpBusy]               = useState(false);
  // Credits (Apr 2026) — user's available credit for THIS game's
  // currency, populated on mount. Used by the Pay Now CTA to offer
  // "Use $X credit" as an alternative to card payment.
  const [myCreditCents, setMyCreditCents]     = useState(0);
  const [creditBusy,    setCreditBusy]        = useState(false);
  // ── Note for the team ────────────────────────────────────────────
  // Inline note field rendered under the I'm In / I'm Out buttons.
  // Decoupled from the RSVP action so that checking in/out stays a
  // one-tap motion; the note is an opt-in second action. `noteDraft`
  // holds unsaved edits; a Save button appears when it diverges from
  // the canonical `myNotes`. `savingNote` gates the button during the
  // in-flight POST to /availability/notes.
  const [noteDraft,   setNoteDraft]   = useState('');
  const [savingNote,  setSavingNote]  = useState(false);
  const [attendance, setAttendance]           = useState({});
  const [countdown, setCountdown]             = useState('');
  const [overrideMode, setOverrideMode]       = useState(false);
  const [selected, setSelected]               = useState({}); // userId → true
  const [overriding, setOverriding]           = useState(false);
  // Individual player actions (coach tap on a player row) — shows a small
  // dropdown menu with Check In / Out / Waitlist / No Response / Message.
  // Stores the anchor's viewport rect + the player object so the menu can
  // render via position:fixed (immune to list-scroll clipping) and the
  // action handlers can reference the player even though they live outside
  // the row in the JSX tree.
  const [playerMenuFor, setPlayerMenuFor] = useState(null); // { key, rect, player } or null
  // 2026-08-20 (no-show list) — late-arrival modal target. Set when the
  // coach clears a no-show on a live game: the player showed up after
  // all, so the coach picks what happens next (check in + assign to a
  // team manually, check in + AI re-balance, or just clear the flag).
  const [lateArrivalFor, setLateArrivalFor] = useState(null); // player or null
  // 2026-09-10 (mig 110) — RSVP audit trail viewer. { player, rows|null, error }
  const [rsvpHistoryFor, setRsvpHistoryFor] = useState(null);
  const [lateArrivalBusy, setLateArrivalBusy] = useState(false);
  // Compose-message modal state. Can be opened with a single recipient
  // (from the player menu) or with a recipient list (from a future "Message
  // Players" button at the top of the page).
  const [composeOpen,    setComposeOpen]    = useState(false);
  const [composeTargets, setComposeTargets] = useState([]); // [{userId, name}]
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody,    setComposeBody]    = useState('');
  const [composeSending, setComposeSending] = useState(false);
  // Apr 2026 (Tom_Notes B8) — channel routing. Defaults to email
  // for back-compat; the user can flip in_app/push toggles before
  // sending. We keep the state as an array so the API payload is
  // a clean { channels: [...] }.
  const [composeChannels, setComposeChannels] = useState(['email']);
  // Group-message state (coach clicks "Email Players" in Coach Actions).
  // Separate from single-player compose so the per-row "Message Player"
  // flow stays simple. The picker state defaults to "everyone who checked
  // in" — the most common coach use case (confirm arena change, warn of
  // late start, coordinate pre-game meet).
  const [groupMsgOpen,       setGroupMsgOpen]       = useState(false);
  // 2026-09-17 (Phase 4) — scopes: checked_in | checked_in_wait | waitlist_only | everyone | ft_no_response | invited_no_response | specific. All but 'specific' resolve server-side (messages.js).
  const [groupMsgScope,      setGroupMsgScope]      = useState('checked_in');
  const [groupMsgIncludeWait,setGroupMsgIncludeWait]= useState(false);
  // Apr 30 2026 — when scope === 'specific', this Set holds userIds the
  // coach has selected via the search-and-pick UI. Stored as Set rather
  // than array for O(1) toggle/has lookups while clicking through a
  // long roster. Reset to empty when the modal opens.
  const [groupMsgPickedIds, setGroupMsgPickedIds] = useState(() => new Set());
  // Search query for the specific-player picker. Filters by player name.
  const [groupMsgSearch, setGroupMsgSearch] = useState('');
  const [groupMsgSubject,    setGroupMsgSubject]    = useState('');
  const [groupMsgBody,       setGroupMsgBody]       = useState('');
  const [groupMsgSending,    setGroupMsgSending]    = useState(false);
  // Apr 2026 (Tom_Notes B8) — channel routing for the game-page
  // group-message blast. Default email; coach can opt in to
  // push/in-app for urgent ones (e.g. "Game cancelled, ice rink
  // flooding!").
  const [groupMsgChannels,   setGroupMsgChannels]   = useState(DEFAULT_MSG_CHANNELS); // 2026-09-17 — default 'prefs' (player preferences)
  // 2026-07-17 (coach requirement) — server-resolved recipient preview.
  // Replaces the old client-computed / "~N likely qualify" previews so
  // every scope shows the EXACT list /send will use (see the useEffect
  // below and POST /teams/:teamId/messages/preview in messages.js).
  // `recipients` is null until the first fetch resolves (or the scope
  // needs no fetch); `error` triggers a fallback to the old client-side
  // estimate so the modal never blocks sending on a failed preview call.
  const [groupMsgPreview, setGroupMsgPreview] = useState({ loading: false, error: false, total: null, recipients: null });
  const [groupMsgPreviewExpanded, setGroupMsgPreviewExpanded] = useState(false);
  // Monotonic id guarding against a slow, stale preview response
  // clobbering a faster, newer one (e.g. coach taps "Everyone" then
  // "Checked In" quickly — the "Everyone" request may resolve last).
  const groupMsgPreviewReqId = useRef(0);

  // 2026-07-17 (coach requirement) — scope→candidate resolution for the
  // "Message Players" modal, hoisted out of the modal's render body (it
  // used to live inline inside the modal's conditional IIFE) into a memo
  // so it's reachable from the preview-fetch effect below. Hooks must run
  // unconditionally every render, so an effect can't read locals scoped
  // inside `{groupMsgOpen && (() => {...})()}` — this memo is the shared
  // computation both the effect and the modal body read from, so they
  // can never disagree about "who's in scope" for the four client-side
  // scopes (checked_in/everyone/all_responded/waitlist_only/specific).
  // Logic is a verbatim copy of the original inline `inScope` predicate.
  const groupMsgCandidates = useMemo(() => {
    if (!game) return { candidates: [], isFTResponseScope: false };
    const roster = (game.all_members || []).filter(m => m.userId);
    const avById = {};
    for (const a of (game.availability || [])) {
      if (a.userId) avById[a.userId] = a.status;
    }
    // 2026-09-17 (Phase 4) — every scope except "Pick Players" is a
    // server-resolved GROUP now (messages.js resolveRecipients, over the
    // whole mini-game session). `isFTResponseScope` is kept as the name
    // for "send `group`, not `userIds`" so the effect + send paths below
    // don't change shape. The client-side predicate only remains as the
    // fallback estimate shown if /preview fails.
    const isFTResponseScope = groupMsgScope !== 'specific';
    const inScope = (m) => {
      const s = avById[m.userId];
      const isWaitlistish = s === 'waitlist' || s === 'pending_payment';
      switch (groupMsgScope) {
        case 'checked_in':
          return s === 'in';
        case 'checked_in_wait':
          return s === 'in' || isWaitlistish;
        case 'waitlist_only':
          return isWaitlistish;
        case 'specific':
          return groupMsgPickedIds.has(m.userId);
        case 'ft_no_response': {
          const isFullTimePlayer = m.isPrimary === true && m.role !== 'coach' && m.role !== 'manager';
          const hasResponded = s === 'in' || s === 'out' || isWaitlistish;
          return isFullTimePlayer && !hasResponded;
        }
        case 'invited_no_response': {
          const isPlayer = m.role !== 'coach' && m.role !== 'manager';
          const hasResponded = s === 'in' || s === 'out' || isWaitlistish;
          return isPlayer && !hasResponded;
        }
        case 'everyone':
        default:
          return true;
      }
    };
    return { candidates: roster.filter(inScope), isFTResponseScope };
  }, [game, groupMsgScope, groupMsgIncludeWait, groupMsgPickedIds]);

  // 2026-07-17 (coach requirement) — fetch the server-resolved recipient
  // preview whenever the modal is open and the audience changes (scope
  // tile, waitlist checkbox — both flow through groupMsgCandidates above,
  // so this effect doesn't need to know about them individually).
  // Debounced ~300ms so flipping through scope tiles doesn't fire a
  // request per click; a monotonic request id (groupMsgPreviewReqId)
  // guards against a slow earlier response clobbering a faster later one.
  useEffect(() => {
    if (!groupMsgOpen) return undefined;
    // "Pick Players" is already exact (the coach's own selections) — no
    // server round trip needed; see the render below, which renders
    // targetsSafe directly through the same list component.
    if (groupMsgScope === 'specific') {
      return undefined;
    }

    const { candidates, isFTResponseScope } = groupMsgCandidates;
    // 2026-09-17 — 'everyone' maps to the team-wide 'all' group (which also
    // includes pending invites, like the coach expects); the rest are the
    // game-scoped groups added in Phase 4.
    const body = isFTResponseScope
      ? { group: groupMsgScope === 'everyone' ? 'all' : groupMsgScope, gameId: gameId || null }
      : { userIds: candidates.map(m => m.userId) };

    // Nothing to ask the server about — mirror the "no matches" state
    // instead of firing a request that /preview would 400 on (empty
    // userIds with no group is the same "provide userIds or group"
    // shape the /send validation rejects).
    if (!isFTResponseScope && body.userIds.length === 0) {
      setGroupMsgPreview({ loading: false, error: false, total: 0, recipients: [] });
      return undefined;
    }

    const reqId = ++groupMsgPreviewReqId.current;
    setGroupMsgPreview(prev => ({ ...prev, loading: true, error: false }));

    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post(`/teams/${teamId}/messages/preview`, body);
        if (groupMsgPreviewReqId.current !== reqId) return; // a newer request superseded this one
        setGroupMsgPreview({ loading: false, error: false, total: data.total, recipients: data.recipients || [] });
      } catch (err) {
        if (groupMsgPreviewReqId.current !== reqId) return;
        // Fall back to the old client-side estimate (rendered below via
        // groupMsgPreview.error) rather than blocking Send on a failed
        // preview call — /send still resolves the real audience itself.
        setGroupMsgPreview({ loading: false, error: true, total: null, recipients: null });
      }
    }, 300);

    // Cleanup fires on every re-run (new debounce timer superseding this
    // one) AND on unmount/modal-close — both cases should cancel a timer
    // that hasn't fired yet.
    return () => clearTimeout(timer);
  }, [groupMsgOpen, groupMsgScope, teamId, gameId, groupMsgCandidates]);

  // Collapse the "+X more" expanded list whenever the scope changes so a
  // coach who expanded "Everyone" doesn't land on an already-expanded
  // (and momentarily stale) list after switching to "Waitlist Only".
  useEffect(() => {
    setGroupMsgPreviewExpanded(false);
  }, [groupMsgScope]);

  const [availabilityOpen, setAvailabilityOpen] = useState(true);
  const [balanceScore, setBalanceScore]       = useState(null);
  const [showNonResponders, setShowNonResponders] = useState(false);
  // May 4 2026 (v2.5.69) — bulk assign picker (manual create-teams
  // mode). Opens a modal where the coach multi-selects checked-in
  // players and drops each one into Home or Away. Persists via the
  // new POST /roster/bulk-assign endpoint. Only meaningful when the
  // team has team_creation_mode='manual'; the entry-point button
  // only renders in that mode (see Coach Actions block above).
  const [showBulkAssign,  setShowBulkAssign]  = useState(false);
  // May 4 2026 — sub-group filter inside the Awaiting Response → Confirm
  // Jab modal. 'all' = whole list (legacy behavior); other values
  // restrict to the matching subgroup. Lets coaches Jab just spares,
  // just goalies, etc. without checking each name. Reset to 'all'
  // every time the modal opens so a stale filter from a previous
  // open doesn't surprise the coach.
  const [jabFilter, setJabFilter] = useState('all');
  useEffect(() => { if (showNonResponders) setJabFilter('all'); }, [showNonResponders]);
  // Chunk 7 (Apr 2026) — editable team labels + manual player swap.
  // labelEdit is { key: 'home'|'away', value: string } or null.
  // rosterSwap is { player: {userId, inviteId, name}, from: 'home'|'away', fromLabel } or null.
  const [labelEdit,   setLabelEdit]   = useState(null);
  const [labelSaving, setLabelSaving] = useState(false);
  const [rosterSwap,  setRosterSwap]  = useState(null);
  const [rosterSwapBusy, setRosterSwapBusy] = useState(false);

  // Apr 2026 — FCFS (first come first pay) modal state.
  // fcfsOpen: boolean — controls the manage-FCFS modal.
  // fcfsSpots / fcfsRecipients: form fields when opening a window.
  // fcfsBusy: submit guard.
  const [fcfsOpen,        setFcfsOpen]        = useState(false);
  const [fcfsSpots,       setFcfsSpots]       = useState('');
  const [fcfsRecipients,  setFcfsRecipients]  = useState('all');
  const [fcfsBusy,        setFcfsBusy]        = useState(false);
  // Spares picker — coach clicks "Spares" in Coach Actions to nudge some or
  // all spares to RSVP. Shows each spare's current availability status
  // (checked in / waitlist / out / no-response) with checkboxes for selection.
  // Replaces the prior "blast every spare" behaviour that emailed everyone
  // unconditionally regardless of their response state.
  const [sparePickerOpen,   setSparePickerOpen]   = useState(false);
  // v2.5.97 — when true, the spare invite goes out using the
  // spareRequestFree template (warm copy, "FREE" cost row, no
  // tracking-fee block) instead of the standard spareRequest. The
  // backend POST body grows a `freeInvite: true` field. Resets to
  // false every time the picker opens so coaches don't accidentally
  // carry the flag from one batch to the next.
  const [sparePickerFree,   setSparePickerFree]   = useState(false);
  // 2026-09-17 (per Gilles, Phase 6) — per-game price override (mig 112).
  // Dollar strings for the two inputs; '' = no override (team default).
  const [sparePickerFeeSkater, setSparePickerFeeSkater] = useState('');
  const [sparePickerFeeGoalie, setSparePickerFeeGoalie] = useState('');
  // 2026-07-10 (batch-B C2, coach request) — optional custom note that
  // rides INSIDE the spare-invite email. Coaches were sending the
  // invite, then a second message with what they actually wanted to say.
  const [sparePickerNote,   setSparePickerNote]   = useState('');
  const [sparePickerIds,    setSparePickerIds]    = useState(new Set()); // Set<userId>
  // v2.5.185 — filter the Invite Spares list by position so skaters and
  // goalies aren't mixed together. 'all' | 'players' | 'goalies'.
  const [spareInviteFilter, setSpareInviteFilter] = useState('all');
  const [sparePickerSending,setSparePickerSending]= useState(false);
  const [nudgingSingle, setNudgingSingle]     = useState({}); // userId → true when sending
  const [rosterChanged, setRosterChanged]     = useState(false); // flag when roster changes after balance
  // Apr 30 2026 (App_Change_List #33) — nudgeLog state retired with
  // the auto-jab feature. Coach Actions no longer renders an auto-jab
  // history block; the Notification Log admin page is the place to
  // see historical records.

  // ── fast two-step load ──────────────────────────────────────────
  useEffect(() => { resolveGame(); }, [gameId]);

  // Apr 2026 (Tom_Notes B12) — load per-game reminders for coaches.
  // Refreshes when the game version changes (someone tweaks a
  // setting that affects the cron schedule, etc.). 403-tolerant
  // since the role hook may flip between renders.
  useEffect(() => {
    if (!teamId || !gameId || !isCoach) {
      setGameReminders([]);
      return;
    }
    let cancelled = false;
    api.get(`/teams/${teamId}/scheduled-reminders`, { params: { gameId, limit: 20 } })
      .then(({ data }) => {
        if (cancelled) return;
        setGameReminders(Array.isArray(data?.events) ? data.events : []);
      })
      .catch(() => { if (!cancelled) setGameReminders([]); });
    return () => { cancelled = true; };
  }, [teamId, gameId, isCoach]);

  const resolveGame = async (_attempt = 0) => {
    setLoading(true);
    try {
      // Fix #13: cache gameId→teamId in sessionStorage so revisiting the same
      // game page skips the extra round-trip lookup on every mount.
      const cacheKey = `gameday:game:${gameId}:teamId`;
      let tid = sessionStorage.getItem(cacheKey);

      if (!tid) {
        const { data: meta } = await api.get(`/games/${gameId}`);
        tid = meta.teamId;
        sessionStorage.setItem(cacheKey, tid);
      }

      setTeamId(tid);
      teamIdRef.current = tid;

      // 2026-07-21 (multi-team email-link landing audit) — pin this game's
      // OWN team as active, mirroring the RsvpResultPage magic-login fix
      // (2026-07-20) for the same underlying issue. /game/:gameId is the
      // CTA target of many plain (non-magic) email links — arenaChange,
      // waitlistPromoted, coachCheckedYouIn, coachRemovedYou,
      // coachMovedToWaitlist, manualTeamsWarning, playerCheckout,
      // fcfsInvite, paymentConfirmed — opened by an ALREADY-authenticated
      // multi-team user whose last-active team (gd:lastTeamId) may be a
      // DIFFERENT team than this game's. The game content above is
      // already correct (tid comes from THIS game via /games/:gameId,
      // never from "whichever team is active"), but without this pin:
      //   (a) the persistent sidebar nav keeps showing the stale team, and
      //   (b) any sibling page that defaults its season filter to outlet
      //       context's currentSeasonId (Chat/Schedule/Players) would
      //       inherit the WRONG team's season id after a sidebar click.
      // localStorage pin fixes future loads (pickActiveTeam prefers it —
      // see utils/lastTeam.js); correcting the already-mounted layout's
      // activeTeam directly (when the Outlet context is available and the
      // team is already in the user's loaded teams list) fixes the
      // CURRENT view too, not just the next one.
      try { localStorage.setItem('gd:lastTeamId', tid); } catch { /* storage unavailable — non-fatal */ }
      if (outletCtx?.activeTeam?.id !== tid) {
        const ownTeam = outletCtx?.teams?.find(t => t.id === tid);
        if (ownTeam) outletCtx.setActiveTeam?.(ownTeam);
      }

      await loadGameDetail(tid);
    } catch (err) {
      // 2026-07-20 (production bug: RSVP "I'm In" → RSVP recorded but the
      // browser lands on a "Game not found" page) — ROOT CAUSE #4. This
      // was a blanket `catch { toast.error('Game not found') }` that
      // showed the SAME terminal message for every failure: a real 404,
      // a 403 (not a member), a 401 still mid-refresh (this page can be
      // the very first request fired right after a magic-login redirect,
      // racing the token swap — see AuthContext.loadUser's comment), a
      // 429, or a plain network blip on cellular right after opening an
      // email link. Only 404/403 are genuinely "not found" — everything
      // else is transient and deserves a retry, not a dead end.
      const status = err?.response?.status;
      const definitivelyMissing = status === 404 || status === 403;
      if (!definitivelyMissing && _attempt < 1) {
        setTimeout(() => resolveGame(_attempt + 1), 1200);
        return; // keep the spinner up through the retry
      }
      toast.error(definitivelyMissing
        ? 'Game not found'
        : "Couldn't load this game — check your connection and try again.");
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  const loadGameDetail = async (tid) => {
    try {
      const { data } = await api.get(`/teams/${tid}/games/${gameId}`);
      setGame(data);
      // Re-seed the auto-refresh baseline from the fresh server version.
      // Critical: this must happen on EVERY load (initial + every
      // refresh()) so a user's own actions never trigger their own
      // auto-refresh loop. The poll only fires onChanged when the
      // server's version moves AHEAD of this baseline.
      if (data?.version) gameVersionRef.current = data.version;
      if (data.roster?.length > 0) {
        setTeams({
          home: data.roster.filter(r => r.team === 'home'),
          away: data.roster.filter(r => r.team === 'away'),
        });
      } else {
        setTeams(null);
      }
      const me = (data.availability || []).find(a => a.userId === user?.id);
      setMyStatus(me?.status ?? null);
      setMyNotes(me?.notes || '');
      // Sync noteDraft to the server's notes unless the user has
      // uncommitted edits in the draft. This handles initial load,
      // refresh after status change, and WebSocket-driven refreshes
      // without clobbering in-progress typing.
      setNoteDraft(prev => {
        // If the draft hasn't been touched yet (equals previous server
        // value or empty), take the new server value. Otherwise keep
        // the user's in-progress edits.
        if (prev === '' || prev === myNotes) return me?.notes || '';
        return prev;
      });
      setMyFeedback(data.my_feedback || null);
    } catch { toast.error('Failed to load game'); }
  };

  // Load attendance map for this team
  useEffect(() => {
    if (!teamId) return;
    api.get(`/teams/${teamId}/stats/attendance`)
      .then(r => setAttendance(r.data))
      .catch(() => {});
    // Apr 30 2026 (App_Change_List #33) — auto-nudge-log fetch removed
    // alongside the auto-jab feature itself. Past rows in the
    // auto_nudge_log table are still readable via the admin
    // notification-log page for historical audit.
  }, [teamId, gameId]);

  const refresh = () => teamId && loadGameDetail(teamId);

  // 2026-09-16 (dashboard quick actions, per Gilles) — `?open=spares` /
  // `?open=jab` deep-links straight into the Invite Spares picker or the
  // "No Response — Jab Players" modal. Used by the Next Game card on the
  // Dashboard so a coach gets there in one tap instead of game page →
  // scroll → button. Fires ONCE per mount, only after the game has
  // loaded (openSparePicker reads game.all_members / availability) and
  // only for coaches (the buttons don't exist for players). The param is
  // stripped from the URL afterwards so a refresh doesn't re-open it.
  const autoOpenDoneRef = useRef(false);
  useEffect(() => {
    if (autoOpenDoneRef.current || loading || !game || !isCoach) return;
    let open = null;
    try { open = new URLSearchParams(window.location.search).get('open'); } catch { /* ignore */ }
    if (!open) { autoOpenDoneRef.current = true; return; }
    autoOpenDoneRef.current = true;
    if (open === 'spares') openSparePicker();
    else if (open === 'jab') setShowNonResponders(true);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('open');
      window.history.replaceState({}, '', url.pathname + (url.search || '') + (url.hash || ''));
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, game, isCoach]);
  // Populate the ref used by useGameVersionPoll's onChanged callback
  // so the silent auto-refresh can reach `refresh` despite the
  // declaration-order mismatch (hook call is above `refresh`).
  refreshRef.current = refresh;

  // ── AI analysis polling ─────────────────────────────────────────
  // Polls every 5s for up to 60s waiting for background AI to finish
  const startAiPoll = () => {
    if (pollRef.current) return; // already polling
    setAiPolling(true);
    let attempts = 0;
    const maxAttempts = 12; // 60 seconds
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const tid = teamIdRef.current;
        if (!tid) return;
        const { data } = await api.get(`/teams/${tid}/games/${gameId}`);
        if (data.ai_analysis?.content) {
          setGame(prev => prev ? { ...prev, ai_analysis: data.ai_analysis } : prev);
          clearInterval(pollRef.current);
          pollRef.current = null;
          setAiPolling(false);
          toast.success('🤖 AI analysis ready!');
          return;
        }
      } catch {}
      if (attempts >= maxAttempts) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setAiPolling(false);
      }
    }, 5000);
  };

  // Clean up poll on unmount
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // Credits balance loader (Apr 2026) — fetch the user's available
  // credit in THIS game's currency. Used by the Pay Now CTA to show
  // a "Use $X credit" alternative to card payment. Only runs for
  // spares on pay-required teams (other users won't see credits
  // regardless). Re-runs when pending_payment status changes, so the
  // button refreshes after a credit is applied or after paying.
  useEffect(() => {
    if (!game) return;
    const isSpare      = game.myIsPrimary === false;
    const payEnabled   = game.teamSettings?.payment_enabled === true;
    const payRequired  = game.teamSettings?.spare_pay_required === true;
    if (!isSpare || !payEnabled || !payRequired) {
      setMyCreditCents(0);
      return;
    }
    const currency = (game.teamSettings?.payment_currency || 'cad').toLowerCase();
    (async () => {
      try {
        const { data } = await api.get('/credits/balance');
        const cents = (data?.byCurrency && data.byCurrency[currency]) || 0;
        setMyCreditCents(cents);
      } catch {
        // Non-fatal — balance stays 0 and the UI falls back to card
        setMyCreditCents(0);
      }
    })();
  }, [game?.myIsPrimary, game?.teamSettings?.payment_enabled,
      game?.teamSettings?.spare_pay_required, game?.teamSettings?.payment_currency,
      myStatus]);

  // Live countdown for upcoming games
  useEffect(() => {
    if (!game || game.status === 'completed' || game.status === 'cancelled') return;
    const tick = () => {
      const gameDateTime = new Date(`${safeDate(game.game_date)}T${game.game_time || '00:00'}`);
      const diff = gameDateTime - Date.now();
      if (diff <= 0) { setCountdown('Game time! 🏟'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      if (d > 0) setCountdown(`${d}d ${h}h ${m}m`);
      else if (h > 0) setCountdown(`${h}h ${m}m`);
      else setCountdown(`${m}m`);
    };
    tick();
    const timer = setInterval(tick, 60000);
    return () => clearInterval(timer);
  }, [game?.game_date, game?.game_time, game?.status]);

  // ── availability ────────────────────────────────────────────────
  // `notes` semantics (matches the backend):
  //   - undefined → preserve the existing note on the row (don't touch)
  //   - ''        → clear the existing note
  //   - string    → replace with this value
  // The comment modal Submit passes the textarea value (which becomes
  // either '' to clear or a new string to replace). Skip passes
  // `undefined` so the user's existing note survives a Skip-without-edit.

  // ── Chunk 7 handlers (Apr 2026) ──
  // Editable team labels: tap heading → modal with input. Save PATCHes
  // the team-labels endpoint with just the changed side.
  const openTeamLabelEdit = (key, current) => {
    setLabelEdit({ key, value: current === 'Home' || current === 'Away' ? '' : current });
  };
  const saveTeamLabel = async () => {
    if (!labelEdit) return;
    setLabelSaving(true);
    try {
      const body = labelEdit.key === 'home'
        ? { homeLabel: labelEdit.value || null }
        : { awayLabel: labelEdit.value || null };
      await api.patch(`/teams/${teamId}/games/${gameId}/team-labels`, body);
      setLabelEdit(null);
      await refresh();
      toast.success('Team name updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save team name');
    }
    setLabelSaving(false);
  };

  // Player swap: tap a row → modal showing "Move to <other team>".
  // Player already on team X → offer to move to the opposite team.
  const openRosterSwap = (player, fromKey, fromLabel) => {
    setRosterSwap({ player, from: fromKey, fromLabel });
  };
  const doRosterSwap = async () => {
    if (!rosterSwap) return;
    setRosterSwapBusy(true);
    try {
      const targetTeam = rosterSwap.from === 'home' ? 'away' : 'home';
      const body = rosterSwap.player.userId
        ? { userId: rosterSwap.player.userId, team: targetTeam }
        : { inviteId: rosterSwap.player.inviteId, team: targetTeam };
      await api.post(`/teams/${teamId}/games/${gameId}/roster/swap`, body);
      setRosterSwap(null);
      await refresh();
      toast.success(`Moved ${rosterSwap.player.name}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to move player');
    }
    setRosterSwapBusy(false);
  };

  // Apr 2026 — FCFS open/close handlers.
  const doFcfsOpen = async () => {
    const spots = parseInt(fcfsSpots);
    if (!Number.isFinite(spots) || spots < 1) {
      toast.error('Enter at least 1 spot');
      return;
    }
    setFcfsBusy(true);
    try {
      const { data } = await api.post(
        `/teams/${teamId}/games/${gameId}/fcfs/open`,
        { spotsAvailable: spots, recipients: fcfsRecipients }
      );
      const parts = [];
      if (data.sentImmediate) parts.push(`${data.sentImmediate} waitlist email${data.sentImmediate === 1 ? '' : 's'} sent`);
      if (data.deferredCount) parts.push(`${data.deferredCount} more in 5 min`);
      toast.success(`FCFS opened — ${parts.join(', ') || 'no recipients'}`);
      setFcfsOpen(false);
      setFcfsSpots('');
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to open FCFS window');
    }
    setFcfsBusy(false);
  };
  const doFcfsClose = async () => {
    setFcfsBusy(true);
    try {
      await api.post(`/teams/${teamId}/games/${gameId}/fcfs/close`);
      toast.success('FCFS window closed');
      setFcfsOpen(false);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to close FCFS window');
    }
    setFcfsBusy(false);
  };

  const setAvailability = async (status, notes = undefined) => {
    if (!teamId) return;
    // Apr 2026 (Tom_Notes K2) — guard against double-tap. The button
    // is also disabled visually when rsvpBusy is true, but a fast
    // tap-tap could still fire two onClicks before React re-renders
    // the disabled state. The early-return covers that race.
    if (rsvpBusy) return;
    setRsvpBusy(true);
    try {
      await setAvailabilityImpl(status, notes);
    } finally {
      setRsvpBusy(false);
    }
  };

  // Apr 2026 (Tom_Notes K2) — original setAvailability body lifted
  // verbatim into a sub-function. The wrapper above adds the busy
  // guard around it. Pulling the body out (instead of inlining the
  // try/finally into the original) keeps the diff minimal and the
  // existing offline/Stripe branches untouched.
  const setAvailabilityImpl = async (status, notes = undefined) => {
    // Normalize: trim + cap at 500 if a note was provided. `undefined`
    // sticks as undefined so the body omits the key entirely.
    const cleanNotes = notes === undefined
      ? undefined
      : (notes || '').trim().slice(0, 500);

    // ── Spare payment gate ─────────────────────────────────────────
    // If this user is a spare (is_primary = false), the team requires payment
    // before confirming, and they're saying "I'm In" → hit the spare-checkout
    // endpoint. Two outcomes:
    //   - freeGame: true → coach set spare_fee_cents = 0 for a free game;
    //     backend already marked the spare 'in', no Stripe involvement
    //   - otherwise → backend created a Checkout Session; redirect to Stripe
    //
    // Guard: if the spare is ALREADY 'in' (coach moved them off the waitlist),
    // skip the Stripe trigger. They're confirmed; tapping "I'm In" again
    // should be a no-op, not a surprise checkout redirect.
    // Apr 2026 fix — don't trigger Stripe checkout when the spare's
    // RSVP would land on the waitlist anyway. Previously a spare who
    // tapped "I'm In" on a full game was sent to checkout, paid, and
    // THEN the backend cap-checked and waitlisted them — leaving them
    // paying for a waitlist spot. Now we pre-check the cap client-side
    // (matches backend logic in POST /availability) and skip the pay
    // trigger when the spare would be waitlisted. They'll land on the
    // waitlist free, and when the coach promotes them, the promote
    // handler triggers the pay flow properly.
    //
    // Apr 2026 expansion — when `spare_pay_required` is ON, the
    // default workflow is waitlist-first (tap I'm In → join waitlist
    // free → coach promotes → pay). The only times a spare should
    // get routed straight to Stripe on the initial tap are:
    //   (1) an FCFS window is open (coach has explicitly flipped to
    //       first-come-first-pay mode — paying IS the check-in)
    //   (2) the coach enabled `spare_bypass_waitlist_after_request`
    //       AND sent the spare-request email (spare_request_sent_at
    //       is set) — coach signaled they need spares right now
    //       and will accept direct check-ins
    //   (3) the coach has already promoted this spare for this game
    //       (their `promoted_at` is set) — they're coming BACK in
    //       after an earlier "I'm Out", pay is still owed
    // In all other cases, fall through to the normal RSVP path (which
    // the backend routes to the waitlist). The game page's
    // pending_payment CTA handles the pay step later.
    if (game && status === 'in' && game.teamSettings?.payment_enabled && game.teamSettings?.spare_pay_required && game.myIsPrimary === false) {
      const fcfsActive = !!game.fcfs_window_opened_at && !game.fcfs_closed_at;
      const bypassActive = game.teamSettings.spare_bypass_waitlist_after_request === true
                         && !!game.spare_request_sent_at;
      // Apr 2026 — tighten the "previously promoted" check to require
      // ALSO having a PAID payment row. Without the &&myHasPaid, a
      // spare whose promoted_at was stamped by any earlier coach
      // action (even one that didn't complete payment) incorrectly
      // got routed to the direct-pay flow on their next check-in.
      // On pay-on-promote teams that meant seeing "pay to secure your
      // spot" when the correct flow was "join waitlist free".
      //
      // The `alreadyPromoted && myHasPaid` state represents genuine
      // stickiness: coach approved, spare paid, they left, they're
      // coming back — admit free, no re-charge.
      const alreadyPromotedAndPaid = !!game.myPromotedAt && !!game.myHasPaid;
      // Apr 2026 — 'pay_upfront' mode: coach wants direct Stripe on
      // every check-in, no waitlist-first. Treated as a fourth
      // direct-pay-allowed condition.
      const payUpfront = game.teamSettings.spare_pay_mode === 'pay_upfront';
      // v2.5.169 — mirror the backend: the explicit spare_auto_waitlist
      // toggle waitlists the spare even in pay_upfront mode, so don't
      // run direct Stripe in that case (the backend would waitlist them
      // anyway, and a Stripe charge to "secure" a waitlist spot is
      // wrong). FCFS still re-enables direct pay (backend disables
      // auto-waitlist when FCFS is active).
      const explicitAutoWaitlist = game.teamSettings.spare_auto_waitlist === true;
      const directPayAllowed = fcfsActive || bypassActive || alreadyPromotedAndPaid
        || (payUpfront && !explicitAutoWaitlist);

      if (gameFullForMe || !directPayAllowed) {
        // Fall through to the normal RSVP path — the backend will put
        // them on the waitlist for free. No Stripe trigger.
      } else if (myStatus !== 'in') {
        // Spot exists, direct-pay path is allowed, and they're not
        // already confirmed — run the Stripe flow as before.
        try {
          const { data } = await api.post('/payments/spare-checkout', {
            teamId,
            gameId,
          });
          if (data.freeGame) {
            setMyStatus('in');
            toast.success(data.message || '✅ You\'re in — this game is free for spares!');
            refresh();
            return;
          }
          window.location.href = data.url;
          return;
        } catch (err) {
          // Fall through to the normal RSVP path for errors that
          // should route to the waitlist instead of surfacing:
          //   - game_full (409): cap hit after our client check
          //   - not_promoted (400): stale promoted_at or buggy client
          //     tried to trigger checkout on a pay_on_promote team
          //     without actually being promoted. Backend refuses;
          //     fall through to put them on the waitlist for free.
          const httpStatus = err.response?.status;
          const errCode    = err.response?.data?.error;
          if ((httpStatus === 409 && errCode === 'game_full') ||
              (httpStatus === 400 && errCode === 'not_promoted')) {
            // noop — fall through below to normal RSVP path
          } else {
            toast.error(friendlyError(err, 'Could not start payment — try again'));
            return;
          }
        }
      }
    }

    // Bug F fix: if offline, queue the RSVP in the SW background-sync store
    // so it replays automatically when connectivity returns.
    if (offline.isOffline) {
      const token = localStorage.getItem('token');
      const sw = navigator.serviceWorker?.controller;
      if (sw && token) {
        // Build body — include `notes` only when explicitly provided so
        // the backend can preserve the existing note on a Skip path.
        const body = cleanNotes === undefined ? { status } : { status, notes: cleanNotes };
        sw.postMessage({
          type: 'QUEUE_RSVP',
          payload: {
            url: `/api/teams/${teamId}/games/${gameId}/availability`,
            body,
            token,
            action: `rsvp_${status}`,
            queuedAt: Date.now(),
          },
        });
        // Optimistic UI update so the player sees their selection immediately
        setMyStatus(status);
        const labels = { in: '✅ Queued — will sync when online', out: '❌ Queued — will sync when online', maybe: '🤔 Queued — will sync when online' };
        toast.success(labels[status], { duration: 3500 });
      } else {
        toast.error('You\'re offline and no sync is available. Try again when connected.');
      }
      return;
    }

    try {
      const body = cleanNotes === undefined ? { status } : { status, notes: cleanNotes };
      const { data } = await api.post(`/teams/${teamId}/games/${gameId}/availability`, body);
      const effectiveStatus = data.status || status;
      setMyStatus(effectiveStatus);
      if (data.waitlisted) {
        // Different messaging based on WHY the backend waitlisted us:
        //   'spare_auto' — the coach has spare_auto_waitlist enabled. Being
        //      a spare does NOT mean the game is full; tell the user that
        //      their RSVP was received and the coach will promote them if
        //      a spot opens up, so they're not confused by a "game is full"
        //      message when there might be 4 spots still open.
        //   'cap' — the confirmed roster hit the player cap. "Game is full"
        //      is the right copy here.
        //   else (null/unknown) — fall back to a neutral "you're on the
        //      waitlist" message that's true either way.
        //   'spare_auto' — spares are auto-waitlisted by team setting
        //      (explicit toggle) OR because pay-required implies
        //      waitlist-first. Both land on spare_auto from the
        //      backend. Copy distinguishes pay-required (which spares
        //      will recognize from the notice above the button)
        //      vs the explicit setting.
        const isPayRequired = game?.teamSettings?.payment_enabled
                           && game?.teamSettings?.spare_pay_required;
        const msg = data.waitlistReason === 'spare_auto'
          ? (isPayRequired
              ? '⏳ Added to the waitlist. You\'ll get an email and notification — with a Pay Now link — if the coach promotes you off the waitlist.'
              : '⏳ Thanks for checking in! This team auto-adds spares to the waitlist — your coach will promote you if a spot opens up. You\'ll get a notification (and email, if enabled) when you\'re in.')
          : data.waitlistReason === 'cap'
            ? '⏳ Game is full — you\'ve been added to the waitlist. You\'ll get a notification if a spot opens up.'
            // App_Change_List #32b — explicit copy when overflow was
            // disabled because spare invites went out. Helps coaches
            // (and players) understand why a check-in went to waitlist
            // even though "the game wasn't full" — the cap firmed up
            // the moment the team called for spares.
            : data.waitlistReason === 'overflow_lockout'
              ? '⏳ The cap is locked because the team has already invited spares for this game. You\'ve been added to the waitlist — the coach will let you know if a spot opens up.'
              : '⏳ You\'ve been added to the waitlist. You\'ll be notified if a spot opens up.';
        toast(msg, {
          // Fully opaque background so text doesn't blend with the page
          // content behind (previous semi-transparent purple was unreadable
          // in light mode where the card colors bled through). Dark purple
          // fill + white text = strong contrast in both themes; the left
          // border keeps the waitlist colour identity.
          style: {
            background: '#4c1d95',
            border: '1px solid #6d28d9',
            borderLeft: '4px solid #a78bfa',
            color: '#ffffff',
            padding: '14px 16px',
            fontSize: 14,
            lineHeight: 1.45,
            fontWeight: 500,
            maxWidth: 420,
            boxShadow: '0 10px 30px rgba(76, 29, 149, 0.35)',
          },
          duration: 8000,
        });
      } else if (effectiveStatus === 'pending_payment') {
        // Apr 2026 — backend guard for direct /availability POST on
        // pay-required teams returned pending_payment. The page will
        // re-render and show the "Pay Now to Secure Your Spot" CTA
        // (the setMyStatus above triggers the pending_payment block).
        // Just acknowledge the RSVP and let the CTA do the work.
        toast('💳 Almost there — tap Pay Now to lock in your spot.', {
          icon: '💳', duration: 5000,
          style: { background: '#4c1d95', color: '#fff', borderLeft: '4px solid #a78bfa' }
        });
      } else {
        // Pre-approved spare check-in — coach already promoted them for
        // this game, so the auto-waitlist was bypassed. Tell them why
        // (so it doesn't seem like the auto-waitlist setting just broke).
        if (data.preApproved) {
          toast.success('✅ You\'re back in — your coach already approved you for this game', {
            duration: 5000,
          });
        } else {
          const labels = { in: '✅ You\'re in!', out: '❌ Marked as out', maybe: '🤔 Maybe noted' };
          toast.success(labels[status]);
        }
      }
      // Flag that the roster changed after teams were already balanced
      if (teams) setRosterChanged(true);
      refresh();
      // May 2 2026 — suggest-push event no longer dispatched. The
      // push permission nudge banner was removed entirely; users
      // who want push subscribe deliberately from Profile.
    } catch (err) {
      // May 4 2026 — surface server's actual error (e.g. lookahead
      // limit's "This team only allows RSVPs for the next N games").
      toast.error(err.response?.data?.error || 'Failed to update');
    }
  };

  // Apr 2026 (Tom_Notes B16) — coach toggle: include goalies in
  // team balancing or not. Default true for backward-compat (existing
  // behaviour included goalies as part of the player pool). When the
  // toggle is off, goalies get assigned to teams via a separate
  // round-robin path on the backend rather than weighted by rating.
  // May 2 2026 — initial value mirrored the team's persisted setting.
  // May 4 2026 — bug fix: the toggle was purely local component state,
  // so a coach's per-game flip got stomped on every refresh() call
  // when the team default re-arrived from the server. Auto-balance
  // also ignored per-game flips entirely. Migration 067 introduced
  // a `games.balance_include_goalies` column (NULL = inherit team
  // default). Resolution order, both here and on the backend:
  //   1. game.balance_include_goalies (per-game override, mig 067)
  //   2. game.teamSettings.balance_include_goalies (team default)
  //   3. true (legacy fallback)
  // The /balance endpoint persists to the per-game column whenever
  // the body explicitly sets includeGoalies, so flipping the toggle
  // and clicking Re-balance actually saves. The auto-balance cron
  // also reads per-game first, so an emailed team-assignment respects
  // the coach's per-game preference.
  const [balanceIncludeGoalies, setBalanceIncludeGoalies] = useState(true);
  useEffect(() => {
    // Per-game value wins. Only fall back to team setting when the
    // per-game column is NULL (no explicit override yet for this game).
    const perGame = game?.balance_include_goalies;
    const teamDefault = game?.teamSettings?.balance_include_goalies;
    if (typeof perGame === 'boolean') {
      setBalanceIncludeGoalies(perGame);
    } else if (typeof teamDefault === 'boolean') {
      setBalanceIncludeGoalies(teamDefault);
    }
  }, [game?.balance_include_goalies, game?.teamSettings?.balance_include_goalies]);

  // May 4 2026 — surface the team default in an info note next to
  // the toggle so the coach knows the starting position. Also tells
  // them WHEN their per-game flip is overriding the team default
  // (so they know it's not a bug when this game's toggle disagrees
  // with what they set in Team Settings).
  const goalieDefaultIsIn = (game?.teamSettings?.balance_include_goalies !== false);
  const goalieOverrideActive = typeof game?.balance_include_goalies === 'boolean';

  // May 2 2026 — server-authoritative lock state. Derived directly
  // from the game row's teams_locked_at column. When non-null the
  // Re-Balance / Goalies / Clear buttons are disabled; manual
  // per-row swap stays available. Lock/unlock POSTs round-trip and
  // refresh() pulls the new state back.
  const teamsLocked = !!game?.teams_locked_at;

  // ── balance teams ───────────────────────────────────────────────
  const balanceTeams = async (opts = {}) => {
    setBalancing(true);
    try {
      // includeGoalies: defaults to whatever the toggle is set to
      // unless the caller (e.g. a SyntheticEvent from a button
      // press without explicit args) passes nothing. The legacy
      // call sites without options also still work.
      const includeGoalies = (opts && typeof opts === 'object' && 'includeGoalies' in opts)
        ? !!opts.includeGoalies
        : balanceIncludeGoalies;
      const { data } = await api.post(`/teams/${teamId}/games/${gameId}/balance`, {
        includeGoalies,
      });
      setTeams({ home: data.home, away: data.away });
      setBalanceScore(data.balanceScore ?? null);
      setRosterChanged(false); // clear the warning — teams are fresh
      // Apr 2026 (Tom_Notes B16) — toast mentions skipped goalies
      // when the toggle was off. Helps the coach not be surprised
      // by goalies missing from the balanced rosters.
      if (data.skippedGoalies > 0) {
        toast.success(
          `⚖️ Balanced! Score: ${data.balanceScore}/100 · ${data.skippedGoalies} goalie${data.skippedGoalies !== 1 ? 's' : ''} unassigned`
        );
      } else {
        toast.success(`⚖️ Balanced! Score: ${data.balanceScore}/100`);
      }
      refresh();
      // AI analysis fires in background — poll for it
      startAiPoll();
    } catch (err) { toast.error(err.response?.data?.error || 'Balancing failed'); }
    setBalancing(false);
  };

  // Apr 2026 (Tom_Notes B17) — manual team creation. Creates an
  // empty roster (every IN player as unassigned) so the coach can
  // drag/tap players onto home/away themselves. Bypasses the
  // balancer entirely.
  const initManualTeams = async () => {
    if (inPlayers.length < 2) {
      toast.error('Need at least 2 checked-in players');
      return;
    }
    setBalancing(true);
    try {
      await api.post(`/teams/${teamId}/games/${gameId}/roster/manual-init`);
      toast.success('Empty teams created — assign players below');
      setRosterChanged(false);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start manual teams');
    }
    setBalancing(false);
  };

  // Apr 2026 (Tom_Notes B17) — clear roster. Wipes team_assignment
  // for every player so the coach can re-balance OR re-start
  // manually. Confirms because it's destructive.
  const clearRoster = async () => {
    if (!confirm('Clear all team assignments and start over? This cannot be undone.')) return;
    setBalancing(true);
    try {
      await api.delete(`/teams/${teamId}/games/${gameId}/roster`);
      setTeams(null);
      setBalanceScore(null);
      setRosterChanged(false);
      toast.success('Player list cleared');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to clear player list');
    }
    setBalancing(false);
  };

  // May 2 2026 — Lock/Unlock teams for this game. Server-side this
  // hits POST /teams/.../teams/lock or .../teams/unlock; both are
  // idempotent. While locked the backend refuses re-balance with
  // 423 and the auto-balance crons skip the game. UI mirrors via
  // teamsLocked (derived from game.teams_locked_at after refresh).
  const toggleTeamsLock = async () => {
    if (teamsLocked) {
      if (!confirm('Unlock teams? Re-balancing and auto-balancing will be allowed again.')) return;
    } else {
      if (!confirm('Lock teams? No more re-balancing — coach can still hand-swap players, but the auto-balance cron and the Re-Balance button will be disabled until unlocked.')) return;
    }
    try {
      const url = teamsLocked
        ? `/teams/${teamId}/games/${gameId}/teams/unlock`
        : `/teams/${teamId}/games/${gameId}/teams/lock`;
      await api.post(url);
      toast.success(teamsLocked ? 'Teams unlocked' : 'Teams locked 🔒');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update lock');
    }
  };

  // v2.5.102 — manual "Send Teams" panic button. Reads existing
  // game_rosters and emails each player their assignment, WITHOUT
  // re-balancing. Coach uses this when the auto-balance cron failed
  // to fire (e.g. transient error left the scheduled_job_log row
  // claimed). Safe on locked games — the backend doesn't require
  // unlocked state, just non-empty game_rosters. Also writes a
  // manual_teams_send dedup row so the cron won't ALSO email the
  // same players if its window arrives after.
  const sendTeamAssignments = async () => {
    if (!confirm('Email each player their team assignment now?\n\nThis is useful if the auto-balance email didn\'t fire. The existing teams stay unchanged — no re-balancing.')) return;
    setSendingTeams(true);
    try {
      const { data } = await api.post(
        `/teams/${teamId}/games/${gameId}/send-team-assignments`
      );
      if (data.failed > 0) {
        toast.error(data.message || `Sent ${data.sent} (${data.failed} failed)`);
      } else {
        toast.success(data.message || `Sent to ${data.sent} player${data.sent !== 1 ? 's' : ''}`);
      }
      // v2.5.149 — refresh so teamChangesSinceSend resets to 0
      // (backend cleared it) and the "N changes since send" banner
      // disappears.
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send team assignments');
    }
    setSendingTeams(false);
  };

  // ── game result ─────────────────────────────────────────────────
  const submitResult = async (e) => {
    e.preventDefault();
    const home = parseInt(result.homeScore);
    const away = parseInt(result.awayScore);
    if (isNaN(home) || isNaN(away) || home < 0 || away < 0) {
      toast.error('Please enter valid scores (0 or greater)');
      return;
    }
    try {
      await api.post(`/teams/${teamId}/games/${gameId}/result`, {
        homeScore: home,
        awayScore: away,
      });
      // 🎉 Confetti on a win!
      if (home !== away) {
        const teamColor = document.documentElement.style.getPropertyValue('--ice') || '#00d4ff';
        launchConfetti(teamColor);
        toast.success(home > away ? '🏆 Home team wins! Great game!' : '🏆 Away team wins! Great game!', { duration: 4000 });
      } else {
        toast.success('Result saved! AI analysis queued 🤖');
      }
      setShowResult(false);
      refresh();
      startAiPoll();
    } catch { toast.error('Failed to save result'); }
  };

  // ── arena change ────────────────────────────────────────────────
  const submitArenaChange = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/teams/${teamId}/games/${gameId}/arena-override`, arenaForm);
      toast.success(data.message);
      setShowArenaChange(false);
      setArenaForm({ newArena: '', newAddress: '', reason: '' });
      refresh();
    } catch { toast.error('Failed to update arena'); }
  };

  // ── send reminders / spares ─────────────────────────────────────
  // Apr 2026 — opening the Game Reminder no longer fires the email
  // immediately. Instead, it pops a scope picker so the coach
  // confirms WHO gets the email. Default selection is
  // 'checked_in' — most reminders are "see you tonight" sent the
  // afternoon of the game, and emailing players who already said
  // 'out' (or never RSVP'd) is just inbox noise. Coach can switch
  // to 'responded' / 'full_roster' / 'everyone' for the cases
  // where they really do want a wider blast.
  const [reminderScopeOpen, setReminderScopeOpen] = useState(false);
  // 2026-09-17 (per Gilles, Phase 4) — the four radio scopes are gone.
  // Two checkbox audiences (both on by default), an optional note that
  // rides inside the reminder email, and a preview of what players get.
  //   in_and_waitlist — checked in + waitlist (never checked-out players)
  //   not_checked_in  — invited but not checked in (no answer yet)
  const [reminderGroups, setReminderGroups] = useState(() => new Set(['in_and_waitlist', 'not_checked_in']));
  const [reminderNote, setReminderNote] = useState('');
  const [reminderPreviewOpen, setReminderPreviewOpen] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);

  const sendReminder = () => {
    // Reset to the defaults each time so a previous selection doesn't
    // accidentally persist into a new send.
    setReminderGroups(new Set(['in_and_waitlist', 'not_checked_in']));
    setReminderNote('');
    setReminderPreviewOpen(false);
    setReminderScopeOpen(true);
  };

  const sendReminderWithGroups = async () => {
    const groups = [...reminderGroups];
    if (groups.length === 0) return toast.error('Pick at least one group');
    setReminderSending(true);
    try {
      const { data } = await api.post(
        `/teams/${teamId}/games/${gameId}/send-reminder`,
        { groups, customMessage: reminderNote.trim() || undefined }
      );
      toast.success(data.message);
      setReminderScopeOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send');
    }
    setReminderSending(false);
  };

  // ── spares picker ───────────────────────────────────────────────

  // Opens the spares picker. Default selection: every emailable spare
  // who HASN'T responded yet — those are the people the coach most
  // likely wants to nudge. Coach can untick or add others (e.g. also
  // nudge someone who said 'out' that may have changed their mind).
  //
  // Apr 30 2026 (App_Change_List #23) — pre-selection is restricted to
  // spares with a real userId (i.e. emailable). Placeholders + pending
  // invites still appear in the picker (the modal computes the full
  // list itself), but they aren't pre-checked because they can't
  // receive an email blast.
  const openSparePicker = () => {
    const roster = (game?.all_members || []);
    const avById = {};
    for (const a of (game?.availability || [])) {
      if (a.userId) avById[a.userId] = a.status;
    }
    // v2.5.226 — exclude goalie-spares from the DEFAULT pre-selection.
    // Coach feedback: a manual spare request kept emailing goalies (e.g.
    // Graham Spence, a Goalie Spare with no RSVP, got auto-ticked and sent).
    // A "spare request" is for skaters by default — same skaters-only intent
    // as the auto-invite cron. Goalies still appear in the picker (Goalies
    // tab) and can be ticked explicitly; they're just not auto-included.
    // Goalie = position 'goalie' OR it's in their positions[] list.
    const isGoalieMember = (m) =>
      m.position === 'goalie' ||
      (Array.isArray(m.positions) && m.positions.includes('goalie'));
    const candidateSpares = roster.filter(m =>
      m.userId && !m.isPrimary && !m.isPlaceholder && !isGoalieMember(m));
    // Pre-tick spares who haven't responded. Spares already checked in or on
    // waitlist don't need a nudge. 'out' spares are left unticked but shown
    // so the coach can deliberately re-ask them.
    const preSelected = new Set(
      candidateSpares
        .filter(m => {
          const s = avById[m.userId];
          return !s; // no RSVP row at all
        })
        .map(m => m.userId)
    );
    setSparePickerIds(preSelected);
    // v2.5.97 — reset the free-invite flag every time the picker
    // opens so coaches don't accidentally carry it from a prior
    // batch.
    setSparePickerFree(false);
    setSparePickerNote(''); // 2026-07-10 (C2) — never carry a note across batches
    // 2026-09-17 — seed the price inputs from the game's stored override
    // (if the coach already changed it for this game) or the team default.
    {
      const ts = game?.teamSettings || {};
      const skOv = game?.spare_fee_override_cents;
      const gkOv = game?.spare_goalie_fee_override_cents;
      const skDefault = Number(ts.spare_fee_cents) || 0;
      const gkTeam = Number(ts.spare_goalie_fee_cents) || 0;
      const gkDefault = ts.spare_goalies_free !== false && gkTeam <= 0 ? 0 : (gkTeam > 0 ? gkTeam : skDefault);
      setSparePickerFeeSkater(((skOv != null ? Number(skOv) : skDefault) / 100).toFixed(2));
      setSparePickerFeeGoalie(((gkOv != null ? Number(gkOv) : gkDefault) / 100).toFixed(2));
    }
    setSpareInviteFilter('all'); // v2.5.226 — reset the Players/Goalies tab on open
    setSparePickerOpen(true);
  };

  // 2026-09-17 (Phase 6) — pricing helpers for the Invite Spares panel.
  // Same math as backend utils/pricing.js computeSpareCharge (SOURCE-OF-
  // TRUTH:pricing.js): when the platform fee is passed to the spare, the
  // charge is grossed up so Stripe's 2.9% + 30¢ and the platform fee are
  // covered; otherwise the coach absorbs both out of the base price.
  const spareChargingPossible = () => {
    const ts = game?.teamSettings || {};
    return ts.payment_enabled === true && ts.spare_pay_required === true && ts.stripe_ready === true;
  };
  const teamDefaultSkaterCents = () => Number(game?.teamSettings?.spare_fee_cents) || 0;
  const teamDefaultGoalieCents = () => {
    const ts = game?.teamSettings || {};
    const gk = Number(ts.spare_goalie_fee_cents) || 0;
    if (ts.spare_goalies_free !== false && gk <= 0) return 0;
    return gk > 0 ? gk : teamDefaultSkaterCents();
  };
  const dollarsToCents = (str) => {
    const n = Number(String(str).replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
  };
  const overrideFor = (str, defaultCents) => {
    const c = dollarsToCents(str);
    return c === defaultCents ? 'reset' : c;
  };
  const spareFeeMath = (baseCents) => {
    const ts = game?.teamSettings || {};
    const platform = Math.max(0, Number(ts.platform_fee_cents) || 100);
    const pass = ts.spare_pass_platform_fee !== false;
    const roundUp = ts.spare_round_up_quarters !== false;
    const base = Math.max(0, Number(baseCents) || 0);
    if (base <= 0) return { base: 0, spare: 0, stripe: 0, platform: 0, net: 0, pass };
    let spare = pass ? Math.ceil((base + 30 + platform) / (1 - 0.029)) : base;
    if (roundUp) spare = Math.ceil(spare / 25) * 25;
    const stripe = Math.round(spare * 0.029) + 30;
    const net = spare - stripe - platform;
    return { base, spare, stripe, platform, net, pass };
  };
  const fmtC = (c) => `$${(Math.max(0, c) / 100).toFixed(2)}`;

  const sendSpareRequests = async (userIds) => {
    if (!Array.isArray(userIds) || userIds.length === 0) {
      return toast.error('Pick at least one spare to invite');
    }
    setSparePickerSending(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/games/${gameId}/send-spare-request`, {
        userIds,
        // v2.5.97 — pass the free-invite flag so the backend picks
        // the spareRequestFree template. 2026-09-17: also forced on when
        // the team can't charge (no Stripe / payments off).
        freeInvite: sparePickerFree || !spareChargingPossible(),
        // v2.5.226 — which Players/Goalies/All tab the coach sent from,
        // so the notification log can show the audience of the send.
        audience: spareInviteFilter,
        // 2026-07-10 (C2) — optional coach note rendered inside the email.
        customMessage: sparePickerNote.trim() || undefined,
        // 2026-09-17 (Phase 6) — per-game price override in cents. Equal to
        // the team default → 'reset' (clears any earlier override); else the
        // coach's number. Omitted entirely for free invites / no-Stripe teams.
        ...(spareChargingPossible() && !sparePickerFree ? {
          feeOverrideCents: overrideFor(sparePickerFeeSkater, teamDefaultSkaterCents()),
          goalieFeeOverrideCents: overrideFor(sparePickerFeeGoalie, teamDefaultGoalieCents()),
        } : {}),
      });
      toast.success(data.message || `Invited ${data.sent ?? userIds.length} spare${(data.sent ?? userIds.length) !== 1 ? 's' : ''}${sparePickerFree ? ' (free)' : ''}`);
      setSparePickerOpen(false);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send spare requests');
    }
    setSparePickerSending(false);
  };

  // ── feedback ────────────────────────────────────────────────────
  const submitFeedback = async (feedbackType) => {
    const prev = myFeedback;
    setMyFeedback(feedbackType); // optimistic
    try {
      await api.post(`/teams/${teamId}/games/${gameId}/feedback`, { feedbackType });
      toast.success('Feedback recorded — helps future balance 📊');
    } catch {
      setMyFeedback(prev);
      toast.error('Failed to save feedback');
    }
  };

  // ── Coach bulk availability override ────────────────────────
  const toggleSelect = (userId) => {
    setSelected(p => ({ ...p, [userId]: !p[userId] }));
  };

  const selectAll = (userIds) => {
    const allSelected = userIds.every(id => selected[id]);
    const next = {};
    userIds.forEach(id => { next[id] = !allSelected; });
    setSelected(next);
  };

  // ── Single-player coach action (from the per-row popover) ───────────
  // Same endpoint as the bulk override, just with one player. 'none' deletes
  // the availability row (so it shows as "no response"), everything else
  // sets status.
  const applySingleStatus = async (player, status) => {
    if (!player) return;
    setPlayerMenuFor(null);
    try {
      const update = player.userId
        ? { userId: player.userId, status }
        : player.inviteId
          ? { inviteId: player.inviteId, status }
          : null;
      if (!update) return;
      await api.post(`/teams/${teamId}/games/${gameId}/availability/override`, { updates: [update] });
      const label = {
        in: 'checked in', out: 'marked out',
        waitlist: 'moved to waitlist', none: 'cleared response',
      }[status] || status;
      toast.success(`${player.name || 'Player'} — ${label} ✓`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    }
  };

  // 2026-07-28 (mig 109) — flag / clear a no-show. Same override
  // endpoint; flagging also marks the player OUT (they weren't there),
  // and the backend clears their roster rows so their stats stop
  // counting the game (live-game flags included, as of 2026-08-20).
  //
  // 2026-08-20 (no-show list) — flagging is no longer historical-only.
  // Real case from Gilles: a player checked in, teams were balanced,
  // game time arrives and they're not there. The coach flags them AT
  // the rink. The player stays visible in a dedicated "No Shows"
  // section (previously they silently dropped into the Declined tail —
  // "their name disappears from the list"), and if teams were already
  // balanced we offer an immediate AI re-balance without them.
  const applyNoShow = async (player, flag) => {
    if (!player?.userId) return;
    setPlayerMenuFor(null);
    try {
      await api.post(`/teams/${teamId}/games/${gameId}/availability/override`, {
        updates: [{ userId: player.userId, status: 'out', noShow: flag }],
      });
      toast.success(flag
        ? `👻 ${player.name || 'Player'} flagged as a no-show — the team sees it next game`
        : `😇 ${player.name || 'Player'} forgiven — no-show cleared`);
      // Teams already balanced on a live game? Offer a one-tap
      // re-balance without the ghost. window.confirm keeps this
      // consistent with the lock/email confirms elsewhere on the page.
      if (flag && !isHistorical && teams &&
          confirm(`${player.name || 'This player'} was removed from the game.\n\nRe-balance the teams without them?`)) {
        await balanceTeams();
      }
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update no-show flag');
    }
  };

  // 2026-08-20 (no-show list) — the LATE ARRIVAL flow. The flagged
  // player walked in after all. The coach clears the flag and picks:
  //   'home' / 'away' — check in + manually assign to that side
  //   'rebalance'     — check in + let the AI re-balance everyone
  //   'clear'         — just forgive the flag, leave them checked out
  // Check-in happens FIRST because roster/add validates status='in'.
  // 2026-09-10 — load a player's status trail for this game.
  const openRsvpHistory = async (player) => {
    if (!player?.userId) return;
    setPlayerMenuFor(null);
    setRsvpHistoryFor({ player, rows: null, error: null });
    try {
      const { data } = await api.get(`/teams/${teamId}/games/${gameId}/availability-history/${player.userId}`);
      setRsvpHistoryFor({ player, rows: data.history || [], error: null });
    } catch (err) {
      setRsvpHistoryFor({ player, rows: [], error: err.response?.data?.error || 'Failed to load history' });
    }
  };

  const handleLateArrival = async (player, action) => {
    if (!player?.userId) return;
    setLateArrivalBusy(true);
    try {
      await api.post(`/teams/${teamId}/games/${gameId}/availability/override`, {
        updates: [{
          userId: player.userId,
          status: action === 'clear' ? 'out' : 'in',
          noShow: false,
        }],
      });
      if (action === 'home' || action === 'away') {
        await api.post(`/teams/${teamId}/games/${gameId}/roster/add`, {
          userId: player.userId, team: action,
        });
        const label = action === 'home'
          ? (game?.home_team_label || 'Home')
          : (game?.away_team_label || 'Away');
        toast.success(`😅 ${player.name || 'Player'} is back in — assigned to ${label}`);
      } else if (action === 'rebalance') {
        toast.success(`😅 ${player.name || 'Player'} is back in — re-balancing…`);
        await balanceTeams();
      } else if (action === 'checkin') {
        // Teams not built yet — check-in is all there is to do; they'll
        // be picked up by the balancer like everyone else.
        toast.success(`😅 ${player.name || 'Player'} is back in`);
      } else {
        toast.success(`😇 ${player.name || 'Player'} forgiven — no-show cleared`);
      }
      setLateArrivalFor(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update player');
    }
    setLateArrivalBusy(false);
  };

  // Open compose modal for a single player. Subject is pre-populated with
  // game date so the player has context in their inbox.
  const openComposeForPlayer = (player) => {
    if (!player?.userId) {
      toast.error('Can only message players who have a Game Day account');
      return;
    }
    setComposeTargets([{ userId: player.userId, name: player.name }]);
    // Pre-fill subject with team name + game date so the inbox preview gives
    // the recipient immediate context. Guarded because date formatting can
    // throw on malformed dates — falls back to just the team name.
    let dateLabel = '';
    try {
      if (game?.game_date) {
        dateLabel = format(new Date(safeDate(game.game_date) + 'T12:00:00'), 'EEE, MMM d');
      }
    } catch { /* leave dateLabel blank */ }
    const teamLabel = game?.team_name || 'Team';
    setComposeSubject(dateLabel ? `${teamLabel} — ${dateLabel}` : teamLabel);
    setComposeBody('');
    setPlayerMenuFor(null);
    setComposeOpen(true);
  };

  const sendComposeMessage = async () => {
    if (!composeSubject.trim()) return toast.error('Subject is required');
    if (!composeBody.trim())    return toast.error('Message body is required');
    if (composeTargets.length === 0) return toast.error('No recipients');
    // Apr 2026 (Tom_Notes B8) — at least one channel must be selected.
    // The UI guards against unchecking all three, but defend on submit.
    if (!Array.isArray(composeChannels) || composeChannels.length === 0) {
      return toast.error('Pick at least one channel (email, push, or in-app)');
    }
    setComposeSending(true);
    try {
      let contextLine = null;
      try {
        if (game?.game_date) {
          contextLine = `Game on ${format(new Date(safeDate(game.game_date) + 'T12:00:00'), 'EEE, MMM d')}`;
        }
      } catch { /* leave contextLine null */ }
      const { data } = await api.post(`/teams/${teamId}/messages/send`, {
        userIds: composeTargets.map(t => t.userId),
        subject: composeSubject.trim(),
        body:    composeBody.trim(),
        contextLine,
        // Apr 2026 (Tom_Notes B8) — channels and gameId.
        // gameId lets the backend tag the in-app notification row
        // with the game so the player's notification feed can deep-
        // link back to the right game on tap.
        channels: composeChannels,
        gameId:   gameId || null,
      });
      // Apr 2026 (Tom_Notes B8) — richer toast with per-channel
      // breakdown when the backend returns it. Falls back to the
      // legacy single-count message for older deployments.
      const bd = data.breakdown || {};
      const parts = [];
      if (bd.emailSent != null && composeChannels.includes('email')) {
        parts.push(`${bd.emailSent} email${bd.emailSent !== 1 ? 's' : ''}`);
      }
      if (bd.pushSent != null && composeChannels.includes('push')) {
        parts.push(`${bd.pushSent} push`);
      }
      if (bd.inAppSent != null && composeChannels.includes('in_app')) {
        parts.push(`${bd.inAppSent} in-app`);
      }
      const summary = parts.length > 0
        ? `Sent: ${parts.join(' · ')}`
        : `Sent to ${data.sent ?? 0} of ${data.total ?? 0} recipient${(data.total ?? 0) !== 1 ? 's' : ''}`;
      toast.success(`📧 ${summary}`);
      setComposeOpen(false);
      setComposeSubject('');
      setComposeBody('');
      setComposeTargets([]);
      // Reset channels back to email-only so the next compose starts clean.
      setComposeChannels(['email']);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send message');
    }
    setComposeSending(false);
  };

  // ── Group message from the game page ───────────────────────────────
  // Opens a picker anchored to the current game's RSVP state. Defaults to
  // "everyone who's checked in" — the most common use case. The scope
  // picker + waitlist toggle resolve to a concrete userIds array at send
  // time so the backend receives an explicit recipient list (no server-side
  // game-scope filter needed).
  const openGroupMessage = () => {
    // Pre-fill subject with team + game date for context in the inbox
    let dateLabel = '';
    try {
      if (game?.game_date) {
        dateLabel = format(new Date(safeDate(game.game_date) + 'T12:00:00'), 'EEE, MMM d');
      }
    } catch { /* leave blank */ }
    const teamLabel = game?.team_name || 'Team';
    setGroupMsgSubject(dateLabel ? `${teamLabel} — ${dateLabel}` : teamLabel);
    setGroupMsgBody('');
    setGroupMsgScope('checked_in');
    setGroupMsgIncludeWait(false);
    // Apr 30 2026 — reset the specific-player picker state on each
    // open so the coach starts fresh (previous session's picks
    // shouldn't carry over silently).
    setGroupMsgPickedIds(new Set());
    setGroupMsgSearch('');
    // 2026-07-17 — reset the server-resolved preview too, so a re-open
    // doesn't briefly flash the previous session's recipient list before
    // the effect above re-fetches for the freshly-reset 'checked_in' scope.
    setGroupMsgPreview({ loading: false, error: false, total: null, recipients: null });
    setGroupMsgPreviewExpanded(false);
    setGroupMsgOpen(true);
  };

  // 2026-07-16 — signature widened from a plain userIds array to a
  // payload object so this can also drive a server-resolved `group`
  // send (ft_no_response / ft_invited_no_response). The four original
  // scopes are unaffected — callers still pass { userIds }.
  const sendGroupMessage = async (payload) => {
    if (!groupMsgSubject.trim()) return toast.error('Subject is required');
    if (!groupMsgBody.trim())    return toast.error('Message body is required');
    const isGroupSend = !!payload?.group;
    const recipientIds = payload?.userIds || null;
    if (!isGroupSend) {
      // Explicit-userIds path (existing four scopes + Pick Players) —
      // the coach already sees the exact recipient list, so validate
      // it client-side before we even hit the network.
      if (!recipientIds || recipientIds.length === 0) {
        return toast.error('No recipients match — pick a different group');
      }
      if (recipientIds.length > 100) {
        return toast.error('Maximum 100 recipients per send');
      }
    }
    // Group-send path (FT-no-response scopes) has no client-side count
    // to validate against — the server resolves the real audience
    // (mini-game sessions + invites) and reports back via data.sent/
    // data.total, surfaced in the toast below either way.
    if (!Array.isArray(groupMsgChannels) || groupMsgChannels.length === 0) {
      return toast.error('Pick at least one channel (player preferences, email, push, or SMS)');
    }
    setGroupMsgSending(true);
    try {
      let contextLine = null;
      try {
        if (game?.game_date) {
          contextLine = `Game on ${format(new Date(safeDate(game.game_date) + 'T12:00:00'), 'EEE, MMM d')}`;
        }
      } catch { /* leave contextLine null */ }
      const { data } = await api.post(`/teams/${teamId}/messages/send`, {
        ...(isGroupSend ? { group: payload.group === 'everyone' ? 'all' : payload.group } : { userIds: recipientIds }),
        subject: groupMsgSubject.trim(),
        body:    groupMsgBody.trim(),
        contextLine,
        // Apr 2026 (Tom_Notes B8) — channels + gameId for in-app
        // deep-linking. The backend tags the notification with the
        // game so a tap from the bell icon can deep-link back here.
        // 2026-07-16 — gameId is now also REQUIRED server-side when
        // group is one of the FT-no-response scopes; this page is
        // always game-scoped so gameId is already populated here.
        channels: groupMsgChannels,
        gameId:   gameId || null,
      });
      const bd = data.breakdown || {};
      const parts = [];
      if (bd.emailSent != null && groupMsgChannels.includes('email')) {
        parts.push(`${bd.emailSent} email${bd.emailSent !== 1 ? 's' : ''}`);
      }
      if (bd.pushSent != null && groupMsgChannels.includes('push')) {
        parts.push(`${bd.pushSent} push`);
      }
      if (bd.inAppSent != null && groupMsgChannels.includes('in_app')) {
        parts.push(`${bd.inAppSent} in-app`);
      }
      if (bd.smsSent != null && (groupMsgChannels.includes('sms') || groupMsgChannels.includes('prefs')) && bd.smsSent > 0) {
        parts.push(`${bd.smsSent} SMS`);
      }
      if (groupMsgChannels.includes('prefs') && parts.length === 0) {
        if (bd.emailSent) parts.push(`${bd.emailSent} email${bd.emailSent !== 1 ? 's' : ''}`);
        if (bd.pushSent)  parts.push(`${bd.pushSent} push`);
      }
      if ((data.sent ?? 0) > 0 || parts.length > 0) {
        const summary = parts.length > 0
          ? `Sent: ${parts.join(' · ')}`
          : `Sent to ${data.sent ?? 0} of ${data.total ?? 0} recipient${(data.total ?? 0) !== 1 ? 's' : ''}`;
        toast.success(`📧 ${summary}`);
      } else {
        toast(
          '⚠️ No messages sent — no matching recipients (all opted out, or no email/push subscriptions).',
          { icon: '⚠️', duration: 6000 }
        );
      }
      setGroupMsgOpen(false);
      setGroupMsgSubject('');
      setGroupMsgBody('');
      setGroupMsgChannels(['email']);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send message');
    }
    setGroupMsgSending(false);
  };

  const applyOverride = async (status) => {
    const keys = Object.entries(selected).filter(([,v]) => v).map(([k]) => k);
    if (keys.length === 0) return toast.error('Select at least one player');
    setOverriding(true);
    try {
      // rowKey is either a userId UUID, "invite:UUID", or "pending-N" (no ID yet — skip)
      const updates = keys
        .filter(key => !key.startsWith('pending-'))
        .map(key => {
          if (key.startsWith('invite:')) {
            return { inviteId: key.slice(7), status };
          }
          return { userId: key, status };
        });
      await api.post(`/teams/${teamId}/games/${gameId}/availability/override`, { updates });
      const statusLabel = { in: 'in', out: 'out', waitlist: 'on the waitlist', none: 'no response' }[status] || status;
      toast.success(`Marked ${keys.length} player(s) as ${statusLabel}`);
      setSelected({});
      setOverrideMode(false);
      refresh();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to update'); }
    setOverriding(false);
  };

  // ── Send reminder to one specific player ────────────────────────
  const nudgeSingle = async (userId, playerName) => {
    setNudgingSingle(p => ({ ...p, [userId]: true }));
    try {
      const { data } = await api.post(`/teams/${teamId}/games/${gameId}/send-reminder`, { userId });
      // 2026-09-22 — read the result instead of assuming. A player with email
      // turned off used to get a cheerful "Jab sent!" while nothing went
      // anywhere. A coach's Jab now overrides the player's preferences
      // server-side; the message says when it did.
      if (data?.sent > 0 || data?.pushed > 0) {
        toast.success(`${data.message || `Jab sent to ${playerName}`} 📬`, { duration: data?.overrode ? 5000 : 4000 });
      } else {
        toast.error(data?.message || `Couldn't reach ${playerName}`, { duration: 7000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to nudge ${playerName}`);
    }
    setNudgingSingle(p => ({ ...p, [userId]: false }));
  };

  // ── Loading ─────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex items-center justify-center py-40">
      <div className="text-center">
        <div className="text-5xl mb-4">🏟</div>
        <div className="spinner mx-auto" />
      </div>
    </div>
  );

  if (!game) return (
    <div className="text-center py-32 text-secondary">
      <p>Game not found.</p>
      <button onClick={() => navigate(-1)} className="btn-ghost mt-4 mx-auto">Go Back</button>
    </div>
  );

  const availability = game.availability || [];
  const inPlayers      = availability.filter(a => a.status === 'in');
  const outPlayers     = availability.filter(a => a.status === 'out');
  const maybes         = availability.filter(a => a.status === 'maybe');
  const pendingPay     = availability.filter(a => a.status === 'pending_payment');
  // Apr 2026 — include pending_payment in the waitlist count + bucket.
  // These are spares the coach promoted but who haven't paid yet.
  // Shown in the waitlist section with a "Pending Payment" badge so
  // the coach knows their spot isn't locked. Without this, those rows
  // were invisible — dropped from every bucket.
  const waitlisted     = availability.filter(a => a.status === 'waitlist' || a.status === 'pending_payment');
  const allMembers     = game.all_members || [];
  const maxPlayers     = game.max_players || null;
  // Is this game in the past? Used to pick historical-vs-upcoming section
  // labels + scroll-anchor ids for the availability pills in the header.
  // Defined at outer scope (not inside any render IIFE) so both the header
  // pills AND the availability-card body can reference the same value.
  const isHistorical   = game?.status === 'completed';
  const respondedUserIds   = new Set(availability.filter(a => a.userId).map(a => a.userId));
  const respondedInviteIds = new Set(availability.filter(a => a.inviteId).map(a => a.inviteId));
  const notResponded = allMembers.filter(m =>
    m.userId ? !respondedUserIds.has(m.userId) : !respondedInviteIds.has(m.inviteId)
  );
  // Apr 30 2026 (App_Change_List #15) — set of users who have actually
  // RECEIVED an invitation/reminder for this game (server-fetched from
  // email_logs filtered to spareRequest/gameDayReminder/autoNudge,
  // status sent|skipped). Used to scope the Awaiting Response list to
  // people the team actually contacted, so coaches don't see e.g.
  // un-invited spares cluttering "who's late to respond." Pre-deploy
  // backends won't return this field — fall through to the legacy
  // behaviour (every roster member counted) so the UI never breaks.
  const invitedUserIdsSet = new Set(game?.invitedUserIds || []);
  const hasInvitedData    = Array.isArray(game?.invitedUserIds);
  // "Awaiting response" pill in the header — full-time players AND
  // spares who haven't RSVPed.
  // Apr 2026 (Tom_Notes B14) — was previously `m.isPrimary`-only, which
  // excluded spares. Coach asked to see spares too; tracking spare
  // responses matters when the team is short and you're waiting on
  // an "I'm in" from a spare you've already invited. Pending-invite
  // rows (no userId) stay excluded since you can't expect an in-app
  // response from someone who never accepted the invite.
  //
  // Apr 30 2026 (App_Change_List #15) — further refined:
  //   • Full-timers: included whether or not they've appeared in
  //     email_logs (they're auto-invited at game creation, but logs
  //     may be incomplete on older games / fresh test data).
  //   • Spares: ONLY shown in Awaiting Response when they've actually
  //     received a spareRequest invitation (or any other game email).
  //     Un-invited spares are not "waiting to respond" — the coach
  //     hasn't asked them yet.
  // When `hasInvitedData` is false (older backend), fall through to
  // the legacy "every non-RSVP'd roster member" behaviour so we don't
  // hide players from coaches running old infrastructure.
  const isAwaitingResponse = (m) => {
    if (!m.userId) return false;            // pending invites: no account yet
    if (respondedUserIds.has(m.userId)) return false;
    if (!hasInvitedData) return true;        // legacy backend
    if (m.isPrimary) return true;            // FT — always counted (auto-invited)
    return invitedUserIdsSet.has(m.userId);  // Spare — only if explicitly invited
  };
  const awaitingResponseCount = allMembers.filter(isAwaitingResponse).length;
  // Apr 2026 — detect game-full state for the current user. Matches
  // backend cap logic (Chunk 2): skaters count against max_players,
  // goalies capped at 2. Used by the RSVP button labels and the
  // "Spares pay $X" notice so neither mislead the user into
  // expecting a Stripe charge on a full game.
  const myPositionForCap = availability.find(a => a.userId === user?.id)?.position
    || allMembers.find(m => m.userId === user?.id)?.position
    || null;
  const imAGoalie       = myPositionForCap === 'goalie';
  const skatersInCount  = availability.filter(a => a.status === 'in' && a.position !== 'goalie').length;
  const goaliesInCount  = availability.filter(a => a.status === 'in' && a.position === 'goalie').length;
  const capForMe        = imAGoalie ? 2 : maxPlayers;
  const currentCountForMe = imAGoalie ? goaliesInCount : skatersInCount;
  const gameFullForMe   = capForMe != null && currentCountForMe >= capForMe;

  // Nudge can only go to real users (pending invites have no account
  // to notify). Apr 30 2026 (App_Change_List #15, #17) — also
  // restricted to people who are actually "Awaiting Response" per
  // the new definition (FT auto-invited OR spare explicitly invited).
  // The main "No Response — Jab Players" button now targets exactly
  // this set, and the confirmation modal lists exactly these names so
  // coaches see who they're about to Jab before sending.
  const nudgeableNotResponded = notResponded.filter(isAwaitingResponse);

  // Rating midpoint default — 3 on the 1–5 scale (3=Average). Used when a
  // player has no rating so their contribution to the sum is reasonable.
  const ratingSum = (players) => players.reduce((s, p) => s + (p.rating || 3), 0);

  return (
    <div className="animate-fade-in">
      {/* Back — sticky on phone so it doesn't get lost when the user
          scrolls deep into a long availability list. Previously this was
          a normal flow element that scrolled off screen; bug report:
          "can't scroll back to the top to click on the back button."
          `position: sticky` keeps it pinned to the top of the scroll
          container (which sits just below the app top bar) while the
          user scrolls anywhere on the page. The background matches
          --bg so it blends cleanly; a soft bottom border distinguishes
          it from the content flowing below. ── */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'var(--bg)',
          margin: '-12px -14px 12px',
          padding: '8px 14px 6px',
          borderBottom: '1px solid transparent',
        }}>
        <button onClick={() => navigate(-1)} className="btn-ghost -ml-1"
          style={{ padding: '8px 12px', fontSize: 12, minHeight: 38, borderRadius: 10 }}>
          <ChevronLeft size={15} /> Back
        </button>
      </div>

      {/* ── Game header ── */}
      <div className="card mb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="section-title text-2xl mb-2">
              {game.opponent_name ? <>vs {game.opponent_name}</> : format(new Date(safeDate(game.game_date) + 'T12:00:00'), 'EEE, MMM d')}
            </h1>
            {game.opponent_name && (
              <div className="text-sm font-semibold mb-1" style={{ color: 'var(--text-2)' }}>
                {format(new Date(safeDate(game.game_date) + 'T12:00:00'), 'EEE, MMM d')}
              </div>
            )}
            <div className="space-y-1 text-sm text-secondary">
              <div className="flex items-center gap-2">
                <Clock size={13} /> {formatTime(game.game_time)}
                {/* Apr 30 2026 (App_Change_List #18) — end time +
                    duration alongside the start time. End time uses
                    the same 12h/24h formatter as the start so they
                    visually match. Both fall through cleanly when
                    we can't compute them (no per-game duration AND
                    no team default). */}
                {(() => {
                  const durMin = game.duration_minutes || game.teamSettings?.default_game_duration_minutes;
                  const dur     = formatDuration(durMin);
                  const endTime = computeEndTime(game.game_time, durMin);
                  return (
                    <>
                      {endTime && <> – {formatTime(endTime)}</>}
                      {dur && (
                        <span className="text-xs" style={{ color: 'var(--text-3)' }}>
                          · {dur}
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>
              {game.arena_name ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <MapPin size={13} style={{ flexShrink: 0 }} />
                  {mapsUrl(game.arena_name, game.arena_address) ? (
                    <a
                      href={mapsUrl(game.arena_name, game.arena_address)}
                      target="_blank" rel="noopener noreferrer"
                      title="Open in Maps"
                      style={{ color: 'var(--ice)', textDecoration: 'underline', textUnderlineOffset: 2 }}>
                      {game.arena_name}
                    </a>
                  ) : (
                    <span>{game.arena_name}</span>
                  )}
                  {isCoach && (
                    <button
                      onClick={() => { setShowArenaChange(true); setArenaForm({ newArena: game.arena_name || '', newAddress: game.arena_address || '', reason: '' }); }}
                      className="text-xs font-semibold flex-shrink-0"
                      style={{ color: 'var(--ice)', background: 'none', border: 'none', cursor: 'pointer', padding: '1px 4px', borderRadius: 4 }}
                      title="Change arena — confirmed players will be notified">
                      ✏️ Edit
                    </button>
                  )}
                </div>
              ) : isCoach ? (
                <button
                  onClick={() => { setShowArenaChange(true); setArenaForm({ newArena: '', newAddress: '', reason: '' }); }}
                  className="flex items-center gap-2 text-sm"
                  style={{ color: 'var(--ice)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <MapPin size={13} /> + Add arena
                </button>
              ) : null}
              {game.arena_address && (
                <div className="text-xs ml-5 opacity-70">
                  {mapsUrl(game.arena_name, game.arena_address) ? (
                    <a
                      href={mapsUrl(game.arena_name, game.arena_address)}
                      target="_blank" rel="noopener noreferrer"
                      style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2 }}>
                      {game.arena_address}
                    </a>
                  ) : game.arena_address}
                </div>
              )}
              {/* Apr 2026 (Tom_Notes K1) — quick "message the team"
                  shortcut from the game page. Coaches already have
                  rich broadcast tools (Coach Actions → Email Players
                  + per-player message popovers). For players, the
                  most coherent path is the existing team chat —
                  it's where everyone already gathers, and a player
                  posting "running late!" reaches the same audience
                  as a coach broadcast. We deep-link into chat from
                  here so a player on the game page doesn't have to
                  navigate back to dashboard then into the team. */}
              {!isCoach && (
                <Link
                  to={`/team/${teamId}/chat`}
                  className="flex items-center gap-2 text-sm mt-1"
                  style={{ color: 'var(--ice)', textDecoration: 'none' }}
                  title="Open the team chat to message everyone playing"
                >
                  <MessageSquare size={13} />
                  <span style={{ fontWeight: 600 }}>Message the team</span>
                </Link>
              )}
              {/* 2026-06-10 — team media gallery, pre-filtered to this
                  game. Everyone on the team can post/view photos +
                  videos of the game (encrypted at rest). */}
              <Link
                to={`/team/${teamId}/media?game=${gameId}`}
                className="flex items-center gap-2 text-sm mt-1"
                style={{ color: 'var(--ice)', textDecoration: 'none' }}
                title="Photos & videos of this game"
              >
                <ImageIcon size={13} />
                <span style={{ fontWeight: 600 }}>Photos &amp; videos</span>
              </Link>
            </div>
          </div>

          {/* Score badge or Countdown */}
          {game.result_home_score !== null ? (
            <div className="glass px-5 py-2.5 text-center">
              <div className="text-xs text-secondary mb-0.5 uppercase tracking-wide">Final</div>
              <div className="section-title text-3xl" style={{ color: 'var(--goal)' }}>
                {game.result_home_score} — {game.result_away_score}
              </div>
            </div>
          ) : countdown ? (
            <div className="glass px-4 py-2.5 text-center" style={{ border: '1px solid var(--ice-a20)' }}>
              <div className="text-xs text-secondary mb-0.5 uppercase tracking-wide">Starts In</div>
              <div className="section-title text-xl" style={{ color: 'var(--ice)' }}>{countdown}</div>
            </div>
          ) : null}
        </div>

        {/* AI analysis */}
        {(game.ai_analysis?.content || aiPolling) && (
          <div className="mt-4 p-4 rounded-xl"
            style={{ background: 'var(--ice-a5)', border: '1px solid var(--ice-a15)' }}>
            <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-ice">
              <Zap size={13} /> AI Game Analysis
              {aiPolling && !game.ai_analysis?.content && (
                <span className="text-xs font-normal text-secondary ml-1 flex items-center gap-1">
                  <div className="spinner" style={{ width: 10, height: 10 }} /> analyzing…
                </span>
              )}
            </div>
            {game.ai_analysis?.content ? (
              <p className="text-sm text-secondary leading-relaxed">{game.ai_analysis.content}</p>
            ) : (
              <p className="text-sm text-secondary opacity-60 italic">AI is analyzing this game. Results will appear here shortly.</p>
            )}
          </div>
        )}
      </div>

      {/* ── Scoreboard relocated (Apr 30 2026, App_Change_List #12) ──
          Moved from above the RSVP block down to below the Balanced
          Teams card. Coach feedback: the page already opens with team
          info, RSVP, and team-rank — the scoreboard is something
          coaches/players check DURING/AFTER a game, by which point
          they've already scrolled past the early sections. Putting it
          right after the team display puts it in the natural
          "scrolling-during-the-game" path. ScoreboardCard component
          itself unchanged; only its position moved. */}

      {/* ── RSVP ── */}
      <div className="card mb-4">
        <h2 className="font-bold mb-3" style={{ fontSize: 15 }}>Are you playing?</h2>

        {/* ── Credit-available pill (Apr 2026) ──
            Shown to spares who have a positive credit balance in the
            team's currency, BUT aren't already in the pending_payment
            state (that state has its own credit-aware Pay Now CTA
            below). Primary purpose: keep waitlisted spares informed
            that a credit from a prior payment is sitting there and
            will cover them if the coach promotes — no surprise
            invoice. Also reassures spares who are about to re-RSVP
            that checking in again won't double-charge.

            Scoped deliberately:
              - Spares only (full-timers don't pay per game)
              - Not shown when status='in' (they're already playing,
                no action needed) or 'out' (they've declined; a
                credit reminder here would be noise)
              - Not shown when status='pending_payment' — the pay CTA
                below handles that state with a dedicated "Use $X
                credit" button wired to /credits/redeem
              - Only when payment collection is actually enabled for
                the team; otherwise the concept of credit doesn't apply.

            Apr 2026 update — DOES show when status='out'. A spare
            who paid, then checked out, gets a credit minted from that
            payment. If they're looking at the game page while 'out',
            seeing "you have $X credit, check back in to use it" is
            exactly the right cue — it answers their likely mental
            question "I paid already, why is this still showing a
            Pay Now button?".
            ── */}
        {myCreditCents > 0
          && game?.myIsPrimary === false
          && game?.teamSettings?.payment_enabled
          && game?.teamSettings?.spare_pay_required
          && myStatus !== 'in'
          && myStatus !== 'pending_payment'
          && (() => {
            const chargeCents = game?.teamSettings?.spare_total_cents || 0;
            const currency = (game?.teamSettings?.payment_currency || 'CAD').toUpperCase();
            const fmt = (c) => `$${(c / 100).toFixed(2)} ${currency}`;
            // Two DIFFERENT credit tallies — must not conflate them:
            //   • myCreditCents: lifetime available balance in team
            //     currency, across ALL games. Goes on the headline
            //     ("You have $X in credit").
            //   • reclaimableCents: credit that /spare-checkout will
            //     actually auto-apply for THIS game. Determines whether
            //     we can promise "no card needed" on check-in.
            // A spare with a $50 credit from Game A visiting Game B
            // has myCreditCents=50 but reclaimableCents=0 → pill still
            // shows (they do have credit) but with honest wording that
            // points them at the Pay Now screen rather than falsely
            // claiming auto-apply.
            const reclaimableCents = game?.myReclaimableCreditCents || 0;
            const reclaimCoversFully = chargeCents > 0 && reclaimableCents >= chargeCents;
            const hasCrossGameOnly = reclaimableCents === 0 && myCreditCents > 0;

            let message;
            if (myStatus === 'waitlist') {
              if (reclaimCoversFully) {
                message = 'If the coach promotes you off the waitlist, your credit will cover this game — no need to pay again.';
              } else if (hasCrossGameOnly) {
                // If promoted → pending_payment, the "Use $X credit"
                // button there calls /credits/redeem which DOES apply
                // any available credit (not game-scoped). So this
                // promise is honest for the waitlist → promote → pay
                // flow.
                message = `Your ${fmt(myCreditCents)} credit will be offered on the Pay Now screen if the coach promotes you.`;
              } else {
                // partial reclaimable — rare but possible
                message = `Your credit (${fmt(reclaimableCents)} for this game) doesn't fully cover ${fmt(chargeCents)}, but will be offered when you're promoted.`;
              }
            } else if (myStatus === 'out') {
              if (reclaimCoversFully) {
                message = 'You already paid for this game. Tap "I\'m In" to rejoin — your credit will be applied automatically, no new charge.';
              } else if (hasCrossGameOnly) {
                // Honest & non-committal. For pay_upfront / FCFS /
                // bypass, /spare-checkout bypasses credit entirely and
                // goes straight to Stripe — so "will be offered on Pay
                // Now" would be a broken promise. Keep it neutral.
                message = `You have ${fmt(myCreditCents)} credit from other games — it isn't automatically applied to this specific game.`;
              } else {
                message = `You have partial credit (${fmt(reclaimableCents)}) for this game. Tap "I'm In" to apply it; the remainder needs card payment.`;
              }
            } else {
              if (reclaimCoversFully) {
                message = 'When you check in, your credit will cover the game fee — no card needed.';
              } else if (hasCrossGameOnly) {
                // Works for pay_on_promote mode (waitlist → promote →
                // pending_payment → /credits/redeem). For pay_upfront
                // it's technically not applied in Stripe, but most
                // users in this state are pay_on_promote → waitlist.
                // Keep the phrasing soft ("may be") to not overcommit.
                message = `Your ${fmt(myCreditCents)} credit may be applied when you confirm, depending on your team's payment mode.`;
              } else {
                message = `Your credit (${fmt(reclaimableCents)} for this game) will apply toward the ${fmt(chargeCents)} fee; the remainder needs card payment.`;
              }
            }
            return (
              <div className="flex items-start gap-3 p-3 rounded-xl mb-3"
                style={{
                  background: 'rgba(0,232,122,0.08)',
                  border: '1px solid rgba(0,232,122,0.28)',
                }}>
                <span style={{ fontSize: 18, lineHeight: 1 }}>🎟</span>
                <div className="flex-1" style={{ minWidth: 0 }}>
                  <p className="text-sm font-semibold" style={{ color: 'var(--goal)' }}>
                    You have {fmt(myCreditCents)} in credit
                  </p>
                  <p className="text-xs text-secondary mt-0.5" style={{ lineHeight: 1.45 }}>
                    {message}
                  </p>
                </div>
              </div>
            );
          })()}

        {/* Spare payment pending state — shown when the coach has
            promoted the user off the waitlist AND spare_pay_required
            is on. Apr 2026: this used to just show a spinner with
            "Your spot will be confirmed once payment clears" but
            there was no way for the user to actually start payment.
            The coach's promote email has a Pay Now button, but when
            the user lands on the game page directly (or the email
            link dumps them here) they need an equivalent CTA. */}
        {myStatus === 'pending_payment' && (() => {
          // Credit awareness (Apr 2026):
          //   * chargeCents — what the spare owes for this game
          //   * myCreditCents — available credit in the team's currency
          //   * coversFully — credit covers the full charge; show a
          //     "Use $X credit to confirm" primary button instead of
          //     Stripe card checkout.
          //   * partial (credit > 0 but < charge) — for MVP, we don't
          //     auto-split. Show a hint below the Pay Now button; the
          //     spare pays the full charge on card, and the existing
          //     credit balance stays untouched.
          const chargeCents   = game?.teamSettings?.spare_total_cents || 0;
          const coversFully   = myCreditCents > 0 && myCreditCents >= chargeCents && chargeCents > 0;
          const hasPartial    = myCreditCents > 0 && myCreditCents < chargeCents;
          const currency      = (game?.teamSettings?.payment_currency || 'CAD').toUpperCase();
          const fmt = (cents) => `$${(cents / 100).toFixed(2)} ${currency}`;
          return (
          <>
            <div className="flex items-center gap-3 p-3 rounded-xl mb-3"
              style={{ background: 'rgba(0,232,122,0.10)', border: '1px solid rgba(0,232,122,0.3)' }}>
              <span style={{ fontSize: 18 }}>🎟</span>
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--goal)' }}>
                  {game?.teamSettings?.spare_pay_mode === 'pay_upfront'
                    ? 'Checkout needed to finish RSVP'
                    : 'You\'ve been promoted off the waitlist!'}
                </p>
                <p className="text-xs text-secondary mt-0.5">
                  Tap below to pay and lock in your spot
                  {chargeCents ? ` — ${fmt(chargeCents)}` : ''}. Card, Apple Pay & Google Pay accepted.
                </p>
              </div>
            </div>

            {coversFully ? (
              // Credit-first path — one tap to redeem the credit and
              // skip Stripe entirely. Primary (green) button.
              <button
                onClick={async () => {
                  setCreditBusy(true);
                  try {
                    const { data } = await api.post('/credits/redeem', { teamId, gameId });
                    if (data.freeGame) {
                      setMyStatus('in');
                      toast.success(data.message || '✅ You\'re in — paid from credit');
                      refresh();
                      return;
                    }
                  } catch (err) {
                    const d = err.response?.data;
                    if (d?.error === 'partial_credit') {
                      toast.error(`Credit only covers ${fmt(d.availableCents)} of ${fmt(d.chargeCents)} — pay the full amount below.`);
                    } else {
                      toast.error(friendlyError(err, 'Could not apply credit — try again'));
                    }
                  } finally {
                    setCreditBusy(false);
                  }
                }}
                disabled={creditBusy}
                className="btn-goal w-full flex items-center justify-center gap-2 py-3 mb-2"
                style={{ fontSize: 14, fontWeight: 700, borderRadius: 10 }}>
                {creditBusy
                  ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Applying credit…</>
                  : <>🎟 Use {fmt(chargeCents)} credit to confirm</>}
              </button>
            ) : (
              <button
                onClick={async () => {
                  // Re-fire the spare-checkout flow. Server already has
                  // this row in pending_payment state; creating a new
                  // session for an existing pending payment is fine —
                  // the /spare-checkout endpoint reuses an open session
                  // if one exists (see the "Check for existing unpaid
                  // session" block in payments.js).
                  try {
                    const { data } = await api.post('/payments/spare-checkout', {
                      teamId, gameId,
                    });
                    if (data.freeGame) {
                      setMyStatus('in');
                      toast.success('✅ You\'re in!');
                      refresh();
                      return;
                    }
                    window.location.href = data.url;
                  } catch (err) {
                    toast.error(friendlyError(err, 'Could not start payment — try again'));
                  }
                }}
                className="btn-goal w-full flex items-center justify-center gap-2 py-3 mb-2"
                style={{ fontSize: 14, fontWeight: 700, borderRadius: 10 }}>
                💳 Pay Now to Secure Your Spot
              </button>
            )}

            {hasPartial && (
              <p className="text-xs text-center mb-2" style={{ color: 'var(--text-2)' }}>
                You have {fmt(myCreditCents)} in credit — not enough to cover {fmt(chargeCents)}.
                Pay via card above, or use your credit toward a different game.
              </p>
            )}
            <p className="text-xs text-secondary text-center mb-2">
              Not ready to pay? Your spot stays reserved until you do.
            </p>
          </>
          );
        })()}

        {/* v2.5.x — Outstanding spare fee for an ALREADY checked-in spare.
            A spare who was promoted/checked-in but never paid (status 'in',
            no paid payment) gets a Pay CTA here so the coach's "Send payment
            link" email (which lands on this page) actually lets them pay.
            Distinct from the pending_payment block above, which only fires
            for not-yet-confirmed spares. */}
        {myStatus === 'in'
          && game?.myIsPrimary === false
          && !game?.myHasPaid
          && game?.teamSettings?.payment_enabled
          && game?.teamSettings?.spare_pay_required
          && Number(game?.teamSettings?.spare_fee_cents) > 0
          && (() => {
            const chargeCents = game?.teamSettings?.spare_total_cents || 0;
            const currency = (game?.teamSettings?.payment_currency || 'CAD').toUpperCase();
            const fmt = (cents) => `$${(cents / 100).toFixed(2)} ${currency}`;
            return (
              <div className="p-3 rounded-xl mb-3"
                style={{ background: 'rgba(255,149,0,0.10)', border: '1px solid rgba(255,149,0,0.3)' }}>
                <div className="flex items-center gap-3 mb-2">
                  <span style={{ fontSize: 18 }}>💳</span>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--puck)' }}>
                      Spare fee outstanding
                    </p>
                    <p className="text-xs text-secondary mt-0.5">
                      You're confirmed for this game but the spare fee
                      {chargeCents ? ` of ${fmt(chargeCents)}` : ''} hasn't been paid yet.
                    </p>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const { data } = await api.post('/payments/spare-checkout', { teamId, gameId });
                      if (data.freeGame) {
                        toast.success(data.message || '✅ Already settled.');
                        refresh();
                        return;
                      }
                      window.location.href = data.url;
                    } catch (err) {
                      toast.error(friendlyError(err, 'Could not start payment — try again'));
                    }
                  }}
                  className="btn-goal w-full flex items-center justify-center gap-2 py-3"
                  style={{ fontSize: 14, fontWeight: 700, borderRadius: 10 }}>
                  💳 Pay {chargeCents ? fmt(chargeCents) : 'spare fee'} now
                </button>
              </div>
            );
          })()}

        {/* Spare pay-required notice — shown before they tap In.
            Uses spare_total_cents (what Stripe will actually charge) instead
            of the coach's base fee, so the number the spare sees here matches
            what they'll see on the checkout page. No breakdown shown to the
            spare — they see one total, the coach sees the full breakdown in
            Team Settings.
            
            Apr 2026 — when the game is full, wording flips to waitlist
            framing: they won't be charged when they tap In (goes to
            waitlist); payment only kicks in if the coach promotes. */}
        {myStatus !== 'in' && myStatus !== 'pending_payment' &&
          game?.teamSettings?.payment_enabled &&
          game?.teamSettings?.spare_pay_required &&
          game?.myIsPrimary === false && (() => {
            // Apr 2026 — three distinct notice states. Mirrors the
            // pay-trigger logic in setAvailability() so the spare
            // reads the same story the backend is about to execute.
            //   (1) Game full → waitlist at no charge (pay only if promoted)
            //   (2) Direct-pay allowed (FCFS open / bypass active / already
            //       promoted) → "Spares pay $X when confirming"
            //   (3) Otherwise → waitlist-first under pay-required
            const fcfsActive    = !!game.fcfs_window_opened_at && !game.fcfs_closed_at;
            const bypassActive  = game.teamSettings.spare_bypass_waitlist_after_request === true
                               && !!game.spare_request_sent_at;
            // Mirror setAvailability's logic: only sticky-paid spares
            // count as "already approved for this game, pay bypassed."
            // A stale promoted_at (without payment) means they owe
            // money and should go through the waitlist-first flow.
            const alreadyPromotedAndPaid = !!game.myPromotedAt && !!game.myHasPaid;
            const payUpfront    = game.teamSettings.spare_pay_mode === 'pay_upfront';
            // v2.5.175 — mirror the tap handler + button label (v2.5.169)
            // and the backend (games.js: spareAutoWaitlist =
            // (spare_auto_waitlist ?? false) || payRequiredOnPromote).
            // When the coach has the explicit spare_auto_waitlist toggle
            // ON, a spare tapping In is WAITLISTED — they do NOT pay to
            // "secure a spot," because there is no spot to secure yet.
            // pay_upfront only enables direct pay when auto-waitlist is
            // OFF. FCFS still re-enables direct pay (backend turns
            // auto-waitlist off while an FCFS window is open). This block
            // was the last of three directPayAllowed derivations still
            // missing the guard — it drove the misleading "Spares pay $X
            // when confirming" + "Pay Now to Secure Your Spot" notice the
            // spare saw even though they'd actually be waitlisted.
            const explicitAutoWaitlist = game.teamSettings.spare_auto_waitlist === true;
            const directPayAllowed = fcfsActive || bypassActive || alreadyPromotedAndPaid
              || (payUpfront && !explicitAutoWaitlist);
            const amountLbl = game.teamSettings.spare_total_cents
              ? `$${(game.teamSettings.spare_total_cents / 100).toFixed(2)} ${(game.teamSettings.payment_currency || 'CAD').toUpperCase()}`
              : null;

            if (gameFullForMe) {
              return (
                <div className="flex items-center gap-2 p-2.5 rounded-xl mb-3"
                  style={{ background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.22)' }}>
                  <span style={{ fontSize: 15 }}>⏳</span>
                  <p className="text-xs" style={{ color: 'var(--text-2)' }}>
                    Game is full — tapping <strong>I'm In</strong> adds you to the
                    waitlist at no charge. If the coach promotes you off the waitlist,
                    you'll be sent a Pay Now link to secure your spot
                    {amountLbl ? ` (${amountLbl})` : ''}.
                  </p>
                </div>
              );
            }
            if (directPayAllowed) {
              // Apr 2026 — credit-aware swap. If the spare has enough
              // credit to cover the full spare fee (common case: they
              // paid earlier, checked out, and a credit was minted),
              // drop the "Spares pay $X when confirming — card..."
              // pitch and tell them the truth: their credit covers it,
              // no card needed. The existing "Pay Now" button also
              // renames to "Use Credit" below; the spare-checkout
              // endpoint already reclaims the credit automatically
              // when they tap it, so no extra frontend logic needed.
              //
              // CRITICAL: the "covers" check uses the SERVER-COMPUTED
              // myReclaimableCreditCents, not the raw lifetime balance.
              // /spare-checkout only auto-applies credits tied to this
              // specific game (same-game source payment, or last
              // applied_to=this game). A spare with credit from a
              // DIFFERENT game would see misleading "Rejoin with Credit"
              // UI and then get routed to Stripe. Scoping this to
              // reclaimable-credit-for-this-game keeps the promise
              // honest.
              const chargeCentsLocal = game.teamSettings.spare_total_cents || 0;
              const reclaimableCents = game.myReclaimableCreditCents || 0;
              const creditCoversFully = reclaimableCents > 0
                && chargeCentsLocal > 0
                && reclaimableCents >= chargeCentsLocal;
              if (creditCoversFully) {
                return (
                  <div className="flex items-center gap-2 p-2.5 rounded-xl mb-3"
                    style={{ background: 'rgba(0,232,122,0.08)', border: '1px solid rgba(0,232,122,0.25)' }}>
                    <span style={{ fontSize: 15 }}>🎟</span>
                    <p className="text-xs" style={{ color: 'var(--text-2)' }}>
                      Your credit covers this game — no new charge when you check in.
                    </p>
                  </div>
                );
              }
              return (
                <div className="flex items-center gap-2 p-2.5 rounded-xl mb-3"
                  style={{ background: 'rgba(0,212,255,0.07)', border: '1px solid rgba(0,212,255,0.15)' }}>
                  <span style={{ fontSize: 15 }}>💳</span>
                  <p className="text-xs" style={{ color: 'var(--text-2)' }}>
                    Spares pay{' '}
                    <strong style={{ color: 'var(--ice)' }}>{amountLbl || 'a fee'}</strong>
                    {' '}when confirming — card, Apple Pay &amp; Google Pay accepted.
                  </p>
                </div>
              );
            }
            // Default pay-required path: waitlist-first.
            return (
              <div className="flex items-center gap-2 p-2.5 rounded-xl mb-3"
                style={{ background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.22)' }}>
                <span style={{ fontSize: 15 }}>⏳</span>
                <p className="text-xs" style={{ color: 'var(--text-2)' }}>
                  Tap <strong>I'm In</strong> to join the waitlist at no charge.
                  You'll only pay {amountLbl ? <strong>({amountLbl})</strong> : 'a fee'} if the coach promotes
                  you off the waitlist.
                </p>
              </div>
            );
          })()
        }

        {/* Track-and-invoice notice — spare sees this when mode is 'track_and_invoice' */}
        {game?.myIsPrimary === false &&
          game?.teamSettings?.spare_payment_mode === 'track_and_invoice' && (
          <div className="flex items-center gap-2 p-2.5 rounded-xl mb-3"
            style={{ background: 'rgba(167,139,250,0.07)', border: '1px solid rgba(167,139,250,0.2)' }}>
            <span style={{ fontSize: 14 }}>📋</span>
            <p className="text-xs" style={{ color: 'var(--text-2)' }}>
              Games you play as a spare are tracked. Your coach will send an invoice at the end of the season.
              {game.teamSettings.spare_fee_cents
                ? ` Fee: $${(game.teamSettings.spare_fee_cents / 100).toFixed(2)} per game.`
                : ''}
            </p>
          </div>
        )}

        {/* Pay-per-game notice — spare sees this when mode is 'pay_per_game' and no Stripe (informational) */}
        {game?.myIsPrimary === false &&
          game?.teamSettings?.spare_payment_mode === 'pay_per_game' &&
          !game?.teamSettings?.spare_pay_required && (
          <div className="flex items-center gap-2 p-2.5 rounded-xl mb-3"
            style={{ background: 'rgba(255,149,0,0.07)', border: '1px solid rgba(255,149,0,0.2)' }}>
            <span style={{ fontSize: 14 }}>💰</span>
            <p className="text-xs" style={{ color: 'var(--text-2)' }}>
              A fee applies for spares this game.
              {game.teamSettings.spare_fee_cents
                ? ` Cost: $${(game.teamSettings.spare_fee_cents / 100).toFixed(2)} — your coach will follow up on payment.`
                : ' Check with your coach for details.'}
            </p>
          </div>
        )}

        <div className="flex gap-2">
          {/* When pending_payment, render just "I'm Out" (so the user
              can back out if they decide not to pay). The "I'm In"
              slot is replaced by the Pay Now CTA above. */}
          {(myStatus === 'pending_payment'
            ? [{ s: 'out', label: "I'm Out", color: 'var(--penalty)', bg: 'rgba(255,68,68,0.12)' }]
            : [
                { s: 'in',  label: "I'm In!", color: 'var(--goal)',    bg: 'rgba(0,255,136,0.12)' },
                { s: 'out', label: "I'm Out", color: 'var(--penalty)', bg: 'rgba(255,68,68,0.12)' },
              ]
          ).map(opt => {
            // Waitlist visual treatment — when the user is on the waitlist,
            // the "I'm In" side of the button acts as the "I've checked in
            // (and I'm on the waitlist)" affordance. Purple/gold accent
            // matches the waitlist section elsewhere in the page so the
            // state is unambiguous. Prior behaviour left NEITHER button
            // highlighted when myStatus === 'waitlist', which meant the
            // user had no visual confirmation their tap was received.
            const myIsSpare     = game?.myIsPrimary === false;
            const autoWaitlist  = game?.teamSettings?.spare_auto_waitlist;
            const payRequired   = game?.teamSettings?.payment_enabled
                                  && game?.teamSettings?.spare_pay_required;
            const isWaitlisted  = myStatus === 'waitlist';
            const isInside      = opt.s === 'in';
            // gameFullForMe hoisted above — see notes there.

            // "Active" = this button represents the user's current state.
            // The 'in' button lights up on both myStatus='in' (confirmed)
            // AND myStatus='waitlist' (they hit "I'm In" and landed on
            // the waitlist — still an accepted RSVP, just not confirmed).
            const isActive = myStatus === opt.s
                            || (isInside && isWaitlisted);

            // Pick palette based on active state + waitlist/normal.
            let bg, borderCol, fg;
            if (isActive && isInside && isWaitlisted) {
              // On the waitlist — purple palette matches the Waitlist
              // section below + all the other waitlist chips/badges.
              bg = 'rgba(167,139,250,0.14)';
              borderCol = 'rgba(167,139,250,0.55)';
              fg = '#a78bfa';
            } else if (isActive) {
              bg = opt.bg;
              borderCol = opt.color + '55';
              fg = opt.color;
            } else {
              bg = 'var(--surface-2)';
              borderCol = 'var(--border)';
              fg = 'var(--text-secondary)';
            }

            // Label — depends on state + spare treatment. Priority order:
            //   1. User is a spare, waitlist-confirmed  → "⏳ On Waitlist"
            //   2. User is a spare, 'in' button, not yet RSVPed, GAME FULL
            //        → "I'm In → Waitlist"
            //   3. User is a spare, 'in' button, not yet RSVPed, pay required
            //      BUT direct-pay isn't allowed (no FCFS, no bypass, not
            //      previously promoted) → "I'm In → Waitlist" (Apr 2026)
            //   4. User is a spare, 'in' button, not yet RSVPed, pay required
            //      AND direct-pay allowed → "Pay Now to Secure Your Spot 💳"
            //   5. User is a spare, 'in' button, not yet RSVPed, auto-waitlist on
            //        → "I'm In (→ Waitlist)"
            //   6. User is 'in' (confirmed)            → "✓ You're In"
            //   7. User is 'out'                       → "You're Out"
            //   8. Fallback                            → plain opt.label
            const fcfsActive    = !!game?.fcfs_window_opened_at && !game?.fcfs_closed_at;
            const bypassActive  = game?.teamSettings?.spare_bypass_waitlist_after_request === true
                               && !!game?.spare_request_sent_at;
            // Sticky-paid check: only treat as direct-pay when the
            // spare was promoted AND paid. Stale promoted_at alone
            // is not enough — see setAvailability's comment.
            const alreadyPromotedAndPaid = !!game?.myPromotedAt && !!game?.myHasPaid;
            const payUpfront    = game?.teamSettings?.spare_pay_mode === 'pay_upfront';
            // v2.5.169 — the backend auto-waitlists a spare whenever the
            // explicit spare_auto_waitlist toggle is ON, EVEN in
            // pay_upfront mode (see games.js: spareAutoWaitlist =
            // (spare_auto_waitlist ?? false) || payRequiredOnPromote;
            // pay_upfront only skips the pay-on-promote-derived gating,
            // not the explicit toggle). So if that toggle is on, tapping
            // does NOT secure a spot — the spare is waitlisted and pays
            // only after promotion. Treat direct-pay as NOT allowed then,
            // so the label/notice tell the truth. FCFS still overrides
            // (the backend sets spareAutoWaitlist=false when FCFS is
            // active), so we keep fcfsActive able to re-enable direct pay.
            const explicitAutoWaitlist = game?.teamSettings?.spare_auto_waitlist === true;
            const directPayAllowed = fcfsActive
              || bypassActive
              || alreadyPromotedAndPaid
              || (payUpfront && !explicitAutoWaitlist);

            let label;
            if (isInside && isWaitlisted) {
              label = '⏳ On Waitlist';
            } else if (isInside && myIsSpare && myStatus !== 'in' && gameFullForMe) {
              // Apr 2026 — game is full, spare RSVP will go to the
              // waitlist regardless of pay-required / auto-waitlist.
              // Don't mislead the spare with a "Pay Now" label.
              label = "I'm In → Waitlist";
            } else if (isInside && myIsSpare && myStatus !== 'in'
                       && payRequired && !directPayAllowed) {
              // Apr 2026 — pay-required but no direct-pay gate open.
              // Tap lands them on the waitlist for free; they'll see
              // Pay Now only after the coach promotes.
              label = "I'm In → Waitlist";
            } else if (isInside && myIsSpare && myStatus !== 'in'
                       && payRequired && autoWaitlist && !directPayAllowed) {
              // v2.5.169 — belt-and-suspenders: team auto-waitlists
              // spares AND no direct-pay gate is open → tapping adds
              // them to the waitlist (free); payment only happens after
              // the coach promotes. (Covered by the branch above when
              // !directPayAllowed, but kept explicit for clarity.)
              label = "I'm In → Waitlist";
            } else if (isInside && myIsSpare && myStatus !== 'in' && payRequired) {
              // v2.5.169 — the tap handler routes to the FREE waitlist
              // path whenever `gameFullForMe || !directPayAllowed`
              // (see the onClick above). So the ONLY time a tap really
              // secures a confirmed spot via Stripe is when a direct-pay
              // gate is open AND the game isn't full. Label must match
              // that exact condition — otherwise a spare who will land
              // on the waitlist is wrongly told "Pay Now to Secure Your
              // Spot." If they'd be waitlisted, say so.
              const willWaitlist = gameFullForMe || !directPayAllowed;
              if (willWaitlist) {
                label = "I'm In → Waitlist";
              } else {
                // Real direct-pay path. Credit-aware swap: a checked-out
                // spare who previously paid for this game has a credit;
                // tapping auto-reclaims it and flips to 'in' with no
                // Stripe charge, so "Pay Now" there is misleading. Uses
                // SERVER-COMPUTED myReclaimableCreditCents (this game
                // only), not the lifetime balance.
                const chargeCentsLocal = game?.teamSettings?.spare_total_cents || 0;
                const reclaimableCents = game?.myReclaimableCreditCents || 0;
                const creditCoversFully = reclaimableCents > 0
                  && chargeCentsLocal > 0
                  && reclaimableCents >= chargeCentsLocal;
                label = creditCoversFully
                  ? '🎟 Rejoin with Credit'
                  : 'Pay Now to Secure Your Spot 💳';
              }
            } else if (isInside && myIsSpare && myStatus !== 'in' && autoWaitlist) {
              label = "I'm In → Waitlist";
            } else if (isInside && myStatus === 'in') {
              label = "✓ You're In";
            } else if (opt.s === 'out' && myStatus === 'out') {
              label = "You're Out";
            } else {
              label = opt.label;
            }

            // Disable the button when it represents the user's CURRENT
            // confirmed state. Re-tapping "✓ You're In" was triggering
            // the spare-auto-waitlist flow on teams with that setting
            // enabled, demoting a confirmed-in spare back to waitlist —
            // which is never what the user wants. Same logic for "You're
            // Out". When on waitlist or pending payment, the button stays
            // active so the user can re-RSVP. ── */
            const isAlreadyActive =
              (isInside && myStatus === 'in') ||
              (opt.s === 'out' && myStatus === 'out');

            return (
              <button
                key={opt.s}
                onClick={() => {
                  if (isAlreadyActive) return;
                  if (rsvpBusy) return;
                  setAvailability(opt.s);
                }}
                disabled={isAlreadyActive || rsvpBusy}
                aria-pressed={isAlreadyActive}
                aria-busy={rsvpBusy}
                className="flex-1 py-3 rounded-xl font-semibold text-sm transition-all active:scale-95"
                style={{
                  background: bg,
                  border: `1.5px solid ${borderCol}`,
                  color: fg,
                  WebkitTapHighlightColor: 'transparent',
                  cursor: (isAlreadyActive || rsvpBusy) ? 'default' : 'pointer',
                  // Apr 2026 (Tom_Notes K2) — visual feedback during
                  // the in-flight request so the user knows the tap
                  // registered. The `disabled` attribute also
                  // prevents the second tap from firing onClick.
                  opacity: rsvpBusy && !isAlreadyActive ? 0.6 : 1,
                }}>
                {rsvpBusy && !isAlreadyActive ? '…' : label}
              </button>
            );
          })}
        </div>

        {/* ── Note for the team (optional) ──
            Separate from the RSVP action above so that checking in/out
            stays a one-tap motion. The note is only ever shown AFTER
            the user has RSVPed — before then, there's no status to
            attach a note to, and the field would be visual noise.
            Saves independently via /availability/notes (idempotent,
            status-preserving). Dirty-state detection keeps the Save
            button from firing when nothing's changed. ── */}
        {myStatus && ['in', 'out', 'waitlist', 'pending_payment'].includes(myStatus) && (
          <div style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: '1px solid var(--border)',
          }}>
            <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-2)' }}>
              📝 Note for the team <span className="font-normal" style={{ color: 'var(--text-3)' }}>(optional)</span>
            </label>
            <textarea
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder={myStatus === 'out'
                ? 'e.g. work meeting ran over'
                : myStatus === 'waitlist' || myStatus === 'pending_payment'
                  ? 'e.g. happy to play if a spot opens'
                  : 'e.g. running a few minutes late'}
              className="input-field"
              style={{
                // 16px prevents iOS Safari auto-zoom on focus
                fontSize: 16,
                lineHeight: 1.35,
                resize: 'none',
                minHeight: 56,
                padding: '10px 12px',
              }}
            />
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-xs" style={{ color: 'var(--text-3)' }}>
                Visible to your team · {noteDraft.length}/500
              </span>
              {noteDraft !== (myNotes || '') && (
                <button
                  type="button"
                  onClick={async () => {
                    if (savingNote) return;
                    setSavingNote(true);
                    try {
                      await api.post(
                        `/teams/${teamId}/games/${gameId}/availability/notes`,
                        { notes: noteDraft }
                      );
                      setMyNotes(noteDraft.trim());
                      toast.success('Note saved');
                    } catch {
                      toast.error('Could not save note — try again');
                    } finally {
                      setSavingNote(false);
                    }
                  }}
                  disabled={savingNote}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-all"
                  style={{
                    background: 'var(--ice-a10)',
                    border: '1px solid var(--ice-a30)',
                    color: 'var(--ice)',
                    cursor: savingNote ? 'wait' : 'pointer',
                  }}>
                  {savingNote ? 'Saving…' : 'Save note'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Coach actions ── */}
      {isCoach && (
      <div className="card mb-4" style={{ border: '1px solid rgba(0,212,255,0.2)', background: 'linear-gradient(135deg, var(--surface) 0%, rgba(0,212,255,0.04) 100%)' }}>
        <div className="flex items-center gap-2 mb-4">
          <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Zap size={14} style={{ color: 'var(--ice)' }} />
          </div>
          <h2 className="font-bold" style={{ color: 'var(--ice)' }}>Coach Actions</h2>
          {balanceScore !== null && (
            <div className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-lg"
              style={{ background: 'rgba(0,255,136,0.1)', border: '1px solid rgba(0,255,136,0.25)' }}>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'var(--font-display)', letterSpacing: '0.1em' }}>BALANCE</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--goal)', fontFamily: 'var(--font-display)' }}>{balanceScore}</span>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>/100</span>
            </div>
          )}
        </div>

        {/* Roster-changed warning — shown after someone RSVPs when teams already set */}
        {rosterChanged && teams && (
          <div className="flex items-start gap-3 p-3 rounded-xl mb-3"
            style={{ background: 'rgba(255,149,0,0.12)', border: '1px solid rgba(255,149,0,0.35)' }}>
            <AlertTriangle size={16} style={{ color: 'var(--puck)', flexShrink: 0, marginTop: 1 }} />
            <div className="flex-1">
              <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--puck)', margin: '0 0 2px' }}>
                Player list changed after teams were set
              </p>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.4 }}>
                A player joined or dropped. Re-balance to keep teams fair.
              </p>
            </div>
            <button onClick={() => setRosterChanged(false)}
              style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 2, flexShrink: 0 }}>
              ✕
            </button>
          </div>
        )}

        {/* Apr 30 2026 (App_Change_List #13) — Auto-Jab history block
            removed from Coach Actions. The upcoming-reminders card
            below already shows pending auto-jabs (when they're queued
            but haven't fired yet); past auto-jabs that already
            dispatched live in the Notification Log page (admin →
            notification-log) for full audit. The duplicate "🔔 Auto-
            Jab Active" card here was confusing because it mixed
            historical sends with the action-y Coach Actions buttons.
            `nudgeLog` is still loaded by the page (line ~384) so
            existing analytics keep working — just not rendered here. */}

        {/* Apr 2026 — missing-position warning right above the balance
            button. The balancer needs every checked-in player's
            position to place them correctly; a player without one
            falls through to forward by default, which can throw off
            the defense distribution the v3 algorithm worked hard to
            optimize. Surfacing the warning here (the moment a coach
            is about to balance) is more actionable than the per-row
            badge on PlayersPage — they can either manually assign
            positions and re-balance, or accept the default.

            The warning lists the affected player names so the coach
            can find them quickly in the Players page. Hidden when no
            checked-in players are missing positions. Coach-only. */}
        {isCoach && (() => {
          const missing = inPlayers.filter(p =>
            !p.position && (!Array.isArray(p.positions) || p.positions.length === 0)
          );
          if (missing.length === 0) return null;
          // Only show first 3 names to keep the banner compact; "+N more" handles overflow.
          const names = missing.slice(0, 3).map(p => p.name).filter(Boolean);
          const more = missing.length - names.length;
          return (
            <div className="mb-3 p-3 rounded-xl flex items-start gap-2"
              style={{ background: 'rgba(255,68,68,0.10)', border: '1px solid rgba(255,68,68,0.30)' }}>
              <AlertTriangle size={16} style={{ color: 'var(--penalty)', flexShrink: 0, marginTop: 1 }} />
              <div className="flex-1" style={{ minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--penalty)', margin: '0 0 2px' }}>
                  {missing.length === 1
                    ? `1 checked-in player has no position`
                    : `${missing.length} checked-in players have no position`}
                </p>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.4 }}>
                  {names.join(', ')}{more > 0 ? ` +${more} more` : ''}.
                  {' '}The balancer will treat them as forwards by default. Set their position
                  in the Players tab to get accurate defense placement.
                </p>
              </div>
            </div>
          );
        })()}

        {/* Apr 2026 — missing-rating warning, parallel to the
            missing-position warning above. Both can show at once
            (independent issues). The balancer silently defaults
            unrated players to rating 3 (average), which works but
            distorts team strength — a "developing" player rated as
            average pulls the strong team down; a "skilled" player
            rated as average pulls the weak team up. Surfacing here
            (before the Balance button) lets the coach fix it before
            the bad data feeds the algorithm.

            Uses orange (var(--puck)) instead of red — missing rating
            is degraded but functional, missing position is broken. */}
        {isCoach && (() => {
          const missing = inPlayers.filter(p => !p.rating);
          if (missing.length === 0) return null;
          const names = missing.slice(0, 3).map(p => p.name).filter(Boolean);
          const more = missing.length - names.length;
          return (
            <div className="mb-3 p-3 rounded-xl flex items-start gap-2"
              style={{ background: 'rgba(255,149,0,0.10)', border: '1px solid rgba(255,149,0,0.30)' }}>
              <AlertTriangle size={16} style={{ color: 'var(--puck)', flexShrink: 0, marginTop: 1 }} />
              <div className="flex-1" style={{ minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--puck)', margin: '0 0 2px' }}>
                  {missing.length === 1
                    ? `1 checked-in player has no rating`
                    : `${missing.length} checked-in players have no rating`}
                </p>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.4 }}>
                  {names.join(', ')}{more > 0 ? ` +${more} more` : ''}.
                  {' '}The balancer will treat them as average (3) until you set a rating.
                  Tap their row in the Players tab to assign Elite, Strong, Average,
                  Developing, or Beginner.
                </p>
              </div>
            </div>
          );
        })()}

        {/* v2.5.149 — Balance Teams / Bulk Assign buttons REMOVED
            from Coach Actions. Coach feedback: the action belongs in
            a dedicated Teams section, not buried in Coach Actions.
            The button (and the empty-state that prompts a first
            balance) now lives in the always-visible Teams card below
            (see the `use_app_for_balancing !== false` block). This
            keeps Coach Actions focused on communications + results. */}

        {/* Apr 30 2026 — "Clear & Restart Teams" moved out of Coach
            Actions into the Balanced Teams card header (lives with
            the teams themselves now). Removed from this location. */}

        {/* When teams exist and the roster changed, surface a quick
            re-balance shortcut here too (the main re-balance button
            lives in the Balanced Teams card per B6). The warning at
            the top of Coach Actions tells the coach WHY; this button
            lets them act without scrolling. Hidden once they hit it
            (rosterChanged resets after a successful re-balance). */}
        {teams && rosterChanged && (
          <button onClick={balanceTeams} disabled={balancing || inPlayers.length < 2}
            className="w-full mb-3"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 8, padding: '12px 18px',
              background: 'var(--puck)',
              color: '#fff',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              borderRadius: 12, border: 'none',
              cursor: inPlayers.length < 2 ? 'not-allowed' : 'pointer',
              opacity: inPlayers.length < 2 ? 0.45 : 1,
              transition: 'transform 0.1s, opacity 0.1s',
              WebkitTapHighlightColor: 'transparent',
            }}
            onTouchStart={e => { e.currentTarget.style.transform = 'scale(0.97)'; }}
            onTouchEnd={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
            {balancing
              ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Re-balancing…</>
              : <><Shuffle size={14} /> Re-balance now</>}
          </button>
        )}

        {/* ── Coach action grid ──
            Apr 2026 (Tom_Notes B5 declutter) — grouped by intent so
            the eye doesn't bounce. Communications first (the most
            common ask: "ping people"). Result button below as a
            distinct row. FCFS and "Jab non-responders" stay
            conditional — they only render when relevant, so the
            grid breathes on most games. */}
        <div className="grid grid-cols-2 gap-2 coach-actions-grid">
          {/* Communications row */}
          <button onClick={sendReminder}
            className="btn-ghost flex items-center justify-center gap-2 py-2.5 w-full"
            style={{ fontWeight: 700, fontSize: 12, letterSpacing: '0.04em', borderRadius: 10 }}>
            <Send size={13} /> Send Reminder
          </button>
          <button onClick={openSparePicker}
            className="btn-ghost flex items-center justify-center gap-2 py-2.5 w-full"
            style={{ fontWeight: 700, fontSize: 12, letterSpacing: '0.04em', borderRadius: 10 }}>
            <Users size={13} /> Invite Spares
          </button>

          {/* Apr 30 2026 — renamed "Email Players" → "Message Players"
              since the modal lets coaches send via email + push + in-app
              (the "Send via" picker at the bottom). "Email" was misleading
              when only the email channel is checked. */}
          <button onClick={openGroupMessage}
            className="btn-ghost col-span-2 flex items-center justify-center gap-2 py-2.5 w-full"
            style={{ fontWeight: 700, fontSize: 12, letterSpacing: '0.04em', borderRadius: 10,
                     color: 'var(--ice)', borderColor: 'var(--ice-a30)', background: 'var(--ice-a8)' }}>
            <Mail size={13} /> Message Players
          </button>

          {/* ── FCFS button removed (v2.5.78, May 7 2026) ──
              Coach feedback: "remove the Accept any spare that
              pays button. We don't need it." Removed the
              top-level coach-actions trigger. The underlying
              backend infrastructure (POST /fcfs/open + /close,
              fcfs_window_opened_at column reads) is left in
              place — payment flow logic at line ~680, ~1914,
              and ~2101 still reads fcfs_window_opened_at to
              determine `fcfsActive` and gate direct-pay
              eligibility, which keeps any pre-existing FCFS
              windows working until they're closed. New FCFS
              windows can no longer be opened via the UI; if
              the feature is ever reinstated, restore the
              button block from git history. The fcfsOpen modal
              at the bottom of the file is now unreachable but
              kept compiling-as-dead-code rather than ripped
              out, in case the feature returns. */}

          {/* ── Conditional row: Jab no-responders ──
              Only renders when there are real (non-pending-invite)
              players who have not RSVP'd yet. Same purple tint as
              the in-list per-row Jab button so the action is
              visually consistent across the page. */}
          {nudgeableNotResponded.length > 0 && (
            <button onClick={() => setShowNonResponders(true)}
              className="col-span-2 flex items-center justify-center gap-2 py-2.5 w-full"
              style={{
                background: 'rgba(124,58,237,0.85)',
                color: '#fff',
                fontWeight: 700, fontSize: 12, letterSpacing: '0.04em',
                borderRadius: 10, border: 'none', cursor: 'pointer',
              }}>
              <MessageSquare size={13} />
              {nudgeableNotResponded.length} No Response — Jab Players
            </button>
          )}

          {/* Record Result moved to the Balanced Teams card (May 1 2026)
              per coach feedback — recording the score is the natural
              conclusion of the game flow that lives there, not a
              top-level coach action. */}
        </div>
      </div>
      )}  {/* end isCoach */}

      {/* ── Upcoming Reminders for this game ──
          Apr 2026 (Tom_Notes B12) — surface the cron-driven events
          queued for THIS game directly under Coach Actions.
          Collapsible (the card defaults to showing 1 on mobile / 2
          on desktop, with a "Show N more" toggle). Renders nothing
          when the list is empty so the section quietly disappears
          for games with no automation queued (e.g. games happening
          tonight where reminders already fired, or games with
          auto-reminders disabled team-wide). */}
      {isCoach && gameReminders.length > 0 && (
        <div className="mb-4">
          <UpcomingRemindersCard
            reminders={gameReminders}
            onGameClick={() => {}}
            title="🔔 Upcoming Reminders for this Game"
          />
        </div>
      )}

      {/* ── Drinks Duty ──
          2026-07-10 (batch-B D1, per Gilles) — CONSOLIDATED: the page
          used to show TWO drinks sections (this compact
          DrinksDashboardTile + the full DrinksPanel much further down).
          Now there is exactly ONE, here at the top: the full-featured
          DrinksPanel (players see who's assigned or a volunteer button;
          coaches get the complete assign/unassign/dismiss controls).
          The #drinks-duty anchor moved up with it so the Dashboard
          drinks card's deep link still lands on the drinks section. */}
      <div id="drinks-duty" />
      {teamId && gameId && (
        <DrinksPanel teamId={teamId} gameId={gameId} isCoach={isCoach} userId={user?.id} />
      )}

      {/* ── Availability ── */}
      <div className="card mb-4">
        {/* Heading row — on phones the title sits above the count pills so
            the pills don't fight for horizontal space with the title and
            the Collapse/Edit buttons. The pills then scroll horizontally
            in their own row rather than wrapping onto 4+ lines. On tablet+
            everything fits on a single line inline with the title. ── */}
        <div
          className={isPhone
            ? 'flex flex-col gap-2 mb-3'
            : 'flex items-center justify-between mb-4'}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setAvailabilityOpen(p => !p)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAvailabilityOpen(p => !p); } }}
            className={isPhone
              ? 'flex items-center justify-between gap-3 w-full text-left'
              : 'flex items-center gap-3 flex-1 text-left'}
            style={{ cursor: 'pointer' }}
          >
            <h2 className="font-bold text-lg">Availability</h2>
            {/* On phone: just the Collapse button sits inline with the title.
                The pills move to a dedicated row below. On tablet+: all pills
                + Collapse stay on the same row. ── */}
            {!isPhone && (
              <div className="flex items-center gap-2 flex-wrap">
              {(() => {
                const jumpTo = (sectionId) => (ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  if (!availabilityOpen) setAvailabilityOpen(true);
                  // Defer so the section is in the DOM by the time we scroll.
                  // Guard against missing ids (e.g. tapping an empty bucket's
                  // pill before the section renders) — bail silently rather
                  // than throw.
                  setTimeout(() => {
                    const el = document.getElementById(sectionId);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, availabilityOpen ? 0 : 120);
                };
                const pillBase = {
                  padding: '4px 10px',
                  borderRadius: 999,
                  fontWeight: 700,
                  fontSize: 15,
                  lineHeight: 1.2,
                  cursor: 'pointer',
                  background: 'transparent',
                  border: '1px solid transparent',
                  transition: 'background 0.12s, border-color 0.12s',
                };
                // Helper to dim pills whose bucket is empty. Keeps them
                // visible so the coach sees the 0 count at a glance, but
                // makes clear tapping won't scroll anywhere.
                const dimIfZero = (count) => count === 0
                  ? { opacity: 0.45, cursor: 'default' }
                  : {};
                // Each pill links to a distinct section; historical games use
                // the "out" id for Declined -> Checked Out since both buckets
                // map to the same stored id.
                const inSectionId   = isHistorical ? 'availability-section-attended' : 'availability-section-in';
                const outSectionId  = isHistorical ? 'availability-section-out'      : 'availability-section-out';
                const waitSectionId = 'availability-section-waitlist';
                const noRespSectionId = isHistorical ? 'availability-section-noresp' : 'availability-section-awaiting';
                return (
                  <>
                    {(() => {
                      // Chunk 1 (Apr 2026): split the IN count into
                      // non-goalie players and goalies. Coach feedback:
                      // "21 IN" was ambiguous when some of those were
                      // goalies (roster cap was off, spare-request
                      // logic was off). Now the pill shows
                      // "21 IN · 🥅 2" so the goalie count is visible
                      // at a glance. Goalie detection uses the
                      // availability row's position (added to the API
                      // response in Chunk 1). Falls back gracefully
                      // when position is missing (old caches) —
                      // everything counts as a non-goalie.
                      const inGoalies = inPlayers.filter(p => p.position === 'goalie').length;
                      const inSkaters = inPlayers.length - inGoalies;
                      return (
                        <button type="button" onClick={jumpTo(inSectionId)}
                          style={{ ...pillBase, color: 'var(--goal)', background: 'rgba(0,255,136,0.08)', borderColor: 'rgba(0,255,136,0.2)', ...dimIfZero(inPlayers.length) }}
                          title="Jump to checked-in players">
                          {/* 2026-09-29 — player icon (matches the Schedule page) instead of ✅ */}
                          🏒 {inSkaters}
                          {inGoalies > 0 && (
                            <span style={{ marginLeft: 6, fontWeight: 600, opacity: 0.85 }}>
                              · 🥅 {inGoalies}
                            </span>
                          )}
                        </button>
                      );
                    })()}
                    <button type="button" onClick={jumpTo(outSectionId)}
                      style={{ ...pillBase, color: 'var(--penalty)', background: 'rgba(255,68,68,0.08)', borderColor: 'rgba(255,68,68,0.2)', ...dimIfZero(outPlayers.length) }}
                      title="Jump to declined / checked-out players">
                      ❌ {outPlayers.length}
                    </button>
                    <button type="button" onClick={jumpTo(noRespSectionId)}
                      style={{ ...pillBase, color: 'var(--puck)', background: 'rgba(255,149,0,0.08)', borderColor: 'rgba(255,149,0,0.2)', ...dimIfZero(awaitingResponseCount) }}
                      title="Jump to players awaiting response">
                      🤔 {awaitingResponseCount}
                    </button>
                    {waitlisted.length > 0 && (
                      <button type="button" onClick={jumpTo(waitSectionId)}
                        style={{ ...pillBase, color: '#a78bfa', background: 'rgba(167,139,250,0.1)', borderColor: 'rgba(167,139,250,0.3)' }}
                        title="Jump to waitlisted players">
                        ⏳ {waitlisted.length} waitlist
                      </button>
                    )}
                    {/* Apr 2026 — surface pending-payment spares as a
                        distinct pill so coaches can see at a glance
                        how many spares owe payment after a promote. */}
                    {pendingPay.length > 0 && (
                      <button type="button" onClick={jumpTo(waitSectionId)}
                        style={{ ...pillBase, color: 'var(--puck)', background: 'rgba(255,149,0,0.10)', borderColor: 'rgba(255,149,0,0.3)' }}
                        title="Spares promoted off the waitlist but haven't paid yet">
                        💳 {pendingPay.length} pending pay
                      </button>
                    )}
                    {maxPlayers && (
                      <span className="text-xs px-2 py-0.5 rounded-md flex-shrink-0"
                        style={{ background: 'rgba(0,212,255,0.08)', color: 'var(--ice)', border: '1px solid rgba(0,212,255,0.2)' }}>
                        Max {maxPlayers}
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
            )}
            {/* Collapse / Expand chip — stays inline with the title on every
                layout (phone renders it right of the title; tablet+ renders
                it at the end of the pill row). ── */}
            <span className="text-xs font-semibold px-2 py-0.5 rounded-md flex-shrink-0"
              style={{ color: 'var(--ice)', background: 'var(--ice-a10)', border: '1px solid var(--ice-a20)' }}>
              {availabilityOpen ? 'Collapse' : 'Expand'}
            </span>
          </div>
          {isCoach && !isPhone && (
            <button
              onClick={() => { setOverrideMode(p => !p); setSelected({}); setClearAllConfirm(false); }}
              className="text-xs font-semibold px-2.5 py-1 rounded-lg transition-all ml-2 flex-shrink-0"
              style={{
                background: overrideMode ? 'var(--ice-a15)' : 'var(--surface-2)',
                border: `1px solid ${overrideMode ? 'var(--ice-a30)' : 'var(--border)'}`,
                color: overrideMode ? 'var(--ice)' : 'var(--text-2)',
              }}>
              {overrideMode ? '✕ Cancel' : '✏️ Edit'}
            </button>
          )}
          {/* Phone-only pill strip — horizontal scroll, no wrap. Pills
              render here on phone (they'd otherwise wrap onto 4+ lines
              in the inline row above). The Edit button is appended on
              the right so it's still reachable without hunting. ── */}
          {isPhone && (
            <div
              className="flex items-center gap-2"
              style={{
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
                padding: '2px 0',
                marginTop: 2,
              }}>
              {(() => {
                const jumpTo = (sectionId) => (ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  if (!availabilityOpen) setAvailabilityOpen(true);
                  setTimeout(() => {
                    const el = document.getElementById(sectionId);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, availabilityOpen ? 0 : 120);
                };
                // Phone pills: same palette as tablet+, but tighter padding
                // and whiteSpace:nowrap to survive horizontal scroll. ── */
                const pillBase = {
                  padding: '4px 10px',
                  borderRadius: 999,
                  fontWeight: 700,
                  fontSize: 14,
                  lineHeight: 1.2,
                  cursor: 'pointer',
                  background: 'transparent',
                  border: '1px solid transparent',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                };
                const dimIfZero = (count) => count === 0
                  ? { opacity: 0.45, cursor: 'default' }
                  : {};
                const inSectionId   = isHistorical ? 'availability-section-attended' : 'availability-section-in';
                const outSectionId  = isHistorical ? 'availability-section-out'      : 'availability-section-out';
                const waitSectionId = 'availability-section-waitlist';
                const noRespSectionId = isHistorical ? 'availability-section-noresp' : 'availability-section-awaiting';
                return (
                  <>
                    {(() => {
                      // Chunk 1 — same split logic as the desktop pill.
                      const inGoalies = inPlayers.filter(p => p.position === 'goalie').length;
                      const inSkaters = inPlayers.length - inGoalies;
                      return (
                        <button type="button" onClick={jumpTo(inSectionId)}
                          style={{ ...pillBase, color: 'var(--goal)', background: 'rgba(0,255,136,0.08)', borderColor: 'rgba(0,255,136,0.2)', ...dimIfZero(inPlayers.length) }}>
                          ✅ {inSkaters}
                          {inGoalies > 0 && (
                            <span style={{ marginLeft: 4, fontWeight: 600, opacity: 0.85 }}>
                              · 🥅 {inGoalies}
                            </span>
                          )}
                        </button>
                      );
                    })()}
                    <button type="button" onClick={jumpTo(outSectionId)}
                      style={{ ...pillBase, color: 'var(--penalty)', background: 'rgba(255,68,68,0.08)', borderColor: 'rgba(255,68,68,0.2)', ...dimIfZero(outPlayers.length) }}>
                      ❌ {outPlayers.length}
                    </button>
                    <button type="button" onClick={jumpTo(noRespSectionId)}
                      style={{ ...pillBase, color: 'var(--puck)', background: 'rgba(255,149,0,0.08)', borderColor: 'rgba(255,149,0,0.2)', ...dimIfZero(awaitingResponseCount) }}>
                      🤔 {awaitingResponseCount}
                    </button>
                    {waitlisted.length > 0 && (
                      <button type="button" onClick={jumpTo(waitSectionId)}
                        style={{ ...pillBase, color: '#a78bfa', background: 'rgba(167,139,250,0.1)', borderColor: 'rgba(167,139,250,0.3)' }}>
                        ⏳ {waitlisted.length}
                      </button>
                    )}
                    {pendingPay.length > 0 && (
                      <button type="button" onClick={jumpTo(waitSectionId)}
                        style={{ ...pillBase, color: 'var(--puck)', background: 'rgba(255,149,0,0.10)', borderColor: 'rgba(255,149,0,0.3)' }}
                        title="Pending payment">
                        💳 {pendingPay.length}
                      </button>
                    )}
                    {maxPlayers && (
                      <span
                        style={{
                          padding: '3px 8px', borderRadius: 6,
                          fontSize: 11, fontWeight: 700,
                          background: 'rgba(0,212,255,0.08)',
                          color: 'var(--ice)',
                          border: '1px solid rgba(0,212,255,0.2)',
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                        }}>
                        Max {maxPlayers}
                      </span>
                    )}
                    {isCoach && (
                      <button
                        onClick={() => { setOverrideMode(p => !p); setSelected({}); setClearAllConfirm(false); }}
                        style={{
                          padding: '4px 10px', borderRadius: 8,
                          fontSize: 12, fontWeight: 700,
                          background: overrideMode ? 'var(--ice-a15)' : 'var(--surface-2)',
                          border: `1px solid ${overrideMode ? 'var(--ice-a30)' : 'var(--border)'}`,
                          color: overrideMode ? 'var(--ice)' : 'var(--text-2)',
                          marginLeft: 4,
                          cursor: 'pointer',
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                        }}>
                        {overrideMode ? '✕ Cancel' : '✏️ Edit'}
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {availabilityOpen && (<>
        {/* Coach bulk-edit toolbar */}
        {isCoach && overrideMode && (() => {
          // Build the full editable list: real users + pending invites (coach can set status for both)
          const editableMembers = (game.all_members || []).filter(m => m.userId);
          // Pending members: no userId. Those WITH inviteId can be overridden; those without are display-only.
          const pendingMembers  = (game.all_members || []).filter(m => !m.userId && m.name);

          const respondedUserIds   = new Set(availability.filter(a => a.userId).map(a => a.userId));
          const respondedInviteIds = new Set(availability.filter(a => a.inviteId).map(a => a.inviteId));

          const notRespondedReal    = editableMembers.filter(m => !respondedUserIds.has(m.userId));
          const notRespondedPending = pendingMembers.filter(m => !respondedInviteIds.has(m.inviteId));

          // availability entries for pending invites (coach-set)
          const inviteAvail = availability.filter(a => a.inviteId);
          const inviteIn    = inviteAvail.filter(a => a.status === 'in');
          const inviteOut   = inviteAvail.filter(a => a.status === 'out');
          const inviteMaybe = inviteAvail.filter(a => a.status === 'maybe');

          const allRows = [
            // Real users who responded
            ...inPlayers.filter(p => p.userId).map(p => ({ ...p, rowKey: p.userId, statusIcon: '✅', statusLabel: 'In', _isInvite: false })),
            ...inviteIn.map(p => ({ ...p, rowKey: `invite:${p.inviteId}`, statusIcon: '✅', statusLabel: 'In', _isInvite: true })),
            ...pendingPay.filter(p => p.userId).map(p => ({ ...p, rowKey: p.userId, statusIcon: '💳', statusLabel: 'Paying…', _isInvite: false })),
            ...maybes.filter(p => p.userId).map(p => ({ ...p, rowKey: p.userId, statusIcon: '🤔', statusLabel: 'Maybe', _isInvite: false })),
            ...inviteMaybe.map(p => ({ ...p, rowKey: `invite:${p.inviteId}`, statusIcon: '🤔', statusLabel: 'Maybe', _isInvite: true })),
            ...outPlayers.filter(p => p.userId).map(p => ({ ...p, rowKey: p.userId, statusIcon: '❌', statusLabel: 'Out', _isInvite: false })),
            ...inviteOut.map(p => ({ ...p, rowKey: `invite:${p.inviteId}`, statusIcon: '❌', statusLabel: 'Out', _isInvite: true })),
            // No-response — real users first, then pending
            ...notRespondedReal.map(m => ({ ...m, rowKey: m.userId, statusIcon: '—', statusLabel: 'No response', status: null, _isInvite: false })),
            ...notRespondedPending.map((m, i) => ({ ...m, rowKey: m.inviteId ? `invite:${m.inviteId}` : `pending-${i}`, statusIcon: '—', statusLabel: 'No response', status: null, _isInvite: true })),
          ];
          const allIds = allRows.map(p => p.rowKey);
          const selectedCount = Object.values(selected).filter(Boolean).length;

          return (
            <div>
              {/* Bulk action bar */}
              <div className="flex items-center gap-2 mb-3 p-2 rounded-xl flex-wrap"
                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                <button onClick={() => selectAll(allIds)}
                  className="text-xs font-semibold px-2 py-1 rounded-lg"
                  style={{ background: 'var(--surface-3)', color: 'var(--text-2)', border: '1px solid var(--border)' }}>
                  {allIds.every(id => selected[id]) ? 'Deselect All' : 'Select All'}
                </button>
                <span className="text-xs text-secondary flex-1">
                  {selectedCount > 0 ? `${selectedCount} selected` : 'Tap players to select'}
                </span>
                {selectedCount > 0 && (
                  <>
                    <button onClick={() => applyOverride('in')} disabled={overriding}
                      className="text-xs font-bold px-3 py-1.5 rounded-lg transition-all"
                      style={{ background: 'rgba(0,255,136,0.15)', color: 'var(--goal)', border: '1px solid rgba(0,255,136,0.3)' }}>
                      ✅ Mark In
                    </button>
                    <button onClick={() => applyOverride('out')} disabled={overriding}
                      className="text-xs font-bold px-3 py-1.5 rounded-lg transition-all"
                      style={{ background: 'rgba(255,59,59,0.12)', color: 'var(--penalty)', border: '1px solid rgba(255,59,59,0.25)' }}>
                      ❌ Mark Out
                    </button>
                    <button onClick={() => applyOverride('none')} disabled={overriding}
                      className="text-xs font-bold px-3 py-1.5 rounded-lg transition-all"
                      style={{ background: 'var(--surface-3)', color: 'var(--text-2)', border: '1px solid var(--border)' }}>
                      — No Response
                    </button>
                    {/* Bulk nudge — only enabled when all selected players are real users with no response */}
                    {Object.entries(selected).filter(([,v])=>v).every(([key]) => {
                      if (key.startsWith('invite:')) return false; // pending can't be nudged
                      return allRows.find(r => r.rowKey === key)?.status === null;
                    }) && Object.entries(selected).filter(([,v])=>v).length > 0 && (
                      <button
                        onClick={async () => {
                          const keys = Object.entries(selected).filter(([,v])=>v).map(([id])=>id);
                          for (const key of keys) {
                            if (key.startsWith('invite:')) continue; // skip pending
                            const row = allRows.find(r => r.rowKey === key);
                            if (row?.status === null && row.userId) await nudgeSingle(row.userId, row.name || 'Player');
                          }
                        }}
                        disabled={overriding}
                        className="text-xs font-bold px-3 py-1.5 rounded-lg transition-all"
                        style={{ background: 'rgba(107,80,230,0.15)', border: '1.5px solid rgba(107,80,230,0.5)', color: '#7c5ce8' }}>
                        📬 Jab Selected
                      </button>
                    )}
                  </>
                )}
                {overriding && <div className="spinner" style={{ width: 14, height: 14 }} />}
                {/* Add to Waitlist — bulk action replacing the old "Clear All Responses".
                    Parks selected players on the waitlist so they don't count toward
                    the confirmed roster but are still tracked and can be promoted later. */}
                <button
                  onClick={() => applyOverride('waitlist')}
                  disabled={overriding || Object.values(selected).filter(Boolean).length === 0}
                  className="text-xs font-bold px-3 py-1.5 rounded-lg transition-all ml-auto flex-shrink-0"
                  style={{
                    background: 'rgba(167,139,250,0.12)',
                    border: '1px solid rgba(167,139,250,0.3)',
                    color: '#a78bfa',
                  }}>
                  ⏳ Add to Waitlist
                </button>
              </div>

              {/* Player list with checkboxes */}
              <div className="space-y-1">
                {allRows.map((p, i) => {
                  const isSelected = !!selected[p.rowKey];
                  const isMe = p.userId === user?.id;
                  const isFirstPending = p._isInvite && (i === 0 || !allRows[i-1]._isInvite);
                  return (
                    <React.Fragment key={p.rowKey}>
                      {isFirstPending && (
                        <p className="text-xs font-semibold px-1 pt-2 pb-0.5"
                          style={{ color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          Not joined yet ({notRespondedPending.length + inviteIn.length + inviteMaybe.length + inviteOut.length}) — invite pending
                        </p>
                      )}
                      <div
                        role={p._isInvite && !p.inviteId ? undefined : 'button'}
                        tabIndex={p._isInvite && !p.inviteId ? undefined : 0}
                        onClick={() => p._isInvite && !p.inviteId ? null : toggleSelect(p.rowKey)}
                        onKeyDown={e => !(p._isInvite && !p.inviteId) && (e.key === 'Enter' || e.key === ' ') && toggleSelect(p.rowKey)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all"
                        style={{
                          background: isSelected ? 'var(--ice-a8)' : 'var(--surface-2)',
                          border: `1px solid ${isSelected ? 'var(--ice-a30)' : 'transparent'}`,
                          cursor: 'pointer',
                        }}>
                        <div style={{
                          width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                          background: isSelected ? 'var(--ice)' : 'var(--surface-3)',
                          border: `1.5px solid ${isSelected ? 'var(--ice)' : 'var(--border)'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#000', fontSize: 11, fontWeight: 800,
                        }}>
                          {isSelected ? '✓' : ''}
                        </div>
                        <span className="text-base w-5 text-center flex-shrink-0">{p.statusIcon}</span>
                        <span className="flex-1 text-sm font-medium truncate">
                          {p.name || 'Player'}{isMe ? ' (you)' : ''}
                          {p._isInvite && (
                            <span className="ml-2 text-xs font-normal" style={{ color: 'var(--text-2)' }}>
                              (not joined)
                            </span>
                          )}
                        </span>
                        {p.position === 'goalie' && (
                          <span className="badge text-xs flex-shrink-0"
                            style={{ background: 'rgba(0,212,255,0.1)', color: 'var(--ice)', border: '1px solid rgba(0,212,255,0.25)' }}>
                            🥅 G
                          </span>
                        )}
                        <span className="text-xs flex-shrink-0"
                          style={{ color: p.status === 'in' ? 'var(--goal)' : p.status === 'out' ? 'var(--penalty)' : p.status === 'maybe' ? 'var(--puck)' : 'var(--text-3)' }}>
                          {p.statusLabel}
                        </span>
                        {/* Per-player nudge — only for real users with no response */}
                        {p.status === null && isCoach && !isMe && p.userId && (
                          <button
                            onClick={e => { e.stopPropagation(); nudgeSingle(p.userId, p.name || 'Player'); }}
                            disabled={!!nudgingSingle[p.userId]}
                            title={`Send reminder to ${p.name}`}
                            className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold transition-all"
                            style={{
                              background: 'rgba(107,80,230,0.15)',
                              border: '1.5px solid rgba(107,80,230,0.5)',
                              color: '#7c5ce8',
                              marginLeft: 6,
                              whiteSpace: 'nowrap',
                            }}>
                            {nudgingSingle[p.userId]
                              ? <div className="spinner" style={{ width: 10, height: 10 }} />
                              : <>📬 Jab</>}
                          </button>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Normal read-only view */}
        {!overrideMode && (
          availability.length === 0 && notResponded.length === 0 ? (
            <p className="text-secondary text-sm">No responses yet. Send a reminder to get RSVPs!</p>
          ) : (
            <div className="space-y-1.5">
              {(() => {
                // Build a combined player map: userId/inviteId → { ...member, status, icon, statusColor }
                // Merge availability responses onto the full member list
                const statusMeta = {
                  in:       { icon: '✅', color: 'var(--goal)'    },
                  maybe:    { icon: '🤔', color: 'var(--puck)'    },
                  out:      { icon: '❌', color: 'var(--penalty)' },
                  waitlist: { icon: '⏳', color: '#a78bfa'        },
                };

                // Index availability by userId and inviteId
                const availByUser   = {};
                const availByInvite = {};
                availability.forEach(a => {
                  if (a.userId)   availByUser[a.userId]     = a;
                  if (a.inviteId) availByInvite[a.inviteId] = a;
                });

                // Merge all_members with availability, deduplicate
                const allPlayers = allMembers.map(m => {
                  const avail = m.userId ? availByUser[m.userId] : availByInvite[m.inviteId];
                  const meta  = avail ? statusMeta[avail.status] : null;
                  return {
                    ...m,
                    status:      avail?.status ?? null,
                    icon:        meta?.icon    ?? null,
                    statusColor: meta?.color   ?? null,
                    isSpare:     avail?.isSpare ?? !m.isPrimary,
                    // Apr 2026 — forward paidThisGame from availability.
                    // Used by the "💳 Paid" badge on confirmed spares.
                    paidThisGame: avail?.paidThisGame ?? false,
                    // May 1 2026 (post-deploy bug 1) — forward sort-key
                    // timestamps from the availability row to the merged
                    // player object so sortByWaitlistOrder can read them.
                    // Without these, p.respondedAt and p.waitlistPriorityAt
                    // are both undefined → the sort falls back to
                    // alphabetical, and the coach's "⬆ Top" bump is
                    // invisible in the UI even though the DB write
                    // succeeded. Root cause of "I clicked Top but the
                    // player didn't move." All three previous attempts
                    // at fixing #39 fixed the SQL/sort/migration but
                    // missed this passthrough.
                    respondedAt:        avail?.respondedAt        ?? null,
                    waitlistPriorityAt: avail?.waitlistPriorityAt ?? null,
                    notes:              avail?.notes              ?? null,
                    // 2026-08-20 (no-show list) — forward the ghost flag.
                    // The badge + "Clear No-Show" menu item read p.noShow,
                    // but this merge never carried it over from the
                    // availability row, so neither ever rendered in this
                    // list. Also feeds the dedicated No Shows bucket.
                    noShow:             avail?.noShow === true,
                  };
                });

                // Pending invite members not yet in allPlayers (no userId, show as no-response)
                const pendingMembers = (game.all_members || []).filter(m => !m.userId && m.name);

                // Sort order within a group: in → maybe → waitlist → null (no response) → out
                const STATUS_ORDER = { in: 0, maybe: 1, waitlist: 2, null: 3, out: 4 };
                const sortPlayers = (arr) =>
                  [...arr].sort((a, b) =>
                    (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3)
                  );

                // Split into four categories, ordered by FT-before-Spare and
                // skaters-before-goalies within each tier. Requested UX: see
                // who the regulars are first, then the goalies, then spares.
                const fullTimers    = sortPlayers(allPlayers.filter(p => p.isPrimary  && p.position !== 'goalie'));
                const fullGoalies   = sortPlayers(allPlayers.filter(p => p.isPrimary  && p.position === 'goalie'));
                const spares        = sortPlayers(allPlayers.filter(p => !p.isPrimary && p.position !== 'goalie'));
                const spareGoalies  = sortPlayers(allPlayers.filter(p => !p.isPrimary && p.position === 'goalie'));

                // ── Availability-centric buckets (new layout) ────────────
                // For upcoming games, coaches want to see who's confirmed
                // vs. waiting vs. silent — not who's full-time vs. spare.
                // Section A: Checked In — all players (FT + spare, any
                // position) with status 'in'. Sort FT first, then spares.
                // v2.5.97 — coach feedback: "On the game page when we
                // invited a goalie that did not have an email he's put
                // on the bottom of the list. Make sure that anyone
                // tagged as a goalie is automatically at the top of
                // the list no matter if there's a spare or full-time."
                //
                // FLIPPED tier precedence: goalie status now wins over
                // FT-vs-spare. Result order:
                //   FT goalies → spare goalies → FT skaters → spare skaters
                // (alphabetical within each of the four sub-buckets).
                //
                // The previous order (FT → spare, then goalie-first
                // within each) buried a spare goalie below 14 FT skaters,
                // which is wrong because the question coaches ask first
                // is "do we have a goalie?" and a spare goalie is
                // strictly better than no goalie.
                //
                // Hybrid players whose team-default isn't 'goalie' but
                // who happen to be playing goalie this game would need
                // their position changed for the goalies-first
                // ordering to apply — that's an edge case.
                const sortByTierThenName = (arr) => [...arr].sort((a, b) => {
                  // Tier 1 (NEW PRIMARY): Goalie before non-goalie.
                  const aGoalie = a.position === 'goalie' ? 1 : 0;
                  const bGoalie = b.position === 'goalie' ? 1 : 0;
                  if (aGoalie !== bGoalie) return bGoalie - aGoalie;
                  // Tier 2: Full-time before spare.
                  if ((b.isPrimary ? 1 : 0) !== (a.isPrimary ? 1 : 0)) {
                    return (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0);
                  }
                  // Tier 3: Alphabetical.
                  return (a.name || '').localeCompare(b.name || '');
                });
                // Apr 2026 — Waitlist needs a DIFFERENT order than the
                // confirmed-in / declined lists. The waitlist is a
                // queue: first-come-first-served. Coaches use "↑ Top"
                // to bump someone to the front.
                //
                // Apr 30 2026 (App_Change_List #39) — sort key updated.
                // Was: respondedAt ASC. Was buggy because every status
                // write overwrote responded_at, so a "Top" bump only
                // lasted until the player tapped anything else and the
                // server stamped responded_at = NOW(). Now we sort by
                // COALESCE(waitlistPriorityAt, respondedAt) ASC so a
                // coach bump (writes the priority field) survives
                // every other write. Backend migration 057 added the
                // column; before that migration runs, waitlistPriorityAt
                // is undefined and the sort falls back to respondedAt
                // identical to the old behaviour.
                const sortByWaitlistOrder = (arr) => [...arr].sort((a, b) => {
                  const ka = a.waitlistPriorityAt || a.respondedAt;
                  const kb = b.waitlistPriorityAt || b.respondedAt;
                  // Items without either timestamp (shouldn't happen
                  // on the waitlist but defensive) sort to the end.
                  if (!ka && !kb) return (a.name || '').localeCompare(b.name || '');
                  if (!ka) return 1;
                  if (!kb) return -1;
                  return new Date(ka) - new Date(kb);
                });
                // 2026-08-20 (no-show list) — flagged no-shows get their
                // OWN section instead of vanishing into the Declined
                // tail. Root cause of "if the coach checks out a no-show
                // near game time, their name disappears from the list":
                // the flag sets status='out', which dropped them to the
                // bottom Declined bucket (collapsed and easy to miss).
                // Excluded from every other bucket so a player appears
                // exactly once.
                const noShowsAll       = sortByTierThenName(allPlayers.filter(p => p.noShow === true));
                const checkedInAll     = sortByTierThenName(allPlayers.filter(p => p.status === 'in' && !p.noShow));
                // Apr 2026 — include pending_payment spares in the
                // waitlist bucket. They're on the hook for payment
                // but not confirmed-in, so waitlist is the honest
                // place. Row will display a "Pending Payment" badge.
                const waitlistedAll    = sortByWaitlistOrder(
                  allPlayers.filter(p => p.status === 'waitlist' || p.status === 'pending_payment')
                );
                // Apr 2026 (Tom_Notes B14) — Awaiting Response now
                // includes BOTH full-timers AND spares who haven't
                // RSVPed. Pending invites (no userId) are deliberately
                // excluded — those people don't have an account yet
                // and can't be expected to "respond" in-app.
                // Sort puts FT before spares (sortByTierThenName
                // already handles that, plus goalies-first within
                // each tier per B13).
                //
                // Apr 30 2026 (App_Change_List #15) — additional gate:
                // spares are only listed when they were actually
                // invited (email_logs check). Mirrors the
                // `isAwaitingResponse` predicate used for the header
                // pill so both views always agree on the count.
                const awaitingAll = sortByTierThenName(
                  allPlayers.filter(p => {
                    if (!p.userId) return false;
                    if (p.status !== null) return false;
                    if (!hasInvitedData) return true;
                    if (p.isPrimary) return true;
                    return invitedUserIdsSet.has(p.userId);
                  })
                );

                // May 4 2026 — split awaiting-response by FT/spare ×
                // skater/goalie so coaches can see at a glance which
                // sub-group hasn't responded. Coach asked: "split the
                // awaiting response by full timers, spares, spare
                // goalies / full time goalies." Each sub-group is
                // rendered as its own labeled section under the main
                // "Awaiting Response" header. Empty sub-groups are
                // suppressed so the page stays compact when (e.g.)
                // there are no spare goalies in play.
                const isGoalieRow = (p) =>
                  p.position === 'goalie' || (Array.isArray(p.positions) && p.positions.includes('goalie'));
                const awaitingFTPlayers   = awaitingAll.filter(p =>  p.isPrimary && !isGoalieRow(p));
                const awaitingFTGoalies   = awaitingAll.filter(p =>  p.isPrimary &&  isGoalieRow(p));
                const awaitingSparePlayers = awaitingAll.filter(p => !p.isPrimary && !isGoalieRow(p));
                const awaitingSpareGoalies = awaitingAll.filter(p => !p.isPrimary &&  isGoalieRow(p));
                // Declined (out) — always shown as a small collapsed tail
                // for both upcoming and historical games so coaches can see
                // who passed on the game.
                const declinedAll      = sortByTierThenName(allPlayers.filter(p => p.status === 'out' && !p.noShow));

                // Historical mode — for completed games, show the final
                // RSVP state as the record of who played. Game Day doesn't
                // track a per-game "did they actually show up" flag, so the
                // end-of-game availability status IS the historical record:
                //   status='in'      → Attended (they committed and presumably played)
                //   status='out'     → Checked Out (they explicitly declined)
                //   status='waitlist'/null → No Response (never confirmed)
                // If a per-game attendance sheet feature is added later, the
                // `attendance` prop shape can be extended and the bucketing
                // updated — but for now RSVP status is the authoritative record.
                // (isHistorical comes from the outer scope — shared with the
                // header pill render so both always agree.)
                const historicalAttended = sortByTierThenName(
                  allPlayers.filter(p => p.status === 'in' && !p.noShow)
                );
                const historicalDeclined = sortByTierThenName(
                  allPlayers.filter(p => p.status === 'out' && !p.noShow)
                );
                const historicalNoResp   = sortByTierThenName(
                  allPlayers.filter(p => p.status !== 'in' && p.status !== 'out' && !p.noShow)
                );

                const renderPlayerRow = (p, i) => {
                  const att     = attendance[p.userId];
                  const isMe    = p.userId === user?.id;
                  const isGoalie = p.position === 'goalie';
                  const dimmed  = p.status === null ? 0.55 : 1;
                  const isWaitlisted = p.status === 'waitlist';
                  // Apr 2026 — spares promoted by the coach but not yet
                  // paid sit in the waitlist section with a "Pending
                  // Payment" badge. They don't get Check In / Top /
                  // Waitlist buttons — coach would use the promote or
                  // override flow to change their state.
                  const isPendingPayment = p.status === 'pending_payment';
                  // Player's optional comment — shown under the row to the
                  // whole team. Useful for "running 15 late", "can't make
                  // it, work conflict", etc. Capped at 500 chars by the
                  // backend POST handler.
                  const playerNote = (p.notes || '').trim();

                  // ── Meta/action slot builders ──
                  // On phone we stack name over badges+buttons so long
                  // names ("Michael Kyriacou") don't get truncated to 2-3
                  // characters. Building the meta fragments as variables
                  // lets us reuse the same JSX in the desktop single-row
                  // layout and the phone stacked layout without code
                  // duplication. Apr 2026 — coach asked: "names are
                  // getting cut off, buttons are big, put buttons below
                  // the name with more spacing".
                  const goalieBadge = isGoalie && (
                    <span className="badge text-xs flex-shrink-0"
                      style={{ background: 'rgba(0,212,255,0.1)', color: 'var(--ice)', border: '1px solid rgba(0,212,255,0.25)' }}>
                      🥅 G
                    </span>
                  );
                  // 2026-07-28 (mig 109) — the GHOST badges.
                  // ghostedLastGameBadge: this player was flagged as a
                  // no-show on the team's previous night — one game of
                  // public shame, visible to the whole team.
                  // flaggedThisGameBadge: on the flagged (historical)
                  // game itself, so the coach sees who's marked.
                  // 2026-09-22 (per Gilles) — repeat no-show who has checked in
                  // AGAIN. Whole team sees it; that is the point. The coach can
                  // turn it off in Team Settings (no_show_ribbing).
                  const ribbing = game?.teamSettings?.no_show_ribbing !== false;
                  const ghostBadge = ribbing && p.status === 'in' && !p.noShow && (p.priorNoShows || 0) > 0 && (
                    <span className="text-xs font-bold flex-shrink-0 px-2 py-0.5 rounded-full"
                      title={`${p.priorNoShows} previous no-show${p.priorNoShows === 1 ? '' : 's'} on this team`}
                      style={{ color: '#ff6b6b', border: '1px dashed rgba(255,107,107,0.6)', background: 'rgba(255,107,107,0.08)', letterSpacing: '0.04em' }}>
                      👻 {noShowBadgeLabel(p.priorNoShows)}
                    </span>
                  );
                  // The July "one game of shame" badge yields to the cumulative one
                  // above when ribbing is on, so a row never wears two ghosts.
                  const ghostedLastGameBadge = !ghostBadge &&
                    Array.isArray(game?.noShowLastGameUserIds) &&
                    p.userId && game.noShowLastGameUserIds.includes(p.userId) && (
                    <span className="badge text-xs flex-shrink-0"
                      title="Checked in last game and didn't show up"
                      style={{
                        background: 'rgba(167,139,250,0.12)',
                        color: '#c4b5fd',
                        border: '1px dashed rgba(167,139,250,0.5)',
                        fontWeight: 700,
                      }}>
                      👻 Ghosted last game
                    </span>
                  );
                  const flaggedThisGameBadge = p.noShow && (
                    <span className="badge text-xs flex-shrink-0"
                      title="Flagged as a no-show for this game"
                      style={{
                        background: 'rgba(255,68,68,0.10)',
                        color: 'var(--penalty)',
                        border: '1px dashed rgba(255,68,68,0.4)',
                        fontWeight: 700,
                      }}>
                      👻 No-show
                    </span>
                  );
                  // 2026-09-17 (per Gilles) — a checked-in (promoted) spare
                  // used to wear the same purple ⏳ as the waitlist, so the
                  // two were indistinguishable at a glance. Now: orange 🔁
                  // "SPARE · playing", set apart with extra left margin.
                  const spareBadge = !p.isPrimary && p.status === 'in' && (
                    <span className="badge text-xs flex-shrink-0"
                      title="Spare player — checked in and playing this game"
                      style={{
                        background: 'rgba(255,149,0,0.12)',
                        color: 'var(--puck)',
                        border: '1px solid rgba(255,149,0,0.45)',
                        fontWeight: 800,
                        marginLeft: 10,
                        letterSpacing: '0.04em',
                      }}>
                      🔁 SPARE · playing
                    </span>
                  );
                  // Apr 2026 — "Pending Payment" badge for spares
                  // promoted off the waitlist who haven't paid yet.
                  // Amber/orange matches the pending-payment palette
                  // used in the spare's own view.
                  const pendingPaymentBadge = isPendingPayment && (
                    <span className="badge text-xs flex-shrink-0"
                      style={{
                        background: 'rgba(255,149,0,0.12)',
                        color: 'var(--puck)',
                        border: '1px solid rgba(255,149,0,0.35)',
                        fontWeight: 700,
                      }}>
                      💳 Pending Payment
                    </span>
                  );
                  // Apr 2026 — "Paid" badge for spares who have a
                  // completed payment on this game (via Stripe or
                  // credit). Only shown on checked-in spares.
                  // `paidThisGame` comes from the availability row
                  // (backend EXISTS subquery against payments).
                  const paidBadge = !p.isPrimary && p.status === 'in' && p.paidThisGame && (
                    <span className="badge text-xs flex-shrink-0"
                      style={{
                        background: 'rgba(0,232,122,0.12)',
                        color: 'var(--goal)',
                        border: '1px solid rgba(0,232,122,0.35)',
                        fontWeight: 700,
                      }}>
                      💳 Paid
                    </span>
                  );
                  // Apr 2026 — attendance history badge is now gated on
                  // the Miscellaneous → "Show attendance history" toggle
                  // in Team Settings. Default off so rows are cleaner;
                  // coaches who want the reliability signal flip it on.
                  const attBadge = att && game?.teamSettings?.show_attendance_counts && (
                    <span className="text-xs text-secondary flex-shrink-0">
                      {att.gamesIn}✅ {att.gamesOut}❌
                    </span>
                  );
                  const pendingBadge = p.isPending && (
                    <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-2)' }}>not joined</span>
                  );
                  const noRespBadge = p.status === null && !p.isPending && (
                    <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-3)' }}>—</span>
                  );

                  // May 4 2026 — per-row Jab button on Awaiting
                  // Response rows. Coach previously had to open the
                  // confirm-Jab modal to send to a single person —
                  // this surfaces the action right where the player
                  // is. Only shown for: real users (have userId), no
                  // RSVP yet (status === null), coach viewing, and
                  // not the coach themselves. Reuses the existing
                  // nudgeSingle handler so the email send + UI lock
                  // (nudgingSingle map) is identical to the modal's
                  // per-row button.
                  const jabBtn = p.status === null && isCoach && !isMe && p.userId && (
                    <button
                      onClick={(e) => { e.stopPropagation(); nudgeSingle(p.userId, p.name || 'Player'); }}
                      disabled={!!nudgingSingle[p.userId]}
                      title={`Send a Jab reminder to ${p.name || 'this player'}`}
                      className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold"
                      style={{
                        // 2026-09-22 — was a 12%-alpha wash that read as
                        // disabled next to the solid Free Play / Paid cash
                        // buttons. Solid fill so it looks like the action it is.
                        background: '#8b5cf6',
                        border: '1px solid #7c3aed',
                        color: '#fff',
                        cursor: nudgingSingle[p.userId] ? 'wait' : 'pointer',
                        opacity: nudgingSingle[p.userId] ? 0.5 : 1,
                      }}>
                      {nudgingSingle[p.userId]
                        ? <div className="spinner" style={{ width: 9, height: 9 }} />
                        : <><Send size={10} /> Jab</>}
                    </button>
                  );

                  // Coach action buttons (waitlist management). Built as
                  // a flex row with larger gap on mobile so fat-thumb
                  // mis-taps are less likely. The coach keeps all the
                  // same affordances they had before — Check In, Top,
                  // and Waitlist.
                  const checkInBtn = isWaitlisted && isCoach && p.userId && (
                    <button
                      onClick={async () => {
                        try {
                          const { data } = await api.post(`/teams/${teamId}/games/${gameId}/availability/${p.userId}/promote`);
                          // Toast differs based on whether the spare
                          // owes payment now. payOnPromote=true means
                          // backend routed them to pending_payment and
                          // they'll get a Pay Now email. Without this,
                          // the coach saw a generic "promoted" toast
                          // and didn't realize the spare still had a
                          // payment step before being truly confirmed.
                          if (data?.payOnPromote) {
                            toast.success(`${p.name} promoted — waiting on payment 💳`);
                          } else {
                            toast.success(`${p.name} promoted from waitlist — they'll be notified ✅`);
                          }
                          refresh();
                        } catch (err) {
                          // v2.5.166 — use friendlyError so server-side
                          // copy (e.g. the stripe_onboarding_incomplete
                          // message guiding the coach to finish Stripe
                          // setup or use Free Play) is shown instead of
                          // the raw error code.
                          toast.error(friendlyError(err, 'Failed to promote player'));
                        }
                      }}
                      className="text-xs font-bold px-3 py-1.5 rounded-lg flex-shrink-0"
                      style={{ background: 'rgba(0,255,136,0.12)', border: '1px solid rgba(0,255,136,0.3)', color: 'var(--goal)', minHeight: 32 }}>
                      Check In
                    </button>
                  );
                  // Apr 2026 — "Free Play" button: visible on waitlist
                  // AND pending_payment rows.
                  //   • Waitlist row: clicking promotes to 'in' AND
                  //     waives payment (single round-trip via the
                  //     promote endpoint with freePlay flag).
                  //   • Pending-payment row: clicking flips to 'in'
                  //     without payment via the dedicated /free-play
                  //     endpoint. The player was already checked in
                  //     (e.g. coach used regular Check In, or the
                  //     spare self-checked-in) — coach is now waiving
                  //     the fee they would have owed.
                  // Visible only when:
                  //   • the team has spare-pay-required ON (otherwise
                  //     no payment to waive — Check In already
                  //     produces 'in' for free), AND
                  //   • the row is a spare (FT players never pay), AND
                  //   • for waitlist rows specifically: the row isn't
                  //     a goalie on a goalies-free team (they auto-
                  //     promote free via Check In already, so showing
                  //     a redundant button just adds noise).
                  // For pending_payment rows we ALWAYS show the
                  // button — that handles the edge case where a
                  // goalie row exists in pending_payment from before
                  // the goalies-free feature was enabled, or from a
                  // toggle change that left old rows stranded. The
                  // coach needs SOME way to flip them to 'in'.
                  const teamRequiresPay = game?.teamSettings?.payment_enabled === true
                                       && game?.teamSettings?.spare_pay_required === true
                                       && Number(game?.teamSettings?.spare_fee_cents) > 0;
                  const goaliesFree = game?.teamSettings?.spare_goalies_free !== false;
                  const rowIsSpareGoalie = !p.isPrimary
                                        && (p.playingAs === 'goalie' || p.position === 'goalie');
                  // Hide on waitlist when the goalies-free shortcut
                  // makes the button redundant. Always show on
                  // pending_payment.
                  const hideAsRedundant = isWaitlisted && goaliesFree && rowIsSpareGoalie;
                  const showFreePlay = (isWaitlisted || isPendingPayment) && isCoach && p.userId
                                    && teamRequiresPay
                                    && !p.isPrimary
                                    && !hideAsRedundant;
                  const freePlayBtn = showFreePlay && (
                    <button
                      onClick={async () => {
                        try {
                          if (isPendingPayment) {
                            // Already pending_payment — use the dedicated
                            // free-play endpoint that flips status without
                            // re-running the promote logic.
                            await api.post(
                              `/teams/${teamId}/games/${gameId}/availability/${p.userId}/free-play`
                            );
                            toast.success(`${p.name} confirmed — free play 🎁`);
                          } else {
                            // Waitlist — promote with freePlay so the
                            // player goes straight to 'in' without
                            // touching pending_payment.
                            await api.post(
                              `/teams/${teamId}/games/${gameId}/availability/${p.userId}/promote`,
                              { freePlay: true }
                            );
                            toast.success(`${p.name} confirmed — free play 🎁`);
                          }
                          refresh();
                        } catch (err) {
                          toast.error(friendlyError(err, 'Failed to grant free play'));
                        }
                      }}
                      title="Confirm this player without requiring payment for this game. Use for guest goalies, comp games, or any time you want to waive the spare fee."
                      className="text-xs font-bold px-3 py-1.5 rounded-lg flex-shrink-0"
                      style={{
                        background: 'rgba(255,149,0,0.10)',
                        border: '1px solid rgba(255,149,0,0.35)',
                        color: 'var(--puck)',
                        minHeight: 32,
                      }}>
                      🎁 Free Play
                    </button>
                  );
                  // 2026-09-17 (per Gilles, Phase 6) — "💵 Paid cash": the spare
                  // handed over cash at the rink. Same visibility as Free Play.
                  // Waitlist → promote with paidCash (backend records the
                  // paid/cash row); pending_payment → free-play flip, then the
                  // Finance cash-payment upsert (default amount = their fee).
                  const paidCashBtn = showFreePlay && (
                    <button
                      onClick={async () => {
                        try {
                          if (isPendingPayment) {
                            await api.post(`/teams/${teamId}/games/${gameId}/availability/${p.userId}/free-play`);
                            await api.post(`/payments/team/${teamId}/finance/cash-payment`, { userId: p.userId, gameId, note: 'Paid cash at the rink' });
                          } else {
                            await api.post(
                              `/teams/${teamId}/games/${gameId}/availability/${p.userId}/promote`,
                              { paidCash: true }
                            );
                          }
                          toast.success(`${p.name} confirmed — paid cash 💵 (recorded in Finance)`);
                          refresh();
                        } catch (err) {
                          toast.error(friendlyError(err, 'Failed to record cash payment'));
                        }
                      }}
                      title="Confirm this spare as paid in cash. Skips the online payment and records the cash in Finance (different from Free Play, which waives the fee)."
                      className="text-xs font-bold px-3 py-1.5 rounded-lg flex-shrink-0"
                      style={{
                        background: 'rgba(0,232,122,0.10)',
                        border: '1px solid rgba(0,232,122,0.35)',
                        color: 'var(--goal)',
                        minHeight: 32,
                      }}>
                      💵 Paid cash
                    </button>
                  );
                  // v2.5.167 — "Send Pay Link" on pending_payment rows.
                  // Re-sends the Pay Now email (+ in-app nudge) to a spare
                  // who's been sitting unpaid — e.g. promoted weeks ago and
                  // never paid. Only shown when the team actually collects
                  // spare payment in-app (otherwise there's no link to send;
                  // Free Play is the right tool). Doesn't change status.
                  const teamCollectsInApp = game?.teamSettings?.payment_enabled === true
                                         && game?.teamSettings?.spare_pay_required === true
                                         && Number(game?.teamSettings?.spare_fee_cents) > 0;
                  const resendPayBtn = isPendingPayment && isCoach && p.userId && teamCollectsInApp && (
                    <button
                      onClick={async (e) => {
                        const btn = e.currentTarget;
                        btn.disabled = true;
                        try {
                          const { data } = await api.post(
                            `/teams/${teamId}/games/${gameId}/availability/${p.userId}/resend-payment-link`
                          );
                          const amt = data?.amountCents
                            ? ` ($${(data.amountCents / 100).toFixed(2)} ${String(data.paymentCurrency || 'CAD').toUpperCase()})`
                            : '';
                          toast.success(`Payment link sent to ${p.name}${amt} 💳`);
                        } catch (err) {
                          toast.error(friendlyError(err, 'Failed to send payment link'));
                        } finally {
                          if (btn) btn.disabled = false;
                        }
                      }}
                      title="Email this spare a fresh Pay Now link for this game (and an in-app reminder). Use when they were checked in but never paid."
                      className="text-xs font-bold px-3 py-1.5 rounded-lg flex-shrink-0"
                      style={{
                        background: 'rgba(0,212,255,0.10)',
                        border: '1px solid rgba(0,212,255,0.35)',
                        color: 'var(--ice, #00d4ff)',
                        minHeight: 32,
                      }}>
                      💳 Send Pay Link
                    </button>
                  );
                  const topBtn = isWaitlisted && isCoach && p.userId && (
                    <button
                      onClick={async () => {
                        // May 1 2026 — optimistic update. Previously this
                        // awaited the POST then called refresh() which
                        // refetches the full game detail (~25 players,
                        // availability, scoreboard, season data). Total
                        // perceived latency was 1-2s; the row didn't move
                        // until everything came back. Now: update the
                        // local availability array immediately so the
                        // bumped player jumps to the top in <50ms, then
                        // confirm with the server in the background.
                        // On server failure, revert by refetching.
                        //
                        // The sort key is COALESCE(waitlistPriorityAt,
                        // respondedAt). Setting waitlistPriorityAt on the
                        // bumped player to a value < every other waitlist
                        // row's effective key wins immediately. Use one
                        // second before the current minimum, matching the
                        // backend's exact algorithm — that way if a quick
                        // second bump happens before refresh resolves,
                        // the second target lands above the first.
                        const prevAvail = game?.availability || [];
                        const waitlistRows = prevAvail.filter(a => a.status === 'waitlist');
                        let earliest = null;
                        for (const row of waitlistRows) {
                          const key = row.waitlistPriorityAt || row.respondedAt;
                          if (!key) continue;
                          if (!earliest || new Date(key) < new Date(earliest)) earliest = key;
                        }
                        // Compute an "earlier than earliest" timestamp.
                        // If no other waitlist row had a key (shouldn't
                        // happen — every RSVP sets respondedAt — but
                        // defensive), use 'now'.
                        const optimisticTs = earliest
                          ? new Date(new Date(earliest).getTime() - 1000).toISOString()
                          : new Date().toISOString();
                        // Apply optimistic update via setGame. We mutate
                        // the matching availability entry's waitlistPriorityAt
                        // and leave everything else untouched.
                        setGame(prev => {
                          if (!prev) return prev;
                          return {
                            ...prev,
                            availability: (prev.availability || []).map(a => (
                              a.userId === p.userId
                                ? { ...a, waitlistPriorityAt: optimisticTs }
                                : a
                            )),
                          };
                        });
                        try {
                          const { data } = await api.post(`/teams/${teamId}/games/${gameId}/availability/${p.userId}/promote-top`);
                          if (data?.noop) {
                            toast(`${p.name} is already at the top of the waitlist`);
                          } else {
                            toast.success(`${p.name} moved to the top of the waitlist ⬆`);
                          }
                          // Background refresh — confirms the optimistic
                          // state and pulls in any other server-side
                          // changes (e.g. another coach promoted a
                          // different player simultaneously). No await:
                          // the user already sees the bumped player at
                          // the top, refresh just brings everything else
                          // current.
                          refresh();
                        } catch (err) {
                          // Revert by refetching authoritative state.
                          // Toast tells the coach what went wrong; the
                          // visual jump-back is the rollback signal.
                          toast.error(err.response?.data?.error || 'Failed to reorder waitlist');
                          refresh();
                        }
                      }}
                      title="Move this player to the top of the waitlist (next in line for an open spot)"
                      className="text-xs font-bold px-3 py-1.5 rounded-lg flex-shrink-0"
                      style={{ background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.3)', color: '#a78bfa', minHeight: 32 }}>
                      ⬆ Top
                    </button>
                  );
                  // 2026-09-17 (per Gilles) — the purple "⏳ Waitlist" button on a
                  // checked-in spare's row is gone (the badge already says they're a
                  // spare; moving one back to the waitlist is still available from
                  // the row's ⋯ menu / override). Kept as dead code per house rule.
                  const waitlistBtn = false && isCoach && p.userId && !p.isPrimary && p.status === 'in' && (
                    <button
                      onClick={() => applySingleStatus(p, 'waitlist')}
                      title="Move this spare back to the waitlist"
                      className="text-xs font-bold px-3 py-1.5 rounded-lg flex-shrink-0"
                      style={{ background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.3)', color: '#a78bfa', minHeight: 32 }}>
                      ⏳ Waitlist
                    </button>
                  );
                  // Apr 2026 — "Assign to Goalie" / "Back to Skater" button.
                  // Only rendered for hybrid players: those who have BOTH
                  // 'goalie' AND a skater position ('forward' or 'defense'
                  // or 'flex') in their `positions` array. A pure goalie
                  // doesn't get this (they're already a goalie), and a
                  // pure skater doesn't get this (they can't tend net).
                  // Coach-only, in-only — flipping a 'pending' or 'out'
                  // row is meaningless (backend enforces this too).
                  //
                  // The /playing-as endpoint accepts the position and
                  // updates game_availability.playing_as without touching
                  // the player's team-wide team_members.position. After
                  // the call we refresh so the row re-renders in the
                  // correct goalie/skater slot.
                  const playerPositions = Array.isArray(p.positions) ? p.positions : [];
                  const canPlayGoalie   = playerPositions.includes('goalie');
                  const canPlaySkater   = playerPositions.includes('forward')
                                       || playerPositions.includes('defense')
                                       || playerPositions.includes('flex');
                  const isHybrid = canPlayGoalie && canPlaySkater;
                  const playingAs = p.playingAs || null;
                  // Effective position for THIS game = override or default.
                  const effectivePos = playingAs || p.position;
                  const goalieToggleBtn = isCoach && p.userId
                    && p.status === 'in'
                    && isHybrid
                    && (
                      <button
                        onClick={async () => {
                          // Toggle: if already playing as goalie this
                          // game (either via override OR default
                          // goalie position), flip them BACK to their
                          // first listed skater position. Otherwise
                          // flip them TO goalie.
                          const flipToGoalie = effectivePos !== 'goalie';
                          const newPos = flipToGoalie
                            ? 'goalie'
                            : (playerPositions.find(x => x === 'forward')
                              || playerPositions.find(x => x === 'defense')
                              || playerPositions.find(x => x === 'flex')
                              || null);
                          // If we're flipping back to skater AND the
                          // player's default team position is skater,
                          // pass null to clear the override. If their
                          // default IS goalie, set the explicit
                          // skater override.
                          const payload = (!flipToGoalie && p.position !== 'goalie')
                            ? null
                            : newPos;
                          try {
                            await api.post(
                              `/teams/${teamId}/games/${gameId}/availability/${p.userId}/playing-as`,
                              { playingAs: payload }
                            );
                            toast.success(flipToGoalie
                              ? `${p.name} will play as goalie 🥅`
                              : `${p.name} back to skater`);
                            refresh();
                          } catch (err) {
                            toast.error(err.response?.data?.error || 'Failed to change position');
                          }
                        }}
                        title={effectivePos === 'goalie'
                          ? 'Move this player back to skater for this game'
                          : 'Assign this player as goalie for this game (frees a skater spot for someone else)'}
                        className="text-xs font-bold px-3 py-1.5 rounded-lg flex-shrink-0"
                        style={{
                          background: effectivePos === 'goalie' ? 'rgba(0,212,255,0.18)' : 'rgba(0,212,255,0.10)',
                          border: '1px solid rgba(0,212,255,0.30)',
                          color: 'var(--ice)', minHeight: 32,
                        }}>
                        {effectivePos === 'goalie' ? '↩ Back to Skater' : '🥅 Assign to Goalie'}
                      </button>
                  );
                  // Apr 2026 — playingAs badge: "G this game" subtitle
                  // shown when the player has an override active. Helps
                  // the coach see at a glance "Bob's normally a forward
                  // but he's in goal for this game". Distinct from the
                  // goalieBadge which fires on team_members.position;
                  // playingAs takes precedence visually when set.
                  const playingAsBadge = playingAs && (
                    <span className="badge text-xs flex-shrink-0"
                      title={`Playing as ${playingAs} for this game (default: ${p.position || 'skater'})`}
                      style={{
                        background: 'rgba(0,212,255,0.10)',
                        color: 'var(--ice)',
                        border: '1px solid rgba(0,212,255,0.30)',
                        fontWeight: 700,
                      }}>
                      {playingAs === 'goalie' ? '🥅 G this game' : `🔄 ${playingAs} this game`}
                    </span>
                  );
                  const kebabBtn = isCoach && !overrideMode && (p.userId || p.inviteId) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const rowKey = p.userId || `invite:${p.inviteId}`;
                        if (playerMenuFor?.key === rowKey) {
                          setPlayerMenuFor(null);
                        } else {
                          const r = e.currentTarget.getBoundingClientRect();
                          setPlayerMenuFor({
                            key: rowKey,
                            rect: { top: r.top, bottom: r.bottom, right: r.right, left: r.left },
                            player: p,
                          });
                        }
                      }}
                      title="Player actions"
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 32, height: 32, borderRadius: 6,
                        background: 'transparent', border: 'none',
                        color: 'var(--text-secondary)', cursor: 'pointer',
                        marginLeft: 2, flexShrink: 0,
                      }}>
                      <MoreHorizontal size={18} />
                    </button>
                  );

                  // Secondary line contents — shown only when there's at
                  // least one badge or button to render. Building as a
                  // computed array lets us skip the row entirely on clean
                  // rows (FT player, no stats, no actions) so we don't
                  // eat vertical space for no reason.
                  const secondaryChildren = [
                    goalieBadge, playingAsBadge, spareBadge, ghostBadge, paidBadge, pendingPaymentBadge, attBadge, pendingBadge, noRespBadge,
                    checkInBtn, freePlayBtn, paidCashBtn, resendPayBtn, topBtn, waitlistBtn, goalieToggleBtn, jabBtn,
                  ].filter(Boolean);
                  const hasSecondary = secondaryChildren.length > 0;

                  return (
                    <div key={p.userId || p.inviteId || `row-${i}`}
                      style={{
                        background: isMe
                          ? 'var(--ice-a5)'
                          : isWaitlisted
                            ? 'rgba(167,139,250,0.04)'
                            : 'var(--surface-2)',
                        border: isMe
                          ? '1px solid var(--ice-a15)'
                          : isWaitlisted
                            ? '1px solid rgba(167,139,250,0.15)'
                            : '1px solid transparent',
                        borderRadius: 8,
                        opacity: dimmed,
                      }}>
                      {isPhone ? (
                        // ── Phone layout: stacked ──
                        // Row 1: avatar + full-width name + kebab
                        // Row 2: (when needed) badges + buttons with wider
                        //        gaps so fat-thumb hits don't bleed into
                        //        an adjacent button.
                        <div style={{ padding: '8px 12px' }}>
                          <div className="flex items-center gap-2.5">
                            {/* Avatar with status badge overlay */}
                            <div className="flex-shrink-0 relative">
                              <Avatar avatarUrl={p.avatarUrl || null} name={p.name} size={32} />
                              {p.icon && (
                                <span style={{
                                  position: 'absolute', bottom: -2, right: -2,
                                  fontSize: 11, lineHeight: 1,
                                  background: 'var(--surface-2)', borderRadius: '50%', padding: 1,
                                }}>
                                  {p.icon}
                                </span>
                              )}
                            </div>
                            {/* Name — no truncation on mobile; allow wrap
                                so long names display in full. Line-height
                                is tight so a two-line name doesn't blow
                                up the row height. */}
                            <span
                              className="flex-1 text-sm font-medium"
                              style={{
                                color: isWaitlisted ? '#c4b5fd' : undefined,
                                lineHeight: 1.25,
                                wordBreak: 'break-word',
                                minWidth: 0,
                              }}>
                              {p.name || 'Player'}{isMe ? ' (you)' : ''}
                            </span>
                            {kebabBtn}
                          </div>
                          {hasSecondary && (
                            <div
                              className="flex items-center"
                              style={{
                                flexWrap: 'wrap',
                                gap: 10, // wider gap so thumbs don't miss-hit
                                marginTop: 8,
                                paddingLeft: 42, // align with name column (avatar 32 + gap 10)
                              }}>
                              {secondaryChildren.map((node, idx) => (
                                <React.Fragment key={idx}>{node}</React.Fragment>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        // ── Desktop layout: single row (unchanged) ──
                        <div className="flex items-center gap-2.5 px-3 py-2">
                          <div className="flex-shrink-0 relative">
                            <Avatar avatarUrl={p.avatarUrl || null} name={p.name} size={28} />
                            {p.icon && (
                              <span style={{
                                position: 'absolute', bottom: -2, right: -2,
                                fontSize: 10, lineHeight: 1,
                                background: 'var(--surface-2)', borderRadius: '50%', padding: 1,
                              }}>
                                {p.icon}
                              </span>
                            )}
                          </div>
                          <span className="flex-1 text-sm font-medium truncate"
                            style={{ color: isWaitlisted ? '#c4b5fd' : undefined }}>
                            {p.name || 'Player'}{isMe ? ' (you)' : ''}
                          </span>
                          {goalieBadge}
                          {playingAsBadge}
                          {flaggedThisGameBadge}
                          {ghostedLastGameBadge}
                          {spareBadge}
                          {paidBadge}
                          {pendingPaymentBadge}
                          {attBadge}
                          {pendingBadge}
                          {noRespBadge}
                          {checkInBtn}
                          {freePlayBtn}
                          {paidCashBtn}
                          {resendPayBtn}
                          {topBtn}
                          {waitlistBtn}
                          {goalieToggleBtn}
                          {jabBtn}
                          {kebabBtn}
                        </div>
                      )}
                      {/* Optional player note — visible to the whole
                          team. Coloured slightly to match the status
                          (red-ish for outs, neutral otherwise). Quoted
                          so it reads like a remark rather than a label.
                          The note rule line is only rendered when there
                          IS a note, so the row stays compact for the
                          common case. ── */}
                      {playerNote && (
                        <div style={{
                          padding: '4px 12px 8px',
                          fontSize: 12,
                          fontStyle: 'italic',
                          color: p.status === 'out' ? 'rgba(255,150,150,0.85)' : 'var(--text-2)',
                          lineHeight: 1.35,
                          borderTop: '1px dashed rgba(255,255,255,0.06)',
                          marginTop: 2,
                        }}>
                          <span style={{ opacity: 0.6, marginRight: 4 }}>💬</span>
                          “{playerNote}”
                        </div>
                      )}
                      {/* 2026-09-22 — the bot has opinions about repeat
                          no-shows who check in again. Same slot as the
                          player note so it reads as a remark on the row. */}
                      {ghostBadge && (
                        <div style={{
                          padding: '4px 12px 8px',
                          fontSize: 12,
                          fontStyle: 'italic',
                          color: 'rgba(255,150,150,0.9)',
                          lineHeight: 1.35,
                          borderTop: '1px dashed rgba(255,255,255,0.06)',
                          marginTop: 2,
                        }}>
                          <span style={{ opacity: 0.8, marginRight: 4 }}>🤖</span>
                          {noShowQuip(p.priorNoShows, `${p.userId || p.inviteId}:${gameId}`)}
                        </div>
                      )}
                    </div>
                  );
                };

                // sectionLabel's `id` prop lets the header counts (above)
                // scroll-anchor directly to the matching section. Slug-format
                // ids ("availability-section-<slug>") keep them unambiguous
                // across the page.
                const sectionLabel = (emoji, label, count, color = 'var(--text-2)', id = null) => (
                  <div id={id || undefined}
                    className="pt-2 pb-0.5 flex items-center gap-1.5"
                    style={id ? { scrollMarginTop: 80 } : undefined}>
                    <span style={{ fontSize: 13 }}>{emoji}</span>
                    <span className="text-xs font-bold"
                      style={{ color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      {label}
                    </span>
                    <span className="text-xs font-semibold"
                      style={{ color: 'var(--text-3)', marginLeft: 2 }}>
                      ({count})
                    </span>
                  </div>
                );

                return (
                  <>
                    {/* Availability-centric layout — shows who's in, who's
                        waiting, and which FT players haven't responded yet.
                        Historical (completed) games switch to a final-
                        attendance layout instead. */}
                    {isHistorical ? (
                      <>
                        {historicalAttended.length > 0 && (
                          <>
                            {sectionLabel('✅', 'Attended', historicalAttended.length, 'var(--goal)', 'availability-section-attended')}
                            {historicalAttended.map((p, i) => renderPlayerRow(p, i))}
                          </>
                        )}
                        {/* 2026-08-20 — flagged no-shows, own section */}
                        {noShowsAll.length > 0 && (
                          <>
                            {sectionLabel('👻', 'No Shows', noShowsAll.length, 'var(--penalty)', 'availability-section-noshow')}
                            {noShowsAll.map((p, i) => renderPlayerRow(p, i))}
                          </>
                        )}
                        {historicalDeclined.length > 0 && (
                          <>
                            {sectionLabel('❌', 'Checked Out', historicalDeclined.length, 'var(--penalty)', 'availability-section-out')}
                            {historicalDeclined.map((p, i) => renderPlayerRow(p, i))}
                          </>
                        )}
                        {historicalNoResp.length > 0 && (
                          <>
                            {sectionLabel('🤷', 'No Response', historicalNoResp.length, 'var(--text-3)', 'availability-section-noresp')}
                            {historicalNoResp.map((p, i) => renderPlayerRow(p, i))}
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {checkedInAll.length > 0 && (
                          <>
                            {sectionLabel('✅', 'Checked In', checkedInAll.length, 'var(--goal)', 'availability-section-in')}
                            {checkedInAll.map((p, i) => renderPlayerRow(p, i))}
                          </>
                        )}
                        {/* 2026-08-20 — flagged no-shows stay VISIBLE in
                            their own section right under Checked In (they
                            used to fall into the collapsed Declined tail
                            and "disappear"). The row's ⋯ menu offers
                            Clear No-Show → late-arrival flow if the
                            player shows up after all. */}
                        {noShowsAll.length > 0 && (
                          <>
                            {sectionLabel('👻', 'No Shows', noShowsAll.length, 'var(--penalty)', 'availability-section-noshow')}
                            {noShowsAll.map((p, i) => renderPlayerRow(p, i))}
                          </>
                        )}
                        {/* Apr 2026 — split Pending Payment out of
                            Waitlist. Coach needs to see at a glance
                            who owes money after a promote. Both
                            buckets share the waitlist anchor id for
                            summary-pill jump-to, but render as
                            distinct sections with different labels. */}
                        {(() => {
                          const pendingPaymentAll = waitlistedAll.filter(p => p.status === 'pending_payment');
                          const pureWaitlist       = waitlistedAll.filter(p => p.status === 'waitlist');
                          return (
                            <>
                              {pendingPaymentAll.length > 0 && (
                                <>
                                  {sectionLabel('💳', 'Pending Payment', pendingPaymentAll.length, 'var(--puck)', 'availability-section-waitlist')}
                                  {pendingPaymentAll.map((p, i) => renderPlayerRow(p, i))}
                                </>
                              )}
                              {pureWaitlist.length > 0 && (
                                <>
                                  {sectionLabel('⏳', 'Waitlist', pureWaitlist.length, '#a78bfa',
                                                pendingPaymentAll.length > 0 ? 'availability-section-waitlist-pure' : 'availability-section-waitlist')}
                                  {pureWaitlist.map((p, i) => renderPlayerRow(p, i))}
                                </>
                              )}
                            </>
                          );
                        })()}
                        {awaitingAll.length > 0 && (
                          <>
                            {sectionLabel('🤔', 'Awaiting Response', awaitingAll.length, 'var(--text-2)', 'availability-section-awaiting')}
                            {/* May 4 2026 — sub-sections under the main
                                Awaiting Response header. Each subgroup
                                gets a smaller indented label so the
                                coach can scan "is it FT or spares
                                holding things up?" without counting.
                                Suppressed when empty. ── */}
                            {awaitingFTPlayers.length > 0 && (
                              <>
                                <div style={{
                                  fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase',
                                  letterSpacing: '0.06em', fontWeight: 700,
                                  padding: '6px 10px 2px 18px',
                                }}>
                                  👤 Full-time players ({awaitingFTPlayers.length})
                                </div>
                                {awaitingFTPlayers.map((p, i) => renderPlayerRow(p, i))}
                              </>
                            )}
                            {awaitingFTGoalies.length > 0 && (
                              <>
                                <div style={{
                                  fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase',
                                  letterSpacing: '0.06em', fontWeight: 700,
                                  padding: '6px 10px 2px 18px',
                                }}>
                                  🥅 Full-time goalies ({awaitingFTGoalies.length})
                                </div>
                                {awaitingFTGoalies.map((p, i) => renderPlayerRow(p, i))}
                              </>
                            )}
                            {awaitingSparePlayers.length > 0 && (
                              <>
                                <div style={{
                                  fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase',
                                  letterSpacing: '0.06em', fontWeight: 700,
                                  padding: '6px 10px 2px 18px',
                                }}>
                                  ⏳ Spare players ({awaitingSparePlayers.length})
                                </div>
                                {awaitingSparePlayers.map((p, i) => renderPlayerRow(p, i))}
                              </>
                            )}
                            {awaitingSpareGoalies.length > 0 && (
                              <>
                                <div style={{
                                  fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase',
                                  letterSpacing: '0.06em', fontWeight: 700,
                                  padding: '6px 10px 2px 18px',
                                }}>
                                  ⏳🥅 Spare goalies ({awaitingSpareGoalies.length})
                                </div>
                                {awaitingSpareGoalies.map((p, i) => renderPlayerRow(p, i))}
                              </>
                            )}
                          </>
                        )}
                        {declinedAll.length > 0 && (
                          <>
                            {sectionLabel('❌', 'Declined', declinedAll.length, 'var(--penalty)', 'availability-section-out')}
                            {declinedAll.map((p, i) => renderPlayerRow(p, i))}
                          </>
                        )}
                      </>
                    )}

                    {/* Pending invites who aren't on the main member list */}
                    {pendingMembers
                      .filter(m => !allPlayers.some(p => p.inviteId === m.inviteId || (m.userId && p.userId === m.userId)))
                      .map((m, i) => renderPlayerRow({ ...m, status: null, icon: null }, i))}
                  </>
                );
              })()}
            </div>
          )
        )}
        </>)}
      </div>

      {/* 2026-07-10 (batch-B D1) — the second Drinks Duty section that
          rendered here was consolidated into the single DrinksPanel
          near the top of the page (below Upcoming Reminders), together
          with the #drinks-duty anchor. */}

      {/* ── Balanced teams ──
          May 4 2026 (v2.5.69) — additional gate: when the team has
          turned off "Use the app to create teams" entirely, hide
          this card no matter what. Pre-feature legacy data may
          still have rosters lingering in game_rosters; we don't
          want to surface them once the coach has explicitly opted
          out of the feature. The gate is `=== false` (not the
          looser `!== true`) so a missing key behaves like the
          legacy default of true (existing teams keep their card). ── */}
      {/* v2.5.149 — card now renders whenever the balancing feature
          is ON, even before teams exist. Coach feedback: "I'm not
          seeing the Teams section where I can make a team... I
          shouldn't have to wait till the teams are auto-generated
          before I see it." When `teams` is null we show an empty-
          state with the Create/Balance affordance (moved here from
          Coach Actions). When `teams` exists, the normal balanced-
          teams UI renders below. */}
      {game?.teamSettings?.use_app_for_balancing !== false && !teams && isCoach && (
        <div className="card mb-4">
          <h2 className="section-title text-lg" style={{ margin: '0 0 12px' }}>⚖️ Teams</h2>
          {(() => {
            const mode = game?.teamSettings?.team_creation_mode || 'auto';
            const tooFew = inPlayers.length < 2;
            return (
              <>
                <p className="text-sm text-secondary mb-3">
                  {tooFew
                    ? 'Teams will appear here. You need at least 2 checked-in players to create teams.'
                    : mode === 'manual'
                      ? 'Assign players to two sides manually, or let the balancer do it.'
                      : 'Create balanced teams from your checked-in players. You can re-balance or tweak afterward.'}
                </p>
                <div className="flex flex-col gap-2">
                  {mode === 'auto' && (
                    <button onClick={balanceTeams} disabled={balancing || tooFew}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        width: '100%', padding: '14px 20px',
                        background: 'var(--ice)', color: 'var(--btn-accent-text)',
                        fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14,
                        letterSpacing: '0.06em', textTransform: 'uppercase',
                        borderRadius: 12, border: 'none',
                        cursor: tooFew ? 'not-allowed' : 'pointer',
                        opacity: tooFew ? 0.45 : 1, minHeight: 50,
                        WebkitTapHighlightColor: 'transparent',
                      }}>
                      {balancing
                        ? <><div className="spinner" style={{ width: 16, height: 16 }} /> Balancing…</>
                        : <><Shuffle size={16} /> Balance Teams</>}
                    </button>
                  )}
                  <button onClick={() => setShowBulkAssign(true)} disabled={balancing || tooFew}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      width: '100%', padding: mode === 'manual' ? '14px 20px' : '12px 18px',
                      background: mode === 'manual' ? '#a78bfa' : 'var(--surface-2)',
                      color: mode === 'manual' ? '#fff' : 'var(--text)',
                      fontFamily: 'var(--font-display)', fontWeight: 700,
                      fontSize: mode === 'manual' ? 14 : 12,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      borderRadius: 12,
                      border: mode === 'manual' ? 'none' : '1px solid var(--border)',
                      cursor: tooFew ? 'not-allowed' : 'pointer',
                      opacity: tooFew ? 0.45 : 1, minHeight: mode === 'manual' ? 50 : 44,
                      WebkitTapHighlightColor: 'transparent',
                    }}>
                    ✋ {mode === 'manual' ? 'Assign Players' : 'Assign Manually Instead'}
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {game?.teamSettings?.use_app_for_balancing !== false && teams && (
        <div className="card mb-4">
          {/* Apr 2026 (Tom_Notes B6) — header now contains the
              Re-Balance Teams button. Coach asked to move it next to
              the team names since that's where they evaluate the
              outcome and decide to redo. Old location was at the
              top of Coach Actions (above the action grid), which
              meant scrolling up after reviewing teams.

              Apr 2026 (Tom_Notes B16) — also added an "Include
              goalies" toggle. Some coaches want goalies factored
              into the balance calculation; others run goalies
              entirely outside the rosters. Toggle persists in
              local component state for the session — coach decides
              before tapping Re-Balance. */}
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <h2 className="section-title text-lg" style={{ margin: 0 }}>⚖️ Balanced Teams</h2>
            {isCoach && (
              <div className="flex items-center gap-2 flex-wrap" style={{ marginLeft: 'auto' }}>
                {/* Apr 30 2026 — Clear & Restart Teams moved here from
                    Coach Actions. Coach feedback: "the button to clear
                    teams should live with the teams themselves, not
                    five sections away in Coach Actions." Subdued
                    ghost styling so it doesn't compete visually with
                    Re-Balance (the primary action).
                    May 2 2026 — disabled when teams are locked. */}
                {teams && (
                  <button onClick={clearRoster} disabled={balancing || teamsLocked}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg"
                    style={{
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      color: 'var(--text-2)',
                      fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 11,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      cursor: (balancing || teamsLocked) ? 'not-allowed' : 'pointer',
                      opacity: (balancing || teamsLocked) ? 0.45 : 1,
                      transition: 'all 0.15s',
                    }}
                    title={teamsLocked
                      ? 'Teams are locked — unlock to clear'
                      : 'Wipe team assignments to start over (auto or manual).'}>
                    🧹 Clear &amp; Restart
                  </button>
                )}
                {/* Goalie include/exclude toggle.
                    May 2 2026 — disabled when locked.
                    May 4 2026 — toggle now persists per-game (mig 067).
                    Was state-only, which got stomped on every refresh
                    when the team default re-arrived from the server.
                    Now: optimistic local update, fire PUT /games to
                    save the override, refetch on success. Toast on
                    failure rolls back the local state. */}
                <button
                  type="button"
                  onClick={async () => {
                    const next = !balanceIncludeGoalies;
                    setBalanceIncludeGoalies(next); // optimistic
                    try {
                      await api.put(`/teams/${teamId}/games/${gameId}`, {
                        balanceIncludeGoalies: next,
                      });
                      // Refresh so the server-confirmed value flows
                      // through and the auto-balance cron picks up the
                      // change next time it considers this game.
                      refresh();
                    } catch (err) {
                      setBalanceIncludeGoalies(!next); // rollback
                      toast.error(err.response?.data?.error || 'Failed to save goalies setting');
                    }
                  }}
                  disabled={teamsLocked}
                  title={teamsLocked
                    ? 'Teams are locked — unlock to change goalies setting'
                    : balanceIncludeGoalies
                      ? 'Goalies are included in team balancing — toggle to exclude them'
                      : 'Goalies are NOT included in team balancing — toggle to include them'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '6px 10px', borderRadius: 8,
                    background: balanceIncludeGoalies ? 'rgba(0,212,255,0.10)' : 'var(--surface-2)',
                    border: `1px solid ${balanceIncludeGoalies ? 'var(--ice-a30)' : 'var(--border)'}`,
                    color: balanceIncludeGoalies ? 'var(--ice)' : 'var(--text-2)',
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                    cursor: teamsLocked ? 'not-allowed' : 'pointer',
                    opacity: teamsLocked ? 0.45 : 1,
                    transition: 'all 0.15s',
                    fontFamily: 'var(--font-display)',
                  }}>
                  <span style={{ fontSize: 12 }}>🥅</span>
                  Goalies {balanceIncludeGoalies ? 'IN' : 'OUT'}
                </button>
                {/* Re-Balance Teams button.
                    May 2 2026 — disabled when locked. */}
                <button
                  type="button"
                  onClick={() => balanceTeams({ includeGoalies: balanceIncludeGoalies })}
                  disabled={balancing || inPlayers.length < 2 || teamsLocked}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg"
                  style={{
                    background: teamsLocked
                      ? 'var(--surface-2)'
                      : (rosterChanged ? 'var(--puck)' : 'var(--ice)'),
                    color: teamsLocked ? 'var(--text-3)' : 'var(--btn-accent-text)',
                    fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 11,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    border: teamsLocked ? '1px solid var(--border)' : 'none',
                    cursor: (inPlayers.length < 2 || teamsLocked) ? 'not-allowed' : 'pointer',
                    opacity: (inPlayers.length < 2 || teamsLocked) ? 0.45 : 1,
                  }}
                  title={teamsLocked
                    ? 'Teams are locked — unlock to re-balance'
                    : undefined}>
                  {balancing
                    ? <><div className="spinner" style={{ width: 12, height: 12 }} /> …</>
                    : <><Shuffle size={12} /> Re-Balance</>}
                </button>
                {/* v2.5.102 — Manual "Send Teams" button. Coach
                    feedback after a game where the auto-balance cron
                    failed to fire 2h before: "Create a button that
                    manually sends out the teams (emails everyone).
                    Put it on the game page before the Lock Teams
                    button." Reads existing game_rosters and emails
                    each player their assignment. Safe on locked
                    games. Visible whenever teams have been built —
                    same gate as Lock/Unlock. */}
                {teams && (
                  <button
                    type="button"
                    onClick={sendTeamAssignments}
                    disabled={sendingTeams || balancing}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg"
                    style={{
                      background: 'rgba(0,212,255,0.12)',
                      border: '1px solid rgba(0,212,255,0.45)',
                      color: 'var(--ice)',
                      fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 11,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      cursor: (sendingTeams || balancing) ? 'not-allowed' : 'pointer',
                      opacity: (sendingTeams || balancing) ? 0.45 : 1,
                      transition: 'all 0.15s',
                    }}
                    title="Email each player their team assignment now. Useful if the auto-balance email didn't fire.">
                    {sendingTeams
                      ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Sending…</>
                      : <><Mail size={12} /> Send Teams</>}
                  </button>
                )}
                {/* May 2 2026 — Lock / Unlock teams button. Coach-only,
                    visible only after teams have been balanced (mirror
                    the Clear & Restart visibility gate). When locked
                    shows a Lock icon + "Locked" label and clicking
                    unlocks; when unlocked shows Unlock icon + "Lock
                    Teams" and clicking locks. Confirmation dialog on
                    both transitions to prevent accidental taps. */}
                {teams && (
                  <button
                    type="button"
                    onClick={toggleTeamsLock}
                    disabled={balancing}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg"
                    style={{
                      background: teamsLocked ? 'rgba(167,139,250,0.15)' : 'var(--surface-2)',
                      border: `1px solid ${teamsLocked ? 'rgba(167,139,250,0.5)' : 'var(--border)'}`,
                      color: teamsLocked ? '#a78bfa' : 'var(--text-2)',
                      fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 11,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      cursor: balancing ? 'not-allowed' : 'pointer',
                      opacity: balancing ? 0.45 : 1,
                      transition: 'all 0.15s',
                    }}
                    title={teamsLocked
                      ? 'Teams are locked — click to unlock'
                      : 'Lock these team assignments — prevents re-balancing and auto-balancing'}>
                    {teamsLocked
                      ? <><Lock size={12} /> Locked</>
                      : <><Unlock size={12} /> Lock Teams</>}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* v2.5.149 — post-send change banner. When the coach has
              made manual edits (swaps / adds) since players were last
              emailed their assignments, surface a one-tap "Send the
              update" affordance so the coach decides WHEN players get
              re-notified — instead of firing an email on every micro-
              edit. teamChangesSinceSend comes from the game GET; it's
              0 until the first send, then counts edits. The auto crons
              are idempotent (scheduled_job_log) so they won't re-send
              on their own — this button is the coach's deliberate
              push. */}
          {isCoach && teams && (game?.teamChangesSinceSend || 0) > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 10, flexWrap: 'wrap',
              padding: '10px 12px', marginBottom: 10, borderRadius: 10,
              background: 'rgba(255,193,7,0.08)',
              border: '1px solid rgba(255,193,7,0.30)',
            }}>
              <div style={{ fontSize: 13, color: 'var(--text)', flex: 1, minWidth: 180 }}>
                <strong>{game.teamChangesSinceSend}</strong> change{game.teamChangesSinceSend !== 1 ? 's' : ''} since players were last emailed.
                <span style={{ color: 'var(--text-2)' }}> Players haven't been told yet.</span>
              </div>
              <button
                type="button"
                onClick={sendTeamAssignments}
                disabled={sendingTeams || balancing}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 9,
                  background: 'var(--ice)', color: 'var(--btn-accent-text)',
                  fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 12,
                  letterSpacing: '0.05em', textTransform: 'uppercase',
                  border: 'none',
                  cursor: (sendingTeams || balancing) ? 'not-allowed' : 'pointer',
                  opacity: (sendingTeams || balancing) ? 0.5 : 1,
                  whiteSpace: 'nowrap',
                }}>
                {sendingTeams
                  ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Sending…</>
                  : <><Mail size={13} /> Send update now</>}
              </button>
            </div>
          )}

          {/* May 2 2026 — surface notices about how this game's teams
              were/will-be built. Two possibilities: (1) coach has the
              team-level "include goalies in balancing" setting OFF,
              so balanced teams won't include goalies — useful info
              for non-coach viewers and a reminder for the coach.
              (2) Teams are currently locked — Re-Balance is gated;
              the coach can still per-row swap. Both notices are
              concise + understated so they don't compete with the
              Balanced Teams headline.

              May 4 2026 — three states surfaced now:
              (a) Per-game override IN effect AND differs from team
                  default → tell the coach their override is active
                  and what direction it differs from the default.
              (b) No per-game override but team default = OFF → existing
                  "Goalies excluded" notice (so non-coach viewers know).
              (c) Teams locked notice — unchanged. */}
          {(() => {
            const showOverrideNotice = goalieOverrideActive
              && (balanceIncludeGoalies !== goalieDefaultIsIn);
            const showTeamDefaultNotice = !goalieOverrideActive
              && goalieDefaultIsIn === false;
            if (!showOverrideNotice && !showTeamDefaultNotice && !teamsLocked) return null;
            return (
              <div style={{
                display: 'flex', flexDirection: 'column', gap: 4,
                padding: '8px 10px', marginBottom: 10, borderRadius: 8,
                background: 'rgba(167,139,250,0.06)',
                border: '1px solid rgba(167,139,250,0.20)',
                fontSize: 12, color: 'var(--text-2)',
              }}>
                {showOverrideNotice && (
                  <div>
                    🥅 <strong style={{ color: 'var(--text)' }}>
                      Per-game override:
                    </strong>{' '}
                    Goalies are <strong style={{ color: 'var(--ice)' }}>
                      {balanceIncludeGoalies ? 'INCLUDED' : 'EXCLUDED'}
                    </strong>{' '}
                    for this game.{' '}
                    <span style={{ color: 'var(--text-3)' }}>
                      Team default is{' '}
                      <strong>{goalieDefaultIsIn ? 'IN' : 'OUT'}</strong>
                      {' '}(set in Team Settings → Create Teams).
                      Auto-balance + emails will respect your per-game override.
                    </span>
                  </div>
                )}
                {showTeamDefaultNotice && (
                  <div>🥅 <strong style={{ color: 'var(--text)' }}>Goalies excluded</strong> from balancing — assign them manually below. <span style={{ color: 'var(--text-3)' }}>(Team default; change in Team Settings → Create Teams.)</span></div>
                )}
                {teamsLocked && (
                  <div>🔒 <strong style={{ color: 'var(--text)' }}>Teams locked.</strong> Re-balancing is disabled. Coach can still swap players manually below.</div>
                )}
              </div>
            );
          })()}

          {/* Apr 30 2026 — "YOU'RE ON" hero banner. The K3 column-
              halo + Your-Team pill (below) were still being missed
              by players checking the page from a phone in a busy
              context. This banner is the unmissable one-line answer
              to "which side am I on?" — full-width, bold, team-
              colored. Hidden for coach/manager viewers (they don't
              have a "their" team), and only shown when teams are
              actually populated (post-balance). The label respects
              the coach's per-team rename via game.{home,away}_team_label. */}
          {(() => {
            const userId = user?.id;
            if (!userId || !teams || (!teams.home?.length && !teams.away?.length)) return null;
            const onHome = teams.home?.some(p => p.userId === userId);
            const onAway = teams.away?.some(p => p.userId === userId);
            if (!onHome && !onAway) return null;
            const myColor = onHome ? 'var(--ice)' : 'var(--goal)';
            const myLabel = onHome
              ? (game?.home_team_label || 'Home')
              : (game?.away_team_label || 'Away');
            const myJersey = onHome
              ? (game?.teamSettings?.home_jersey_color || 'black')
              : (game?.teamSettings?.away_jersey_color || 'white');
            return (
              <div
                role="status"
                aria-live="polite"
                style={{
                  marginBottom: 12,
                  padding: '14px 16px',
                  borderRadius: 14,
                  background: `linear-gradient(135deg, ${myColor}22 0%, ${myColor}10 100%)`,
                  border: `2px solid ${myColor}`,
                  boxShadow: `0 0 0 4px ${myColor}22, 0 6px 18px ${myColor}30`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                }}>
                <div style={{
                  width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                  background: myColor, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 20,
                  boxShadow: `0 0 14px ${myColor}80`,
                }}>
                  🏒
                </div>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase',
                    color: 'var(--text-2)', fontWeight: 700, marginBottom: 2,
                  }}>
                    You're playing for
                  </div>
                  <div style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 22, fontWeight: 900, lineHeight: 1.1,
                    color: myColor, letterSpacing: '0.02em',
                  }}>
                    {myLabel.toUpperCase()}
                  </div>
                  <div style={{
                    fontSize: 12, marginTop: 2, color: 'var(--text-2)', fontWeight: 500,
                  }}>
                    Wear <strong style={{ color: 'var(--text)', textTransform: 'capitalize' }}>{myJersey}</strong>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Player lists side by side — shown first */}
          {/* Apr 2026 (Tom_Notes K3) — figure out which team the
              viewer is on (if any) so we can stand it out visually.
              For non-coach viewers this is the most important piece
              of info on the page: which side of the rink, which
              jersey color. We highlight via a glowing border on the
              card AND a ⭐ marker on the team header. The user's
              own row is also flagged below. */}
          <div className="grid grid-cols-2 gap-3 mb-4"
            style={{ background: 'var(--surface-2)', borderRadius: 10, padding: 12 }}>
            {[
              {
                key: 'home',
                label: game?.home_team_label || 'Home',
                players: teams.home, color: 'var(--ice)',
                // Jersey color shown in the column header so every player
                // knows which sweater to bring. Coach sets this in Team
                // Settings → Jersey Colors. Defaults: home=black, away=white.
                jersey: game?.teamSettings?.home_jersey_color || 'black',
              },
              {
                key: 'away',
                label: game?.away_team_label || 'Away',
                players: teams.away, color: 'var(--goal)',
                jersey: game?.teamSettings?.away_jersey_color || 'white',
              },
            ].map(team => {
              // Apr 2026 (Tom_Notes K3) — does this column contain
              // the viewer? If yes we wrap in a glowing border + star
              // marker so it stands out at a glance.
              const isMyTeam = team.players?.some(p => p.userId && p.userId === user?.id);
              return (
              <div key={team.key}
                style={isMyTeam ? {
                  // Apr 30 2026 — coach feedback: highlight wasn't
                  // strong enough on busy rosters. Bumped the box-
                  // shadow halo, made the border brighter, and
                  // added a wider tinted background so the column
                  // reads as "this is YOUR team" at a glance.
                  background: `linear-gradient(180deg, ${team.color}20 0%, ${team.color}08 100%)`,
                  border: `2px solid ${team.color}`,
                  boxShadow: `0 0 0 4px ${team.color}28, 0 4px 16px ${team.color}30`,
                  borderRadius: 10,
                  padding: '8px 10px',
                  margin: '-8px -10px',
                  position: 'relative',
                } : undefined}>
                {/* Apr 30 2026 — "YOUR TEAM" banner across the top of
                    the highlighted column. Visible-from-across-the-room
                    cue for the player coming back to the page after
                    teams are posted. */}
                {isMyTeam && (
                  <div style={{
                    position: 'absolute',
                    top: -10,
                    left: 8,
                    right: 8,
                    background: team.color,
                    color: '#000',
                    fontFamily: 'var(--font-display)',
                    fontSize: 10,
                    fontWeight: 900,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    padding: '3px 10px',
                    borderRadius: 6,
                    textAlign: 'center',
                    boxShadow: `0 2px 6px ${team.color}50`,
                    pointerEvents: 'none',
                  }}>
                    ★ Your Team ★
                  </div>
                )}
                <div style={{
                  fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 11,
                  letterSpacing: '0.12em', textTransform: 'uppercase',
                  color: team.color, marginBottom: 4,
                  // Chunk 7 (Apr 2026) — coaches can rename a team by
                  // tapping its heading. Non-coaches get a plain
                  // non-interactive label.
                  display: 'flex', alignItems: 'center', gap: 6,
                  flexWrap: 'wrap',
                }}>
                  {isCoach ? (
                    <button
                      type="button"
                      onClick={() => openTeamLabelEdit(team.key, team.label)}
                      title="Tap to rename team"
                      style={{
                        background: 'transparent', border: 'none', padding: 0,
                        color: 'inherit', font: 'inherit', letterSpacing: 'inherit',
                        textTransform: 'inherit', cursor: 'pointer',
                        textDecoration: 'underline', textDecorationStyle: 'dashed',
                        textDecorationColor: 'rgba(255,255,255,0.3)',
                        textUnderlineOffset: 3,
                      }}>
                      {team.label}
                    </button>
                  ) : (
                    <span>{team.label}</span>
                  )}
                  {/* Apr 2026 (Tom_Notes K3) — "YOU" pill on the team
                      that contains the viewer. The whole column is
                      already glowing (see wrapper above) so this is
                      a small reinforcing cue rather than the primary
                      signal. */}
                  {isMyTeam && (
                    <span style={{
                      fontSize: 9, fontWeight: 800, padding: '2px 6px',
                      borderRadius: 4, letterSpacing: '0.08em',
                      background: team.color, color: '#000',
                      textTransform: 'uppercase',
                    }}>
                      You
                    </span>
                  )}
                  {isCoach && (
                    <span style={{ color: 'var(--text-2)', fontWeight: 400 }}>
                      {ratingSum(team.players)} pts
                    </span>
                  )}
                </div>
                {/* Jersey color sub-label — normal weight, muted colour so
                    it reads as a caption under the team name. ── */}
                <div style={{
                  fontFamily: 'var(--font-body)', fontSize: 10.5, fontWeight: 500,
                  color: 'var(--text-2)', marginBottom: 10,
                  textTransform: 'capitalize', letterSpacing: '0.02em',
                }}>
                  {team.jersey} jerseys
                </div>
                <div className="space-y-1.5">
                  {team.players.map(p => {
                    // Apr 2026 (mig 045) — show the player's
                    // assigned position for this game. For hybrid
                    // players drafted to D, this differs from their
                    // team-default position. Compact badge: D / F / G.
                    // Falls back to nothing for legacy rosters that
                    // don't have an assignment (created before
                    // mig 045 ran).
                    const assignedPos = p.assignedPosition || null;
                    const posLabel = assignedPos === 'goalie'  ? 'G'
                                   : assignedPos === 'defense' ? 'D'
                                   : assignedPos === 'forward' ? 'F'
                                   : null;
                    const posColor = assignedPos === 'goalie'  ? 'var(--puck)'
                                   : assignedPos === 'defense' ? 'var(--ice)'
                                   : assignedPos === 'forward' ? 'var(--goal)'
                                   : 'var(--text-2)';
                    // Chunk 7 — coaches can tap a row to open the
                    // player-swap modal. Non-coaches see a plain row.
                    const rowInner = (
                      <>
                        <span className="truncate">{p.name}</span>
                        <span className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                          {posLabel && (
                            <span style={{
                              fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                              padding: '1px 5px', borderRadius: 4,
                              background: 'rgba(255,255,255,0.06)',
                              color: posColor,
                              border: `1px solid ${posColor}30`,
                            }}>
                              {posLabel}
                            </span>
                          )}
                          {/* Apr 2026 (Tom_Notes J5/J6) — render
                              whenever a rating value is present.
                              Backend filters `p.rating` to NULL for
                              non-coach viewers when the team setting
                              `show_ratings_to_players=false`. Own-rating
                              exception lets a player see their own
                              rating regardless. */}
                          {p.rating && (
                            <span className={`badge rating-${p.rating}`}>
                              {/* Apr 2026 — show the numeric rating
                                  (1=Elite, 2=Strong, 3=Average,
                                  4=Developing, 5=Beginner) instead of
                                  letter codes. Coaches asked for the
                                  scale they actually set in the
                                  Players tab so it matches what they
                                  see when assigning ratings. */}
                              {p.rating}
                            </span>
                          )}
                        </span>
                      </>
                    );
                    if (!isCoach) {
                      // Apr 2026 (Tom_Notes K3) — viewer's own row
                      // gets a subtle inset background + bold so
                      // their eye lands on it immediately. Color
                      // matches the team accent for visual cohesion.
                      const isMe = p.userId && p.userId === user?.id;
                      return (
                        <div key={p.userId || p.inviteId || p.name}
                          className="flex items-center justify-between"
                          style={{
                            fontSize: 13,
                            fontWeight: isMe ? 700 : 400,
                            background: isMe ? `${team.color}18` : undefined,
                            borderRadius: isMe ? 4 : undefined,
                            padding: isMe ? '2px 6px' : undefined,
                            margin: isMe ? '-2px -6px' : undefined,
                          }}>
                          {rowInner}
                        </div>
                      );
                    }
                    return (
                      <button
                        key={p.userId || p.inviteId || p.name}
                        type="button"
                        onClick={() => openRosterSwap(p, team.key, team.label)}
                        className="w-full flex items-center justify-between active:opacity-70"
                        title="Tap to move this player"
                        style={{
                          fontSize: 13,
                          background: 'transparent', border: 'none', padding: '4px 6px',
                          borderRadius: 6, cursor: 'pointer',
                          textAlign: 'left', font: 'inherit', color: 'inherit',
                          WebkitTapHighlightColor: 'transparent',
                        }}>
                        {rowInner}
                      </button>
                    );
                  })}
                </div>

                {/* Apr 2026 — per-team POSITION tally. Shown to
                    everyone (not coach-only) since position info isn't
                    sensitive — every player can see "this team has 6
                    forwards, 4 defense, 1 goalie." Uses the same
                    assigned-position the balancer wrote (mig 045)
                    rather than the player's default position, so
                    hybrid players drafted to D show up in the D count
                    as expected.

                    Skips zero counts. Falls back to player.position
                    for legacy rosters where assignedPosition is null.
                    Color-coded matching the per-row position badges:
                    F=green, D=blue, G=orange. */}
                {(() => {
                  const counts = { forward: 0, defense: 0, goalie: 0 };
                  for (const p of team.players) {
                    const pos = p.assignedPosition || p.position;
                    if (counts[pos] !== undefined) counts[pos]++;
                  }
                  const parts = [];
                  if (counts.forward > 0) {
                    parts.push(
                      <span key="f" style={{
                        fontSize: 10, padding: '1px 5px', borderRadius: 4,
                        background: 'rgba(0,232,122,0.12)',
                        color: 'var(--goal)',
                        border: '1px solid rgba(0,232,122,0.30)',
                        fontWeight: 700,
                      }}>F ({counts.forward})</span>
                    );
                  }
                  if (counts.defense > 0) {
                    parts.push(
                      <span key="d" style={{
                        fontSize: 10, padding: '1px 5px', borderRadius: 4,
                        background: 'rgba(0,212,255,0.12)',
                        color: 'var(--ice)',
                        border: '1px solid rgba(0,212,255,0.30)',
                        fontWeight: 700,
                      }}>D ({counts.defense})</span>
                    );
                  }
                  if (counts.goalie > 0) {
                    parts.push(
                      <span key="g" style={{
                        fontSize: 10, padding: '1px 5px', borderRadius: 4,
                        background: 'rgba(255,149,0,0.12)',
                        color: 'var(--puck)',
                        border: '1px solid rgba(255,149,0,0.30)',
                        fontWeight: 700,
                      }}>G ({counts.goalie})</span>
                    );
                  }
                  if (parts.length === 0) return null;
                  return (
                    <div style={{
                      marginTop: 10, paddingTop: 10,
                      borderTop: '1px solid var(--border)',
                      display: 'flex', flexWrap: 'wrap', gap: 4,
                      alignItems: 'center',
                    }}>
                      <span style={{
                        fontSize: 9, fontWeight: 700,
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                        color: 'var(--text-2)', marginRight: 2,
                      }}>
                        Positions:
                      </span>
                      {parts}
                    </div>
                  );
                })()}

                {/* Apr 2026 — per-team rating tally. Coaches asked for
                    a quick "level distribution" so they can eyeball
                    whether teams are balanced not just by total points
                    but by the spread of skill levels. Lists each
                    rating that has at least one player, in 1→5 order,
                    as e.g. "1 (3) · 2 (4) · 3 (2)". Skips ratings
                    with zero so it stays compact. Coach-only —
                    matches the existing rating-badge gating; players
                    don't see other players' ratings.

                    Unrated players are tallied separately (rating=null)
                    so coaches can see "5 rated + 2 unrated" rather
                    than have unrated quietly disappear from the count. */}
                {isCoach && (() => {
                  const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, unrated: 0 };
                  for (const p of team.players) {
                    if (p.rating && counts[p.rating] !== undefined) {
                      counts[p.rating]++;
                    } else if (!p.rating) {
                      counts.unrated++;
                    }
                  }
                  const parts = [];
                  for (const r of [1, 2, 3, 4, 5]) {
                    if (counts[r] > 0) {
                      parts.push(
                        <span key={r} className={`badge rating-${r}`}
                          style={{ fontSize: 10, padding: '1px 5px' }}>
                          {r} ({counts[r]})
                        </span>
                      );
                    }
                  }
                  if (counts.unrated > 0) {
                    parts.push(
                      <span key="unrated" style={{
                        fontSize: 10, padding: '1px 5px', borderRadius: 4,
                        background: 'rgba(255,149,0,0.12)',
                        color: 'var(--puck)',
                        border: '1px solid rgba(255,149,0,0.30)',
                        fontWeight: 700,
                      }}>
                        ?? ({counts.unrated})
                      </span>
                    );
                  }
                  if (parts.length === 0) return null;
                  return (
                    <div style={{
                      marginTop: 10, paddingTop: 10,
                      borderTop: '1px solid var(--border)',
                      display: 'flex', flexWrap: 'wrap', gap: 4,
                      alignItems: 'center',
                    }}>
                      <span style={{
                        fontSize: 9, fontWeight: 700,
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                        color: 'var(--text-2)', marginRight: 2,
                      }}>
                        Levels:
                      </span>
                      {parts}
                    </div>
                  );
                })()}
              </div>
              );
            })}
          </div>

          {/* Apr 30 2026 (App_Change_List #20) — rink diagram removed
              from the balancing view. Coach feedback was that with
              two team columns above already showing every player by
              position, the rink graphic added vertical scroll without
              new information. The RinkDiagram component still exists
              and can be re-imported elsewhere if needed (e.g. a
              standalone "where am I playing" view), but on the Game
              page it's gone — the team columns ARE the team display.
              The feedback row that used to follow the rink stays
              right where it was. */}

          {/* Feedback row */}
          <div className="mt-4">
            <p className="text-xs text-secondary mb-2">
              How balanced was this game?
              {myFeedback && <span className="ml-2 text-ice">✓ You voted</span>}
            </p>
            <div className="flex gap-2">
              {[
                { type: 'great_balance', label: '🏆 Great',   color: 'var(--goal)' },
                { type: 'about_right',   label: '👍 OK',      color: 'var(--ice)' },
                { type: 'too_uneven',    label: '⚡ Uneven',  color: 'var(--puck)' },
              ].map(fb => {
                const isVoted = myFeedback === fb.type;
                return (
                  <button key={fb.type} onClick={() => submitFeedback(fb.type)}
                    className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
                    style={{
                      background: isVoted ? `${fb.color}30` : `${fb.color}12`,
                      border: `1px solid ${isVoted ? fb.color : `${fb.color}25`}`,
                      color: fb.color,
                      boxShadow: isVoted ? `0 0 10px ${fb.color}40` : 'none',
                      transform: isVoted ? 'scale(1.03)' : 'scale(1)',
                    }}>
                    {fb.label}{isVoted ? ' ✓' : ''}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Record Result lives at the bottom of the Balanced Teams
              card (May 1 2026). Coach feedback: this is the natural
              "we played, here's the score" conclusion to the
              Balanced Teams section, not a top-level coach action.
              Coach-only — players don't see this button. */}
          {isCoach && (
            <div className="mt-3 pt-3" style={{ borderTop: '1px dashed var(--border)' }}>
              <button onClick={() => setShowResult(true)}
                className="btn-goal w-full flex items-center justify-center gap-2 py-2.5"
                style={{ fontSize: 13, borderRadius: 10 }}>
                <Trophy size={14} /> Record Result
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Notes ── (Apr 2026: removed per user request — public Game Notes
          field cluttered the page. Coach Notes below is unaffected.) */}

      {/* ── Scoreboard removed from GamePage (May 4 2026, v2.5.61) ──
          Was a "Live Scoreboard →" navigation button below Record
          Result. Coach feedback: doesn't belong on the game detail
          page — scoreboard is its own flow now (top-nav Scoreboards
          menu, Dashboard hero on game day). The dedicated page at
          /team/:teamId/games/:gameId/scoreboard is unchanged and
          still reachable directly or from those entry points. */}

      {/* ── Game stats (2026-09-19, Phase 7A) ──
          Per-player + team tables built from the game_events log. Coaches
          and managers get the "Enter stats" grid and the "Track live" entry
          point; everyone else sees the numbers read-only. Renders nothing at
          all when no stats exist and the viewer cannot add any. */}
      <GameStatsPanel teamId={teamId} gameId={gameId} canEdit={isCoach || isManager} />

      {/* ── Coach Notes (private, coach-only) ── */}
      {isCoach && (
        <CoachNotes
          gameId={gameId}
          teamId={teamId}
          initialNotes={game.coach_notes || ''}
          onSaved={refresh}
        />
      )}

      {/* ── Reminder Scope Modal (Apr 2026) ──
          Pops when the coach taps "Game Reminder". Confirms WHO
          gets the email so a reminder doesn't blast everyone by
          default. Default selection: 'checked_in' — most reminders
          are "see you tonight" sent the afternoon of the game,
          and emailing players who already said 'out' (or never
          RSVP'd) is just inbox noise. */}
      {reminderScopeOpen && (() => {
        // Client-side counts for the two audiences (this game's rows —
        // the server also folds in mini-game siblings and invited spares,
        // so the toast after sending is the exact number).
        const av = game?.availability || [];
        const allMembers = (game?.all_members || []).filter(m => m.userId);
        const statusOf = {};
        for (const a of av) if (a.userId) statusOf[a.userId] = a.status;
        const inWait = allMembers.filter(m => ['in', 'waitlist', 'pending_payment'].includes(statusOf[m.userId]));
        const notIn  = allMembers.filter(m =>
          m.role !== 'coach' && m.role !== 'manager' &&
          !['in', 'out', 'waitlist', 'pending_payment'].includes(statusOf[m.userId]) &&
          (m.isPrimary === true || !!statusOf[m.userId] || (invitedUserIds || []).includes(m.userId)));
        const options = [
          { id: 'in_and_waitlist', label: 'Checked in + waitlist', emoji: '✅',
            hint: 'Everyone marked in, plus the waitlist. Never anyone who checked out.', count: inWait.length },
          { id: 'not_checked_in', label: 'Invited but not checked in', emoji: '🤔',
            hint: 'Full-timers and invited spares who haven\'t answered yet.', count: notIn.length },
        ];
        const totalEst = new Set([
          ...(reminderGroups.has('in_and_waitlist') ? inWait.map(m => m.userId) : []),
          ...(reminderGroups.has('not_checked_in')  ? notIn.map(m => m.userId)  : []),
        ]).size;
        const toggleGroup = (id) => setReminderGroups(prev => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id); else next.add(id);
          return next;
        });
        let dateLabel = '';
        try { dateLabel = format(new Date(safeDate(game.game_date) + 'T12:00:00'), 'EEEE, MMMM d'); } catch { /* ignore */ }
        const timeLabel = game?.game_time ? formatTime(game.game_time) : '';
        return (
          <Modal onClose={() => !reminderSending && setReminderScopeOpen(false)} title="Send Game Reminder">
            <p className="text-sm text-secondary mb-3">
              Who should get this reminder? Pick one or both.
            </p>
            <div className="space-y-2 mb-4">
              {options.map(opt => {
                const checked = reminderGroups.has(opt.id);
                return (
                  <label
                    key={opt.id}
                    className="w-full text-left px-3 py-3 rounded-lg flex items-start gap-3 transition-all cursor-pointer"
                    style={{
                      background: checked ? 'rgba(0,212,255,0.10)' : 'var(--surface-2)',
                      border: `1px solid ${checked ? 'rgba(0,212,255,0.35)' : 'var(--border)'}`,
                      opacity: reminderSending ? 0.6 : 1,
                    }}>
                    <input type="checkbox" checked={checked} disabled={reminderSending}
                      onChange={() => toggleGroup(opt.id)} style={{ marginTop: 3, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-bold" style={{ color: checked ? 'var(--ice)' : 'var(--text)' }}>
                          {opt.emoji} {opt.label}
                        </span>
                        <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-2)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                          {opt.count} {opt.count === 1 ? 'player' : 'players'}
                        </span>
                      </div>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-2)' }}>{opt.hint}</p>
                    </div>
                  </label>
                );
              })}
            </div>

            {/* Custom note — optional, rendered as a "Note from <coach>"
                block inside the reminder email. */}
            <label className="label">Add a note (optional)</label>
            <textarea
              value={reminderNote}
              onChange={e => setReminderNote(e.target.value.slice(0, 1000))}
              disabled={reminderSending}
              rows={3}
              placeholder={'e.g. "Early start this week — doors at 8:30. Wear black."'}
              className="input-field"
              style={{ width: '100%', resize: 'vertical', fontSize: 13, display: 'block', boxSizing: 'border-box' }}
            />
            <div className="flex items-center justify-between mt-1 mb-3">
              <span className="text-xs" style={{ color: 'var(--text-3)' }}>{reminderNote.length}/1000</span>
              <button type="button" onClick={() => setReminderPreviewOpen(v => !v)}
                className="text-xs font-semibold" style={{ color: 'var(--ice)', background: 'none', border: 'none', cursor: 'pointer' }}>
                {reminderPreviewOpen ? 'Hide preview' : '👁 Preview the reminder'}
              </button>
            </div>

            {/* Preview — a faithful mock of the email each player gets
                (their own status banner varies: checked-in players see a
                green "You're checked in", the rest a "please RSVP" nudge). */}
            {reminderPreviewOpen && (
              <div className="mb-4 rounded-xl" style={{ border: '1px solid var(--border)', background: 'var(--surface-2)', padding: 14 }}>
                <div className="text-xs mb-2" style={{ color: 'var(--text-3)' }}>
                  <strong style={{ color: 'var(--text-2)' }}>Subject:</strong> 🏟 Game day coming up — please RSVP ({dateLabel})
                </div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 16, letterSpacing: '0.04em' }}>GAME DAY COMING UP 🏟</div>
                <div className="text-xs mt-1 mb-3" style={{ color: 'var(--text-2)' }}>Hey <strong>[player name]</strong> — let your team know if you're playing.</div>
                <div className="rounded-lg text-center mb-3" style={{ background: 'rgba(255,184,71,0.10)', border: '1px solid rgba(255,184,71,0.4)', padding: '10px 12px' }}>
                  <div style={{ fontSize: 20 }}>⚠️</div>
                  <div className="text-xs font-bold uppercase" style={{ color: '#ffb847', letterSpacing: '0.08em' }}>You haven't RSVPed yet</div>
                  <div className="text-xs" style={{ color: 'var(--text-3)' }}>(checked-in players see "✅ You're checked in" here instead)</div>
                </div>
                <div className="text-xs mb-3" style={{ lineHeight: 1.8 }}>
                  📅 <strong>Date</strong> {dateLabel}<br />
                  ⏰ <strong>Time</strong> {timeLabel}<br />
                  🏟️ <strong>Arena</strong> {game?.arena_name || 'TBD'}
                </div>
                {reminderNote.trim() && (
                  <div className="rounded-lg mb-3" style={{ background: 'var(--ice-a8)', borderLeft: '4px solid var(--ice)', padding: '10px 12px' }}>
                    <div className="text-xs font-bold uppercase mb-1" style={{ color: 'var(--ice)', letterSpacing: '0.1em' }}>Note from {user?.name || 'your coach'}</div>
                    <div className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>{reminderNote.trim()}</div>
                  </div>
                )}
                <div className="flex gap-2">
                  <span className="rounded-lg px-3 py-1.5 text-xs font-bold" style={{ background: '#00e87a', color: '#000' }}>✅ I'M IN</span>
                  <span className="rounded-lg px-3 py-1.5 text-xs font-bold" style={{ background: '#ff3b3b', color: '#fff' }}>❌ I'M OUT</span>
                </div>
                <div className="text-xs mt-2" style={{ color: 'var(--text-3)' }}>No login needed — just click a button. Link expires at game time.</div>
              </div>
            )}

            <div className="p-3 rounded-lg mb-4 text-xs"
              style={{
                background: totalEst > 0 ? 'rgba(0,255,136,0.06)' : 'var(--surface-2)',
                border: `1px solid ${totalEst > 0 ? 'rgba(0,255,136,0.25)' : 'var(--border)'}`,
                color: totalEst > 0 ? 'var(--goal)' : 'var(--text-2)',
              }}>
              {reminderGroups.size === 0
                ? 'Pick at least one group.'
                : `About ${totalEst} ${totalEst === 1 ? 'player' : 'players'} — the exact count (incl. mini-game sessions) is confirmed after sending.`}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setReminderScopeOpen(false)}
                disabled={reminderSending}
                className="btn-ghost flex-1 justify-center">
                Cancel
              </button>
              <button
                type="button"
                onClick={sendReminderWithGroups}
                disabled={reminderSending || reminderGroups.size === 0}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm transition-all btn-ice">
                {reminderSending
                  ? <><div className="spinner" style={{ width: 13, height: 13 }} /> Sending…</>
                  : <><Send size={14} /> Send</>}
              </button>
            </div>
          </Modal>
        );
      })()}

      {/* ── Non-responders Modal ──
          Apr 30 2026 (App_Change_List #17) — title and intro copy
          updated. The list shown here IS the confirmation step the
          user asked for: each name is visible BEFORE the coach taps
          "Jab All", so accidental sends can't happen.
          May 4 2026 — added subgroup filter chips so coaches can
          target just FT players, just spares, just goalies, etc.
          The list + count + Jab All button all reflect the active
          filter. Default 'all' preserves prior behavior. */}
      {showNonResponders && (() => {
        // Compute subgroup buckets the same way as the main page
        // section — keeps "FT players who haven't responded" in
        // sync between the section and the modal.
        const isGoalieRow = (p) =>
          p.position === 'goalie' || (Array.isArray(p.positions) && p.positions.includes('goalie'));
        const buckets = {
          all:           nudgeableNotResponded,
          ft_players:    nudgeableNotResponded.filter(p =>  p.isPrimary && !isGoalieRow(p)),
          ft_goalies:    nudgeableNotResponded.filter(p =>  p.isPrimary &&  isGoalieRow(p)),
          spare_players: nudgeableNotResponded.filter(p => !p.isPrimary && !isGoalieRow(p)),
          spare_goalies: nudgeableNotResponded.filter(p => !p.isPrimary &&  isGoalieRow(p)),
        };
        const chips = [
          { id: 'all',           label: 'All',            count: buckets.all.length },
          { id: 'ft_players',    label: '👤 FT players',  count: buckets.ft_players.length },
          { id: 'ft_goalies',    label: '🥅 FT goalies',  count: buckets.ft_goalies.length },
          { id: 'spare_players', label: '⏳ Spares',      count: buckets.spare_players.length },
          { id: 'spare_goalies', label: '⏳🥅 Spare G',   count: buckets.spare_goalies.length },
        ];
        const filtered = buckets[jabFilter] || buckets.all;
        return (
        <Modal onClose={() => setShowNonResponders(false)} title={`Awaiting Response — Confirm Jab (${nudgeableNotResponded.length})`}>
          <p className="text-sm text-secondary mb-3">
            These are the players who'll receive a Jab — full-timers and
            spares who were invited but haven't responded yet. Filter
            by group, send to one person at a time, or Jab All at the
            bottom.
          </p>
          {/* Subgroup filter chips. Empty buckets are still shown but
              greyed + disabled so the coach can see "0 FT goalies
              awaiting" at a glance. */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {chips.map(c => {
              const active = jabFilter === c.id;
              const empty  = c.count === 0;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => !empty && setJabFilter(c.id)}
                  disabled={empty}
                  style={{
                    padding: '4px 10px', borderRadius: 999,
                    background: active ? 'rgba(167,139,250,0.18)' : 'var(--surface-2)',
                    border: `1px solid ${active ? 'rgba(167,139,250,0.45)' : 'var(--border)'}`,
                    color: active ? '#a78bfa' : empty ? 'var(--text-3)' : 'var(--text-2)',
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.02em',
                    cursor: empty ? 'not-allowed' : 'pointer',
                    opacity: empty ? 0.5 : 1,
                  }}>
                  {c.label} <span style={{ opacity: 0.7 }}>({c.count})</span>
                </button>
              );
            })}
          </div>
          <div className="space-y-1.5 mb-5 max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-secondary text-sm text-center py-4">
                No players in this group are awaiting response.
              </p>
            ) : filtered.map(p => (
              <div key={p.userId}
                className="flex items-center gap-3 px-3 py-2 rounded-lg"
                style={{ background: 'var(--surface-2)' }}>
                <span style={{ fontSize: 16 }}>—</span>
                <span className="flex-1 text-sm font-medium truncate">{p.name || 'Player'}</span>
                <button
                  onClick={() => nudgeSingle(p.userId, p.name || 'Player')}
                  disabled={!!nudgingSingle[p.userId]}
                  title={`Jab ${p.name}`}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
                  style={{
                    background: 'rgba(167,139,250,0.12)',
                    border: '1px solid rgba(167,139,250,0.3)',
                    color: '#a78bfa',
                    flexShrink: 0,
                  }}>
                  {nudgingSingle[p.userId]
                    ? <div className="spinner" style={{ width: 10, height: 10 }} />
                    : <><Send size={10} /> Jab</>}
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowNonResponders(false)} className="btn-ghost flex-1 justify-center">
              Close
            </button>
            <button
              onClick={async () => {
                // Apr 30 2026 (App_Change_List #17) — main Jab now
                // targets EXACTLY the Awaiting Response set (FT
                // auto-invited + spares explicitly invited who
                // haven't responded). Was previously calling
                // sendReminderWithScope('full_roster') which blasted
                // every season member regardless of invite status.
                // The modal already lists the audience by name above
                // (the .map(p => …) loop on line ~4490) so this is the
                // confirmation step the user asked for — they see who
                // gets the Jab before tapping the button below.
                //
                // May 4 2026 — now respects the active subgroup filter:
                // the button targets only the filtered subset rather
                // than the full Awaiting Response list. The button
                // label reflects the filtered count + group name so
                // there's no ambiguity about who gets the email.
                const ids = filtered
                  .map(p => p.userId)
                  .filter(Boolean);
                if (ids.length === 0) {
                  toast('Nobody to jab — everyone in this group has responded.');
                  setShowNonResponders(false);
                  return;
                }
                setReminderSending(true);
                try {
                  const { data } = await api.post(
                    `/teams/${teamId}/games/${gameId}/send-reminder`,
                    { userIds: ids }
                  );
                  toast.success(data.message || `Jab sent to ${ids.length} player${ids.length === 1 ? '' : 's'}`);
                  setShowNonResponders(false);
                } catch {
                  toast.error('Failed to send');
                }
                setReminderSending(false);
              }}
              disabled={reminderSending || filtered.length === 0}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm transition-all"
              style={{ background: 'rgba(167,139,250,0.15)', border: '1px solid rgba(167,139,250,0.3)', color: '#a78bfa', opacity: reminderSending ? 0.6 : 1 }}>
              <Send size={14} /> {reminderSending ? 'Sending…' : `Jab ${jabFilter === 'all' ? 'All' : 'Group'} (${filtered.length})`}
            </button>
          </div>
        </Modal>
        );
      })()}

      {/* ── Result Modal ── */}
      {showResult && (
        <Modal onClose={() => setShowResult(false)} title="Enter Result">
          <form onSubmit={submitResult} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Home Score</label>
                <input type="number" min="0" max="99" required className="input-field text-center text-2xl font-bold"
                  style={{ color: 'var(--ice)' }}
                  value={result.homeScore} onChange={e => setResult(p => ({ ...p, homeScore: e.target.value }))} />
              </div>
              <div>
                <label className="label">Away Score</label>
                <input type="number" min="0" max="99" required className="input-field text-center text-2xl font-bold"
                  style={{ color: 'var(--goal)' }}
                  value={result.awayScore} onChange={e => setResult(p => ({ ...p, awayScore: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowResult(false)} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" className="btn-goal flex-1 justify-center">Save Result</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── Arena Change Modal ── */}
      {showArenaChange && (
        <Modal onClose={() => setShowArenaChange(false)} title="⚠️ Arena Change">
          <p className="text-sm text-secondary mb-4">
            All confirmed (IN) players will be notified by email immediately.
          </p>
          <form onSubmit={submitArenaChange} className="space-y-4">
            <div>
              <label className="label">New Arena</label>
              <ArenaSearch
                teamId={teamId}
                name={arenaForm.newArena}
                address={arenaForm.newAddress}
                country={game?.teamSettings?.country || ''}
                onSelect={s => setArenaForm(p => ({ ...p, newArena: s.name, newAddress: s.address }))}
                onChangeName={v => setArenaForm(p => ({ ...p, newArena: v }))}
                onChangeAddress={v => setArenaForm(p => ({ ...p, newAddress: v }))}
                placeholder="Search arena name…"
              />
            </div>
            <div>
              <label className="label">Reason (optional)</label>
              <input type="text" className="input-field" placeholder="Ice maintenance, double-booking…"
                value={arenaForm.reason} onChange={e => setArenaForm(p => ({ ...p, reason: e.target.value }))} />
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowArenaChange(false)} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" className="flex-1 justify-center btn-amber">Notify Team</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── Bulk Assign Players modal (May 4 2026 / v2.5.69) ──
          Manual team-creation mode entry point. Coach feedback (item
          5 of the May 4 batch): "If someone has the 'manual create
          teams' enabled, then in the specific game under the 'Balance
          Teams' group, come up with a way so that they can bulk
          select players and assign them to either team. Have it as a
          popup where they can select the players, who have checked
          in, to be assigned to the specific game."
          Renders three sections: Unassigned (default state of every
          checked-in player), Home, Away. Each row has buttons to move
          to Home/Away/Unassigned. The "Assign Players" submit button
          POSTs the final state to /roster/bulk-assign which atomically
          wipes any prior roster and writes the new one. ── */}
      {showBulkAssign && (() => {
        // BulkAssignModal is a self-contained inline component so
        // the parent doesn't have to thread state down. It reads the
        // checked-in player list from the GamePage's `inPlayers`
        // closure and tracks per-player team assignments locally.
        return (
          <BulkAssignModal
            inPlayers={inPlayers}
            onClose={() => setShowBulkAssign(false)}
            onSubmit={async (home, away) => {
              try {
                await api.post(`/teams/${teamId}/games/${gameId}/roster/bulk-assign`, { home, away });
                toast.success(`Assigned ${home.length} home, ${away.length} away`);
                setShowBulkAssign(false);
                setRosterChanged(false);
                refresh();
              } catch (err) {
                toast.error(err.response?.data?.error || 'Failed to assign players');
              }
            }}
          />
        );
      })()}

      {/* ── Late Arrival modal (2026-08-20, no-show list) ──
          Opens from "Clear No-Show" on a live game. The flagged player
          showed up after all — the coach picks how they re-enter:
          manual assignment to either side, an AI re-balance, or just
          forgiving the flag while leaving them checked out. Manual /
          re-balance options only appear once teams exist. ── */}
      {/* ── RSVP history modal (2026-09-10, mig 110) ── */}
      {rsvpHistoryFor && (() => {
        const { player, rows, error } = rsvpHistoryFor;
        const STATUS_LABEL = { in: '✅ In', out: '❌ Out', waitlist: '⏳ Waitlist',
          pending_payment: '💳 Pending payment', maybe: '🤔 Maybe' };
        const SOURCE_LABEL = {
          app: 'In the app', email_link: 'Email link', coach_override: 'Coach',
          coach_promote: 'Coach promoted', coach_clear: 'Coach cleared responses',
          unattributed: 'Automatic',
        };
        const fmtStatus = (st) => (st == null ? '— (no response)' : (STATUS_LABEL[st] || st));
        const fmtWhen = (iso) => { try { return format(new Date(iso), 'EEE, MMM d · h:mm a'); } catch { return iso; } };
        return (
          <Modal onClose={() => setRsvpHistoryFor(null)} title={`RSVP history — ${player.name || 'Player'}`}>
            <div className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
              Every change to this player's status for this game, newest first — who made it and from what.
              {' '}<strong>Automated</strong> means a mail-security scanner or link preview fetched an email link,
              not a person.
            </div>
            {rows === null && <div className="flex justify-center py-6"><div className="spinner" style={{ width: 18, height: 18 }} /></div>}
            {error && <div className="text-sm" style={{ color: 'var(--penalty)' }}>{error}</div>}
            {rows && rows.length === 0 && !error && (
              <div className="text-sm text-secondary py-4 text-center">No status changes recorded for this game yet.</div>
            )}
            {rows && rows.length > 0 && (
              <div className="space-y-2" style={{ maxHeight: 420, overflowY: 'auto' }}>
                {rows.map(r => {
                  const automated = r.clientKind === 'automated';
                  const who = r.source === 'app' && r.actorUserId === player.userId ? (player.name || 'Player')
                            : (r.actorName || SOURCE_LABEL[r.source] || r.source);
                  return (
                    <div key={r.id} className="px-3 py-2.5 rounded-xl"
                      style={{ background: 'var(--surface-2)', border: `1px solid ${automated ? 'rgba(255,149,0,0.45)' : 'var(--border)'}` }}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="text-sm font-bold">{fmtStatus(r.from)} → {fmtStatus(r.to)}</span>
                        <span className="text-xs text-secondary">{fmtWhen(r.at)}</span>
                      </div>
                      <div className="text-xs mt-1" style={{ color: 'var(--text-2)' }}>
                        <span className="font-semibold">{SOURCE_LABEL[r.source] || r.source}</span>
                        {r.source !== 'unattributed' && <> · {who}</>}
                        {r.client && <> · {automated ? '⚠️ ' : ''}{r.client}</>}
                      </div>
                      {automated && (
                        <div className="text-xs mt-1 font-semibold" style={{ color: 'var(--puck)' }}>
                          Not a person — an automated fetch of an email link.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Modal>
        );
      })()}

      {lateArrivalFor && (
        <Modal
          onClose={() => { if (!lateArrivalBusy) setLateArrivalFor(null); }}
          title={`${lateArrivalFor.name || 'Player'} showed up?`}>
          <div className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
            Clearing the no-show flag removes the 👻 badge and the strike.
            If they're playing, check them in and put them on a team.
          </div>
          <div className="space-y-2">
            {teams && (
              <>
                <button
                  onClick={() => handleLateArrival(lateArrivalFor, 'home')}
                  disabled={lateArrivalBusy}
                  className="w-full text-sm font-bold px-3 py-2.5 rounded-xl text-left"
                  style={{ background: 'rgba(0,212,255,0.10)', color: 'var(--ice)', border: '1px solid rgba(0,212,255,0.30)' }}>
                  ✅ Check in — assign to {game?.home_team_label || 'Home'}
                </button>
                <button
                  onClick={() => handleLateArrival(lateArrivalFor, 'away')}
                  disabled={lateArrivalBusy}
                  className="w-full text-sm font-bold px-3 py-2.5 rounded-xl text-left"
                  style={{ background: 'rgba(255,149,0,0.10)', color: 'var(--puck)', border: '1px solid rgba(255,149,0,0.30)' }}>
                  ✅ Check in — assign to {game?.away_team_label || 'Away'}
                </button>
                {!teamsLocked && (
                  <button
                    onClick={() => handleLateArrival(lateArrivalFor, 'rebalance')}
                    disabled={lateArrivalBusy}
                    className="w-full text-sm font-bold px-3 py-2.5 rounded-xl text-left"
                    style={{ background: 'rgba(0,255,136,0.10)', color: 'var(--goal)', border: '1px solid rgba(0,255,136,0.30)' }}>
                    ⚖️ Check in — re-balance teams with AI
                  </button>
                )}
              </>
            )}
            {!teams && (
              <button
                onClick={() => handleLateArrival(lateArrivalFor, 'checkin')}
                disabled={lateArrivalBusy}
                className="w-full text-sm font-bold px-3 py-2.5 rounded-xl text-left"
                style={{ background: 'rgba(0,255,136,0.10)', color: 'var(--goal)', border: '1px solid rgba(0,255,136,0.30)' }}>
                ✅ Check in (teams not built yet)
              </button>
            )}
            <button
              onClick={() => handleLateArrival(lateArrivalFor, 'clear')}
              disabled={lateArrivalBusy}
              className="w-full text-sm font-semibold px-3 py-2.5 rounded-xl text-left"
              style={{ background: 'var(--surface-2)', color: 'var(--text-2)', border: '1px solid var(--border)' }}>
              😇 Just clear the flag — keep them checked out
            </button>
          </div>
          {lateArrivalBusy && (
            <div className="flex justify-center mt-3"><div className="spinner" style={{ width: 18, height: 18 }} /></div>
          )}
        </Modal>
      )}

      {/* ── Per-player action menu — fixed-position overlay anchored to
          the 3-dot button's viewport rect. Rendered at page level (not
          inside the row map) so scrolling the player list doesn't clip
          the menu, and so we can flip the menu upward when the anchor
          is in the bottom half of the viewport. ── */}
      {playerMenuFor && playerMenuFor.player && (
        <>
          <div
            onClick={() => setPlayerMenuFor(null)}
            style={{
              position: 'fixed', inset: 0, zIndex: 299,
              background: 'transparent',
            }}
          />
          {(() => {
            // Decide above/below based on available space. Menu is ~200px
            // tall with 5 items; flip upward if the anchor is closer to
            // viewport bottom than top.
            const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
            const anchorBottom = playerMenuFor.rect.bottom;
            const spaceBelow   = vh - anchorBottom;
            const flipUp       = spaceBelow < 240;
            // Right-align to the anchor. Clamp so the menu never goes
            // off-screen on narrow viewports.
            const menuWidth = 190;
            const rightEdge = typeof window !== 'undefined' ? window.innerWidth : 400;
            const left = Math.max(8, Math.min(
              playerMenuFor.rect.right - menuWidth,
              rightEdge - menuWidth - 8
            ));
            const p = playerMenuFor.player;
            return (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'fixed',
                  zIndex: 400,
                  left,
                  [flipUp ? 'bottom' : 'top']: flipUp
                    ? (vh - playerMenuFor.rect.top + 4)
                    : (playerMenuFor.rect.bottom + 4),
                  width: menuWidth,
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  padding: 4,
                }}>
                <MenuItem
                  icon="✅" label="Check In"
                  onClick={() => applySingleStatus(p, 'in')}
                  disabled={p.status === 'in'}
                />
                <MenuItem
                  icon="❌" label="Mark Out"
                  onClick={() => applySingleStatus(p, 'out')}
                  disabled={p.status === 'out'}
                />
                <MenuItem
                  icon="⏳" label="Add to Waitlist"
                  onClick={() => applySingleStatus(p, 'waitlist')}
                  disabled={p.status === 'waitlist'}
                />
                <MenuItem
                  icon="—" label="Clear Response"
                  onClick={() => applySingleStatus(p, 'none')}
                  disabled={p.status === null}
                />
                {p.userId && (
                  <>
                    <div style={{ height: 1, background: 'var(--border)', margin: '3px 0' }} />
                    {/* 2026-07-28 (mig 109) — the ghost flag. Flag marks
                        them Out + no-show (stats drop the game, next
                        game wears the 👻 badge); Clear forgives.
                        2026-08-20 — no longer historical-only: coaches
                        flag AT the rink when a checked-in player isn't
                        there at puck drop. Gated on the player having
                        actually responded (in/out) — flagging someone
                        who never checked in makes no sense. */}
                    {!p.noShow && (isHistorical || p.status === 'in' || p.status === 'out') && (
                      <MenuItem
                        icon="👻" label="Flag No-Show"
                        onClick={() => applyNoShow(p, true)}
                      />
                    )}
                    {p.noShow && (
                      <MenuItem
                        icon="😇" label="Clear No-Show"
                        onClick={() => {
                          setPlayerMenuFor(null);
                          // Live game → the player probably showed up
                          // late. Open the late-arrival chooser (check
                          // in + assign / re-balance / just forgive).
                          // Completed game → nothing to assign anymore,
                          // just clear the flag like before.
                          if (isHistorical) applyNoShow(p, false);
                          else setLateArrivalFor(p);
                        }}
                      />
                    )}
                    {/* Send payment link — for a spare who's checked in or
                        awaiting payment but never paid. Only when the team
                        collects spare fees in-app (else there's no link).
                        Backend re-checks: blocks if a real payment exists. */}
                    {(!p.isPrimary  /* spares only — FT players don't pay spare fees */
                      && game?.teamSettings?.payment_enabled === true
                      && game?.teamSettings?.spare_pay_required === true
                      && Number(game?.teamSettings?.spare_fee_cents) > 0
                      && (p.status === 'in' || p.status === 'pending_payment')) && (
                      <MenuItem
                        icon="💳" label="Send payment link"
                        onClick={async () => {
                          setPlayerMenuFor(null);
                          try {
                            const { data } = await api.post(
                              `/teams/${teamId}/games/${gameId}/availability/${p.userId}/resend-payment-link`
                            );
                            const amt = data?.amountCents
                              ? ` ($${(data.amountCents / 100).toFixed(2)} ${String(data.paymentCurrency || 'CAD').toUpperCase()})`
                              : '';
                            toast.success(`Payment link sent to ${p.name}${amt} 💳`);
                          } catch (err) {
                            toast.error(friendlyError(err, 'Failed to send payment link'));
                          }
                        }}
                      />
                    )}
                    <MenuItem
                      icon="✉" label="Message Player"
                      onClick={() => openComposeForPlayer(p)}
                    />
                    {/* 2026-09-10 — the audit trail. Answers "who changed
                        this player's status, when, and from what client?"
                        A mail-security scanner once checked a player out
                        by prefetching the I'M OUT link in his reminder
                        email; this is what makes that visible. */}
                    <MenuItem
                      icon="🕘" label="RSVP history"
                      onClick={() => openRsvpHistory(p)}
                    />
                  </>
                )}
              </div>
            );
          })()}
        </>
      )}

      {/* ── Compose Message modal — opens from the per-player popover
          (Message Player) and from the future "Email Players" button.
          Sends through POST /teams/:teamId/messages/send with Reply-To
          set to the coach's email so replies land in the coach's inbox.
          Limits: subject 200 chars, body 4000 chars, max 100 recipients. ── */}
      {composeOpen && (
        <Modal
          onClose={() => { if (!composeSending) setComposeOpen(false); }}
          title={composeTargets.length === 1
            ? `Message ${composeTargets[0].name || 'Player'}`
            : `Message ${composeTargets.length} Players`}>
          <div className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
            Sent as an email from Game Day. Replies will come directly to your email address.
          </div>

          {composeTargets.length > 1 && (
            <div className="mb-3 p-2 rounded-lg" style={{ background: 'var(--surface-2)', maxHeight: 80, overflowY: 'auto' }}>
              <div className="text-xs font-semibold mb-1" style={{ color: 'var(--text-2)' }}>Recipients</div>
              <div className="text-xs" style={{ color: 'var(--text)', lineHeight: 1.6 }}>
                {composeTargets.map(t => t.name).filter(Boolean).join(', ')}
              </div>
            </div>
          )}

          <label className="label">Subject</label>
          <input
            type="text"
            value={composeSubject}
            onChange={e => setComposeSubject(e.target.value)}
            maxLength={200}
            placeholder="Subject"
            className="input-field mb-3"
            disabled={composeSending}
          />

          <label className="label">Message</label>
          <textarea
            value={composeBody}
            onChange={e => setComposeBody(e.target.value)}
            maxLength={4000}
            rows={8}
            placeholder="Type your message here…"
            className="input-field mb-1"
            // v2.5.185 — was clipping to ~2 visible lines on mobile.
            // display:block + box-sizing + a firmer minHeight stops the
            // textarea collapsing inside its flex/scroll container.
            style={{ resize: 'vertical', minHeight: 140, height: 140, display: 'block', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5 }}
            disabled={composeSending}
          />
          <div className="text-xs text-right mb-3" style={{ color: 'var(--text-3)', fontSize: 10 }}>
            {composeBody.length}/4000
          </div>

          {/* ── Channel selector (Apr 2026, Tom_Notes B8) ──
              Coach picks how the message goes out. Default: email
              only (matches legacy behaviour). Users get the message
              on every selected channel — server-side dedupes per-
              user so they don't get the same in-app notification
              twice when both push and in_app are selected. */}
          <label className="label">Send via</label>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[
              { key: 'email',  emoji: '📧', label: 'Email',
                desc: 'Lands in the inbox. Replies go to your email.' },
              { key: 'push',   emoji: '📱', label: 'Push',
                desc: 'OS-level notification on phones with the app.' },
              { key: 'in_app', emoji: '🔔', label: 'In-app',
                desc: 'Appears in the bell-icon feed inside the app.' },
            ].map(opt => {
              const active = composeChannels.includes(opt.key);
              const toggle = () => {
                setComposeChannels(prev => {
                  // Don't allow unchecking the LAST channel — at
                  // least one must be selected. The submit handler
                  // also defends, but UX feels cleaner if the
                  // checkbox just refuses the deselection.
                  if (active && prev.length === 1) return prev;
                  return active ? prev.filter(c => c !== opt.key) : [...prev, opt.key];
                });
              };
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={toggle}
                  disabled={composeSending}
                  className="flex flex-col items-start gap-1 py-2.5 px-3 rounded-lg text-left transition-all"
                  style={{
                    background: active ? 'var(--ice-a8)' : 'var(--surface-2)',
                    border: `1.5px solid ${active ? 'var(--ice-a40)' : 'var(--border)'}`,
                    color: active ? 'var(--ice)' : 'var(--text-2)',
                    cursor: composeSending ? 'not-allowed' : 'pointer',
                    opacity: composeSending ? 0.6 : 1,
                  }}
                  title={opt.desc}
                >
                  <span className="text-xs font-bold">
                    {active ? '✓ ' : ''}{opt.emoji} {opt.label}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => { if (!composeSending) setComposeOpen(false); }}
              className="btn-ghost flex-1 justify-center"
              disabled={composeSending}>
              Cancel
            </button>
            <button
              type="button"
              onClick={sendComposeMessage}
              disabled={composeSending || !composeSubject.trim() || !composeBody.trim()}
              className="btn-ice flex-1 justify-center">
              {composeSending
                ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Sending…</>
                : <><Mail size={14} /> Send</>}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Spare Picker modal (Coach Actions → Spares button) ─────────
          Replaces the prior behaviour that blast-emailed every spare
          regardless of their RSVP state. Now the coach sees each spare's
          current status and picks who to nudge. Default selection is
          everyone who hasn't responded; the coach can tick additional
          spares (e.g. re-ask an "out" spare who may have changed their
          mind) or untick any they want to leave alone.
          
          When the nudged spares click "I'm In" in the resulting email,
          they land on the game page and their RSVP is created; team
          settings (spare_auto_waitlist) decide whether they go straight
          in or onto the waitlist. ── */}
      {sparePickerOpen && (() => {
        // Apr 30 2026 (App_Change_List #23) — include EVERY spare on
        // the roster, even those without an email or those who haven't
        // accepted their invite yet. Coach feedback: "I have a friend
        // I add as a placeholder, and I want them on this list so I
        // can see them in the same view as the rest of the spares."
        // Three types of rows:
        //
        //   • Real user with email      — checkbox + email goes out
        //   • Real placeholder (memberId, no userId, no email) — shown
        //     as "📋 No email — manual check-in only", checkbox
        //     disabled, coach uses the existing roster-row override
        //     to mark them in for the game.
        //   • Pending invite (inviteId, no userId) — shown the same as
        //     a placeholder, with a "Mark In" inline button that calls
        //     the existing override endpoint with the inviteId. They
        //     CAN be marked in directly because game_availability
        //     supports invite_id (mig 017).
        //
        // The submit (`Invite N`) only sends userIds — the picker tracks
        // checkable-spare userIds in sparePickerIds; placeholders /
        // pending invites are visible but excluded from the email blast.
        const roster = (game.all_members || []);
        const avById = {};
        for (const a of (game.availability || [])) {
          if (a.userId) avById[a.userId] = a.status;
          if (a.inviteId) avById[`invite:${a.inviteId}`] = a.status;
        }
        const allSpares = roster
          .filter(m => !m.isPrimary)
          .map(m => {
            const isPlaceholder = m.isPlaceholder === true;
            const isPending     = m.isPending === true;
            const isEmailable   = !!m.userId && !isPlaceholder;
            const lookupKey     = m.userId
              ? m.userId
              : (m.inviteId ? `invite:${m.inviteId}` : null);
            return {
              ...m,
              isPlaceholder,
              isPending,
              isEmailable,
              status: lookupKey ? (avById[lookupKey] || null) : null,
            };
          })
          // Sort: emailable no-response first (the coach's main target),
          // then emailable other statuses, then non-emailable rows
          // (placeholders + pending invites) last so the picker is
          // top-loaded with actionable rows.
          .sort((a, b) => {
            // primary tier: emailable above non-emailable
            if (a.isEmailable !== b.isEmailable) return a.isEmailable ? -1 : 1;
            const order = { null: 0, waitlist: 1, in: 2, out: 3 };
            const aKey = order[a.status] ?? 4;
            const bKey = order[b.status] ?? 4;
            if (aKey !== bKey) return aKey - bKey;
            return (a.name || '').localeCompare(b.name || '');
          });

        const toggleOne = (userId) => {
          setSparePickerIds(prev => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
          });
        };
        const toggleAll = () => {
          // Apr 30 2026 (#23) — only emailable spares (real users with an
          // email) participate in select-all; placeholders / pending
          // invites can't receive email.
          // v2.5.226 — scope Select-all to the ACTIVE tab (filteredSpares)
          // so it never bulk-grabs goalies when the coach is on the Players
          // tab, or vice-versa. Selections in the other tabs are preserved.
          // (filteredSpares is defined below but this runs on click, after
          // render, so it's initialized by then.)
          const emailable = filteredSpares.filter(s => s.isEmailable);
          const allSelected = emailable.length > 0
            && emailable.every(s => sparePickerIds.has(s.userId));
          setSparePickerIds(prev => {
            const next = new Set(prev);
            if (allSelected) emailable.forEach(s => next.delete(s.userId));
            else             emailable.forEach(s => next.add(s.userId));
            return next;
          });
        };
        const statusBadge = (s) => {
          if (s === 'in')       return { emoji: '✅', label: 'Checked In',   color: 'var(--goal)' };
          if (s === 'waitlist') return { emoji: '⏳', label: 'On Waitlist',  color: '#a78bfa' };
          if (s === 'out')      return { emoji: '❌', label: 'Out',          color: 'var(--penalty)' };
          return                       { emoji: '🤔', label: 'No Response',  color: 'var(--text-3)' };
        };
        const autoWaitlist = game?.teamSettings?.spare_auto_waitlist;
        const emailableSpares = allSpares.filter(s => s.isEmailable);
        const nonEmailableCount = allSpares.length - emailableSpares.length;

        // v2.5.185 — position split. A spare is a goalie if their member
        // position is 'goalie' (or it's in their positions[] list).
        const isGoalieSpare = (s) =>
          s.position === 'goalie' ||
          (Array.isArray(s.positions) && s.positions.includes('goalie'));
        const spareGoalieCount = allSpares.filter(isGoalieSpare).length;
        const sparePlayerCount = allSpares.length - spareGoalieCount;
        // The list actually rendered, after applying the active filter.
        const filteredSpares = spareInviteFilter === 'goalies'
          ? allSpares.filter(isGoalieSpare)
          : spareInviteFilter === 'players'
            ? allSpares.filter(s => !isGoalieSpare(s))
            : allSpares;

        // v2.5.226 — the send + counts are SCOPED to the active tab, so a
        // Players-tab "Invite N" can never include goalies (and vice-versa).
        // This is the core fix for "goalies got the spare request": the tab
        // is now authoritative for who gets emailed, not just a view filter.
        const filteredEmailable = filteredSpares.filter(s => s.isEmailable);
        const noResponseCount = filteredEmailable.filter(s => s.status === null).length;
        const filteredPickedIds = filteredEmailable
          .filter(s => sparePickerIds.has(s.userId))
          .map(s => s.userId);
        const pickedCount = filteredPickedIds.length;

        // Apr 30 2026 (#23) — manual check-in for non-emailable spares.
        // Pending invites (have inviteId) can be marked in via the
        // existing override endpoint (game_availability supports
        // invite_id since mig 017). Placeholders (memberId, no userId,
        // no inviteId) cannot — they need a separate code path that
        // doesn't exist today, so we just surface a hint that the
        // coach should use the player-row override on the Game page.
        const checkInPendingInvite = async (s) => {
          if (!s.inviteId) return;
          try {
            await api.post(
              `/teams/${teamId}/games/${gameId}/availability/override`,
              { updates: [{ inviteId: s.inviteId, status: 'in' }] }
            );
            toast.success(`${s.name || 'Player'} checked in`);
            refresh();
          } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to check in');
          }
        };

        return (
          <Modal
            onClose={() => { if (!sparePickerSending) setSparePickerOpen(false); }}
            title="Invite Spares">
            <div className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
              Pick which spares to email about this game.{' '}
              {autoWaitlist
                ? <>When they click <strong>I'm In</strong>, they'll go onto the <span style={{ color: '#a78bfa' }}>waitlist</span> for you to approve.</>
                : <>When they click <strong>I'm In</strong>, they'll be confirmed for the game (up to the player cap).</>}
              {nonEmailableCount > 0 && (
                <> Players without an email are listed below — they won't receive a message, but you can mark pending invites in directly here, or use the player-row override on the game page for placeholders.</>
              )}
            </div>

            {allSpares.length === 0 ? (
              <div
                className="p-4 rounded-xl text-sm text-center"
                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>
                No spares on this team yet.
              </div>
            ) : (
              <>
                {/* v2.5.185 — Players / Goalies filter so the two groups
                    aren't mixed. Counts shown per tab. */}
                {spareGoalieCount > 0 && sparePlayerCount > 0 && (
                  <div className="flex gap-1 mb-3 p-1 rounded-xl" style={{ background: 'var(--surface-2)' }}>
                    {[
                      { key: 'all',     label: `All (${allSpares.length})` },
                      { key: 'players', label: `🏒 Players (${sparePlayerCount})` },
                      { key: 'goalies', label: `🥅 Goalies (${spareGoalieCount})` },
                    ].map(tab => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setSpareInviteFilter(tab.key)}
                        className="flex-1 text-xs font-semibold py-1.5 rounded-lg transition-all"
                        style={{
                          background: spareInviteFilter === tab.key ? 'var(--ice)' : 'transparent',
                          color: spareInviteFilter === tab.key ? '#0a0e14' : 'var(--text-2)',
                          border: 'none', cursor: 'pointer',
                        }}>
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* Select all / counts */}
                <div className="flex items-center justify-between mb-2 pb-2"
                  style={{ borderBottom: '1px solid var(--border)' }}>
                  <button
                    type="button"
                    onClick={toggleAll}
                    disabled={sparePickerSending || filteredEmailable.length === 0}
                    className="text-xs font-semibold"
                    style={{
                      color: filteredEmailable.length === 0 ? 'var(--text-3)' : 'var(--ice)',
                      background: 'none', border: 'none',
                      cursor: filteredEmailable.length === 0 ? 'default' : 'pointer',
                      padding: '4px 0',
                    }}>
                    {pickedCount === filteredEmailable.length && filteredEmailable.length > 0
                      ? 'Deselect all'
                      : 'Select all'}
                    {' '}({filteredEmailable.length})
                  </button>
                  <span className="text-xs" style={{ color: 'var(--text-2)' }}>
                    {noResponseCount > 0 && (
                      <span>🤔 {noResponseCount} no response</span>
                    )}
                  </span>
                </div>

                {/* Spare list — scrollable if long */}
                <div
                  className="mb-3"
                  style={{ maxHeight: 320, overflowY: 'auto', borderRadius: 8 }}>
                  {filteredSpares.map(s => {
                    const rowKey = s.userId || `invite:${s.inviteId}` || `member:${s.memberId}`;
                    // Apr 30 2026 (#23) — non-emailable rows render
                    // with a different action affordance:
                    //   • Pending invite (inviteId)  → "Mark In" button
                    //   • Placeholder (memberId)     → notice only
                    if (!s.isEmailable) {
                      return (
                        <div
                          key={rowKey}
                          className="flex items-center gap-3 py-2 px-2"
                          style={{
                            background: 'var(--surface-2)',
                            borderRadius: 8,
                            opacity: sparePickerSending ? 0.6 : 1,
                            marginTop: 2,
                          }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 18, height: 18, flexShrink: 0,
                            color: 'var(--text-3)', fontSize: 12,
                          }} title="No email — can't be invited via Game Day">
                            📋
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>
                              {s.name || 'Unnamed'}
                            </div>
                            <div className="text-xs" style={{ color: 'var(--text-3)' }}>
                              {s.isPending
                                ? 'Pending invite — no email on file'
                                : 'Placeholder — no Game Day account'}
                            </div>
                          </div>
                          {s.isPending && s.inviteId && s.status !== 'in' ? (
                            <button
                              type="button"
                              onClick={() => checkInPendingInvite(s)}
                              disabled={sparePickerSending}
                              className="text-xs font-semibold flex-shrink-0 px-2.5 py-1 rounded-lg"
                              style={{
                                background: 'rgba(0,232,122,0.10)',
                                color: 'var(--goal)',
                                border: '1px solid rgba(0,232,122,0.30)',
                                cursor: 'pointer',
                              }}>
                              ✓ Mark In
                            </button>
                          ) : (
                            <span className="text-xs font-semibold flex-shrink-0"
                              style={{ color: statusBadge(s.status).color }}>
                              {statusBadge(s.status).emoji} {statusBadge(s.status).label}
                            </span>
                          )}
                        </div>
                      );
                    }
                    const picked = sparePickerIds.has(s.userId);
                    const badge = statusBadge(s.status);
                    return (
                      <label
                        key={rowKey}
                        className="flex items-center gap-3 py-2 px-2 cursor-pointer"
                        style={{
                          background: picked ? 'var(--ice-a8)' : 'transparent',
                          borderRadius: 8,
                          opacity: sparePickerSending ? 0.6 : 1,
                        }}>
                        <input
                          type="checkbox"
                          checked={picked}
                          onChange={() => toggleOne(s.userId)}
                          disabled={sparePickerSending}
                          style={{ flexShrink: 0 }}
                        />
                        <span className="flex-1 text-sm font-medium truncate"
                          style={{ color: 'var(--text)' }}>
                          {s.name || 'Unnamed'}
                        </span>
                        <span className="text-xs font-semibold flex-shrink-0"
                          style={{ color: badge.color }}>
                          {badge.emoji} {badge.label}
                        </span>
                      </label>
                    );
                  })}
                </div>

                {/* Selection summary */}
                <div
                  className="text-xs mb-4 px-3 py-2 rounded-lg"
                  style={{
                    background: pickedCount > 0 ? 'var(--ice-a5)' : 'rgba(255,149,0,0.08)',
                    border: `1px solid ${pickedCount > 0 ? 'var(--ice-a15)' : 'rgba(255,149,0,0.2)'}`,
                    color: pickedCount > 0 ? 'var(--ice)' : 'var(--puck)',
                    fontWeight: 600,
                  }}>
                  {pickedCount > 0
                    ? `✉ Will send to ${pickedCount} spare${pickedCount !== 1 ? 's' : ''}`
                    : '⚠ Pick at least one spare to nudge'}
                </div>
              </>
            )}

            {/* 2026-09-17 (per Gilles, Phase 6) — PRICING PANEL replaces the
                lone "Invite for free" toggle. Shows the season/team fee for
                players and goalies separately, lets the coach change either
                for THIS game, and shows the money math (what the spare pays →
                system fees → what lands in the coach's account). If the team
                can't charge (Stripe not connected, or payment/spare-pay off)
                it says so and every invite is free. */}
            {(() => {
              const ts = game?.teamSettings || {};
              const canCharge = spareChargingPossible();
              const ccy = String(ts.payment_currency || 'CAD').toUpperCase();
              if (!canCharge) {
                const why = !ts.payment_enabled ? 'Payments are turned off for this team'
                  : !ts.spare_pay_required ? 'Spares aren\'t required to pay on this team'
                  : 'Stripe isn\'t connected for this team yet';
                return (
                  <div className="rounded-xl p-3 mb-3" style={{ background: 'rgba(255,149,0,0.08)', border: '1px solid rgba(255,149,0,0.35)' }}>
                    <div className="text-sm font-bold" style={{ color: 'var(--puck)' }}>⚠️ Spares will be invited for free</div>
                    <div className="text-xs mt-1" style={{ color: 'var(--text-2)', lineHeight: 1.5 }}>
                      {why}, so no spare fee can be collected for this game. To charge spares, finish
                      Stripe setup and turn on spare payments under <Link to={`/team/${teamId}/settings/payouts`} style={{ color: 'var(--ice)', fontWeight: 700 }}>Team Settings → Stripe</Link> and <Link to={`/team/${teamId}/settings/pricing`} style={{ color: 'var(--ice)', fontWeight: 700 }}>Pricing</Link>.
                    </div>
                  </div>
                );
              }
              const rows = [
                { key: 'skater', label: '🏒 Players', value: sparePickerFeeSkater, set: setSparePickerFeeSkater, def: teamDefaultSkaterCents() },
                { key: 'goalie', label: '🥅 Goalies', value: sparePickerFeeGoalie, set: setSparePickerFeeGoalie, def: teamDefaultGoalieCents() },
              ];
              return (
                <div className="rounded-xl mb-3" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', padding: '10px 12px' }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-bold">💳 Spare fee for this game</div>
                    <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: sparePickerFree ? 'var(--zen, #7dd3b8)' : 'var(--text-2)' }}>
                      <input type="checkbox" checked={sparePickerFree} disabled={sparePickerSending}
                        onChange={e => setSparePickerFree(e.target.checked)} />
                      🎁 Invite for free instead
                    </label>
                  </div>
                  {sparePickerFree ? (
                    <div className="text-xs" style={{ color: 'var(--text-2)' }}>
                      You're covering the cost — the email won't mention any spare fee.
                    </div>
                  ) : rows.map(r => {
                    const cents = dollarsToCents(r.value);
                    const m = spareFeeMath(cents);
                    const changed = cents !== r.def;
                    return (
                      <div key={r.key} className="py-2" style={{ borderTop: '1px solid var(--border)' }}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold" style={{ minWidth: 90 }}>{r.label}</span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs" style={{ color: 'var(--text-3)' }}>$</span>
                            <input type="number" inputMode="decimal" min="0" step="0.25"
                              value={r.value}
                              disabled={sparePickerSending}
                              onChange={e => r.set(e.target.value)}
                              className="input-field"
                              style={{ width: 92, padding: '4px 8px', fontSize: 13 }} />
                            <span className="text-xs" style={{ color: 'var(--text-3)' }}>{ccy}</span>
                          </div>
                          {changed ? (
                            <button type="button" onClick={() => r.set((r.def / 100).toFixed(2))}
                              className="text-xs" style={{ color: 'var(--ice)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
                              ↺ team default {fmtC(r.def)}
                            </button>
                          ) : (
                            <span className="text-xs" style={{ color: 'var(--text-3)' }}>team default</span>
                          )}
                        </div>
                        {cents > 0 ? (
                          <div className="text-xs mt-1.5" style={{ color: 'var(--text-2)', lineHeight: 1.6 }}
                            title={m.pass
                              ? 'The platform fee and Stripe processing are added on top of your price, so the spare pays a bit more and you net your full price.'
                              : 'You absorb the platform fee and Stripe processing out of your price.'}>
                            Spare pays <strong style={{ color: 'var(--text)' }}>{fmtC(m.spare)}</strong>
                            {' → '}system fees <strong style={{ color: 'var(--puck)' }}>−{fmtC(m.stripe + m.platform)}</strong>
                            <span style={{ color: 'var(--text-3)' }}> (Stripe ≈{fmtC(m.stripe)} + Game Day {fmtC(m.platform)})</span>
                            {' → '}<strong style={{ color: 'var(--goal)' }}>{fmtC(m.net)} to you</strong>
                          </div>
                        ) : (
                          <div className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>
                            {r.key === 'goalie' ? 'Goalies play free.' : 'Free for this game.'}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {!sparePickerFree && (
                    <div className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>
                      Changing a price here applies to this game only (every checkout and pay link for it uses the same number).
                    </div>
                  )}
                </div>
              );
            })()}

            {/* 2026-07-10 (batch-B C2, coach request) — optional custom
                note that rides inside the invite email, so coaches stop
                sending a second follow-up message with the details. */}
            <div style={{ margin: '0 0 12px' }}>
              <label className="text-xs font-semibold" style={{ color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>
                Add a note (optional — included in the email)
              </label>
              <textarea
                value={sparePickerNote}
                onChange={e => setSparePickerNote(e.target.value.slice(0, 500))}
                disabled={sparePickerSending}
                rows={3}
                placeholder={'e.g. "We\'re short on D — full equipment, black jersey if you have one. Beers after!"'}
                className="input-field"
                style={{ width: '100%', resize: 'vertical', fontSize: 13 }}
              />
              {sparePickerNote.length >= 450 && (
                <p className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>{500 - sparePickerNote.length} characters left</p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { if (!sparePickerSending) setSparePickerOpen(false); }}
                className="btn-ghost flex-1 justify-center"
                disabled={sparePickerSending}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => sendSpareRequests(filteredPickedIds)}
                disabled={sparePickerSending || pickedCount === 0}
                className="btn-ice flex-1 justify-center">
                {sparePickerSending
                  ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Sending…</>
                  : <><Mail size={14} /> Invite {pickedCount}</>}
              </button>
            </div>
          </Modal>
        );
      })()}

      {/* ── Group Message modal (game-scoped "Email Players") ──────────
          Roster-aware variant of the compose dialog. The coach picks a
          scope (checked-in / everyone / waitlist-only / by classification)
          and we resolve that to a concrete userIds list before sending.
          This avoids server-side game-scope filters and makes the recipient
          count visible to the coach *before* they hit Send. ── */}
      {groupMsgOpen && (() => {
        // 2026-07-17 — candidate resolution now lives in the
        // groupMsgCandidates memo (hoisted to component top level so the
        // preview-fetch effect can also read it — hooks can't reach into
        // this IIFE). This IIFE just consumes the result.
        const { candidates, isFTResponseScope } = groupMsgCandidates;
        // Still needed here (unrelated to candidate resolution above) —
        // the "Pick Players" search box filters this full roster by name
        // and shows each row's current RSVP status emoji, independent of
        // groupMsgScope/inScope.
        const roster = (game.all_members || []).filter(m => m.userId);
        const avById = {};
        for (const a of availability) {
          if (a.userId) avById[a.userId] = a.status;
        }

        // Build the target set with names for the "Pick Players" scope
        // and for the client-side fallback shown if the server preview
        // call errors out. game.all_members provides already-decrypted
        // names (the backend masks/decrypts in the same SELECT that
        // populates this list), so we can use `m.name` directly.
        const targetsSafe = candidates.map(m => ({
          userId: m.userId,
          name: m.name || 'Player',
        }));

        return (
          <Modal
            onClose={() => { if (!groupMsgSending) setGroupMsgOpen(false); }}
            title="Message Players">
            <div className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
              Sent as an email from Game Day. Replies come directly to your inbox.
            </div>

            {/* Scope picker */}
            <label className="label">Send to</label>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {/* 2026-09-17 (per Gilles, Phase 4) — six audiences, all
                  resolved server-side over the whole mini-game session:
                  Checked in (no waitlist) · Checked in + waitlist ·
                  Waitlist only · Everyone · Full-time no response ·
                  Invited, no response. "Responded" and the "also include
                  waitlist" checkbox are gone. */}
              {[
                { key: 'checked_in',         label: 'Checked in',            emoji: '✅', desc: 'Marked in — not the waitlist' },
                { key: 'checked_in_wait',    label: 'Checked in + waitlist', emoji: '✅', desc: 'Everyone in, plus the waitlist' },
                { key: 'waitlist_only',      label: 'Waitlist only',         emoji: '⏳', desc: 'Just the bench' },
                { key: 'everyone',           label: 'Everyone',              emoji: '👥', desc: 'Full roster, incl. pending invites' },
                { key: 'ft_no_response',     label: 'Full-time — no response', emoji: '🤔', desc: "Full-timers who haven't replied" },
                { key: 'invited_no_response',label: 'Invited — no response', emoji: '📨', desc: 'Anyone asked to play who said nothing' },
                // Apr 30 2026 — pick specific players via search.
                // Spans both columns so it sits prominently below the
                // four group presets. Use case: coach wants to message
                // just the goalies, or just the line-1 forwards, or
                // just one player without leaving the modal.
                { key: 'specific',      label: 'Pick Players',  emoji: '🎯', desc: 'Search & select', span: true },
              ].map(opt => {
                const active = groupMsgScope === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setGroupMsgScope(opt.key)}
                    disabled={groupMsgSending}
                    className="flex flex-col items-start gap-0.5 py-2 px-3 rounded-lg text-xs font-semibold transition-all text-left"
                    style={{
                      background: active ? 'var(--ice-a12)' : 'var(--surface-2)',
                      border: `1.5px solid ${active ? 'var(--ice-a30)' : 'var(--border)'}`,
                      color: active ? 'var(--ice)' : 'var(--text-2)',
                      cursor: groupMsgSending ? 'not-allowed' : 'pointer',
                      gridColumn: opt.span ? '1 / -1' : undefined,
                    }}>
                    <span><span style={{ marginRight: 4 }}>{opt.emoji}</span>{opt.label}</span>
                    <span className="text-xs" style={{ color: 'var(--text-3)', fontWeight: 500 }}>
                      {opt.desc}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Apr 30 2026 — specific-player picker. Visible only when
                the scope is set to 'specific'. Search input filters the
                roster by name; each row toggles the userId in the
                groupMsgPickedIds Set. Selected count shown above the
                list. Capped at ~280px tall with scroll so a 30-player
                roster doesn't push the message body off-screen. */}
            {groupMsgScope === 'specific' && (
              <div className="mb-3">
                <input
                  type="text"
                  value={groupMsgSearch}
                  onChange={e => setGroupMsgSearch(e.target.value)}
                  placeholder="🔍 Search by name…"
                  className="input-field mb-2"
                  disabled={groupMsgSending}
                  style={{ fontSize: 13 }}
                />
                {(() => {
                  const q = groupMsgSearch.trim().toLowerCase();
                  // 2026-09-17 (per Gilles) — sorted: checked in first, then
                  // waitlist, then no response, then checked out at the
                  // bottom; alphabetical within each band.
                  const bandOf = (m) => {
                    const st = avById[m.userId];
                    return st === 'in' ? 0
                      : (st === 'waitlist' || st === 'pending_payment') ? 1
                      : st === 'out' ? 3
                      : 2;
                  };
                  const filtered = roster.filter(m => {
                    if (!q) return true;
                    return (m.name || '').toLowerCase().includes(q);
                  }).sort((a, b) => bandOf(a) - bandOf(b) || (a.name || '').localeCompare(b.name || ''));
                  const pickedCount = groupMsgPickedIds.size;
                  return (
                    <>
                      <div className="flex items-center justify-between text-xs mb-2 px-1">
                        <span style={{ color: 'var(--text-2)' }}>
                          {pickedCount === 0
                            ? 'Tap players to add them'
                            : `${pickedCount} selected`}
                        </span>
                        {pickedCount > 0 && (
                          <button
                            type="button"
                            onClick={() => setGroupMsgPickedIds(new Set())}
                            disabled={groupMsgSending}
                            className="text-xs"
                            style={{
                              color: 'var(--ice)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '2px 6px',
                              fontWeight: 600,
                            }}>
                            Clear all
                          </button>
                        )}
                      </div>
                      <div
                        style={{
                          maxHeight: 280,
                          overflowY: 'auto',
                          border: '1px solid var(--border)',
                          borderRadius: 10,
                          background: 'var(--surface-2)',
                        }}>
                        {filtered.length === 0 ? (
                          <div className="text-xs text-center p-4" style={{ color: 'var(--text-3)' }}>
                            No players match "{q}"
                          </div>
                        ) : (
                          filtered.map((m, idx) => {
                            const isPicked = groupMsgPickedIds.has(m.userId);
                            const status = avById[m.userId];
                            const statusEmoji = status === 'in' ? '✅'
                              : status === 'out' ? '❌'
                              : status === 'waitlist' || status === 'pending_payment' ? '⏳'
                              : '·';
                            return (
                              <div
                                key={m.userId || m.inviteId || idx}
                                onClick={() => {
                                  if (groupMsgSending) return;
                                  setGroupMsgPickedIds(prev => {
                                    const next = new Set(prev);
                                    if (next.has(m.userId)) next.delete(m.userId);
                                    else next.add(m.userId);
                                    return next;
                                  });
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 10,
                                  padding: '9px 12px',
                                  borderBottom: idx < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                                  cursor: groupMsgSending ? 'not-allowed' : 'pointer',
                                  background: isPicked ? 'var(--ice-a8)' : 'transparent',
                                  transition: 'background 0.12s',
                                  WebkitTapHighlightColor: 'transparent',
                                }}>
                                <input
                                  type="checkbox"
                                  checked={isPicked}
                                  readOnly
                                  style={{ accentColor: 'var(--ice)', pointerEvents: 'none' }}
                                />
                                <span style={{ fontSize: 12, opacity: 0.7 }}>{statusEmoji}</span>
                                <span className="text-sm" style={{ color: isPicked ? 'var(--ice)' : 'var(--text)', fontWeight: isPicked ? 600 : 400 }}>
                                  {m.name || 'Player'}
                                </span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Include-waitlist toggle (only relevant for "checked in").
                2026-07-16 — this deliberately does NOT extend to the
                ft_no_response / ft_invited_no_response scopes: both
                exclude waitlisted players by definition (they've
                already engaged), so the toggle simply doesn't render
                for them rather than offering a checkbox that would be
                silently ignored. */}
            {/* 2026-09-17 — the "Also include waitlist" checkbox is gone;
                "Checked in + waitlist" is its own audience now. */}

            {/* Recipient count + preview strip.
                2026-07-17 (coach requirement) — this used to show an
                approximate "~N likely qualify" banner for the two
                FT-no-response scopes and a client-computed list for
                everything else. Now EVERY scope (except "Pick Players",
                which is already exact) shows the real, server-resolved
                name list via POST /teams/:teamId/messages/preview — see
                the debounced fetch effect near the top of this component.
                This can never drift from what /send actually does because
                both endpoints run the same resolveRecipients() helper. */}
            {groupMsgScope === 'specific' ? (
              // "Pick Players" — the coach's own selections are already
              // exact, so skip the network round trip; same list
              // component as the server-resolved scopes for a consistent
              // look.
              <GroupMessageRecipientPreview
                total={targetsSafe.length}
                items={targetsSafe.map(t => ({ userId: t.userId, name: t.name, isInvite: false }))}
                loading={false}
                expanded={groupMsgPreviewExpanded}
                onToggleExpand={() => setGroupMsgPreviewExpanded(e => !e)}
                emptyLabel="No players picked yet"
              />
            ) : groupMsgPreview.error ? (
              // Preview call failed — fall back to the old client-side
              // estimate so the modal never blocks sending. /send still
              // resolves the real audience itself regardless of whether
              // this preview succeeded, so this is a display-only fallback.
              isFTResponseScope ? (
                <div
                  className="text-xs mb-4 px-3 py-2 rounded-lg"
                  style={{ background: 'var(--ice-a5)', border: '1px solid var(--ice-a15)', color: 'var(--ice)' }}>
                  <div className="font-semibold mb-1">
                    ✉ ~{targetsSafe.length} player{targetsSafe.length !== 1 ? 's' : ''} likely qualify
                  </div>
                  <div style={{ color: 'var(--text-2)', lineHeight: 1.5, fontWeight: 400 }}>
                    Exact list (incl. mini-game sessions and pending invites) is confirmed when you hit Send.
                    <br />Couldn't reach the server to confirm names in advance.
                  </div>
                </div>
              ) : (
                <div
                  className="text-xs mb-4 px-3 py-2 rounded-lg"
                  style={{
                    background: targetsSafe.length > 0 ? 'var(--ice-a5)' : 'rgba(255,149,0,0.08)',
                    border: `1px solid ${targetsSafe.length > 0 ? 'var(--ice-a15)' : 'rgba(255,149,0,0.2)'}`,
                    color: targetsSafe.length > 0 ? 'var(--ice)' : 'var(--puck)',
                  }}>
                  {targetsSafe.length > 0 ? (
                    <>
                      <div className="font-semibold mb-1">
                        ✉ {targetsSafe.length} recipient{targetsSafe.length !== 1 ? 's' : ''} (estimated)
                      </div>
                      <div style={{ color: 'var(--text-2)', lineHeight: 1.5, fontWeight: 400 }}>
                        {targetsSafe.slice(0, 6).map(t => t.name).join(', ')}
                        {targetsSafe.length > 6 && ` + ${targetsSafe.length - 6} more`}
                        <br />Couldn't confirm the exact list from the server.
                      </div>
                    </>
                  ) : (
                    '⚠ No players match — pick a different scope'
                  )}
                </div>
              )
            ) : (
              <GroupMessageRecipientPreview
                total={groupMsgPreview.total}
                items={groupMsgPreview.recipients}
                loading={groupMsgPreview.loading}
                expanded={groupMsgPreviewExpanded}
                onToggleExpand={() => setGroupMsgPreviewExpanded(e => !e)}
                emptyLabel="No players match — pick a different scope"
              />
            )}

            <label className="label">Subject</label>
            <input
              type="text"
              value={groupMsgSubject}
              onChange={e => setGroupMsgSubject(e.target.value)}
              maxLength={200}
              placeholder="Subject line"
              className="input-field mb-3"
              disabled={groupMsgSending}
            />

            <label className="label">Message</label>
            <textarea
              value={groupMsgBody}
              onChange={e => setGroupMsgBody(e.target.value)}
              maxLength={4000}
              rows={7}
              placeholder="Type your message here…"
              className="input-field mb-1"
              // v2.5.185 — see per-recipient composer; same mobile clip fix.
              style={{ resize: 'vertical', minHeight: 140, height: 140, display: 'block', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5 }}
              disabled={groupMsgSending}
            />
            <div className="text-xs text-right mb-4" style={{ color: 'var(--text-3)', fontSize: 10 }}>
              {groupMsgBody.length}/4000
            </div>

            {/* ── Channel selector (Apr 2026, Tom_Notes B8) ──
                Same pattern as the per-recipient compose modal. */}
            <label className="label">Send via</label>
            {/* 2026-09-17 (Phase 4) — shared picker: Player preferences
                (default) / Email / Push / SMS (greyed unless the team plan
                has SMS). "In-app" folded into Push. */}
            <MessageChannelPicker
              value={groupMsgChannels}
              onChange={setGroupMsgChannels}
              smsEnabled={(outletCtx?.teams || []).find(t => t.id === teamId)?.sms_enabled === true}
              disabled={groupMsgSending}
            />

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { if (!groupMsgSending) setGroupMsgOpen(false); }}
                className="btn-ghost flex-1 justify-center"
                disabled={groupMsgSending}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => sendGroupMessage(
                  // 2026-07-16 — FT-no-response scopes send `group` +
                  // gameId so the backend resolves the real audience
                  // (mini-game sessions + pending invites) instead of the
                  // client's same-game-only targetsSafe approximation.
                  isFTResponseScope
                    ? { group: groupMsgScope }
                    : { userIds: targetsSafe.map(t => t.userId) }
                )}
                disabled={groupMsgSending || !groupMsgSubject.trim() || !groupMsgBody.trim() || (!isFTResponseScope && targetsSafe.length === 0)}
                className="btn-ice flex-1 justify-center">
                {groupMsgSending
                  ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Sending…</>
                  : isFTResponseScope
                    ? <><Mail size={14} /> Send{groupMsgPreview.total != null && !groupMsgPreview.loading ? ` to ${groupMsgPreview.total}` : ''}</>
                    : <><Mail size={14} /> Send to {targetsSafe.length}</>}
              </button>
            </div>
          </Modal>
        );
      })()}

      {/* ── Chunk 7 (Apr 2026) — edit team label modal ──
          Coach taps the Home/Away heading on the balanced teams
          card; this modal takes a rename (or lets them clear back
          to default). Empty input clears the override. Capped at
          40 chars client-side to match the backend column width. */}
      {labelEdit && (
        // Mobile note: was `inset-0` (bottom anchored to the LAYOUT
        // viewport), same iOS-keyboard-cutoff bug the shared <Modal>
        // had before v2.5.86 (see its comment above) — this hand-rolled
        // modal never got that fix. Dropping the bottom anchor and
        // sizing from --mobile-h keeps the rename input reachable when
        // the keyboard is up.
        <div className="fixed left-0 right-0 top-0 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ zIndex: 500, height: 'var(--mobile-h, 100dvh)' }}
          onClick={e => { if (e.target === e.currentTarget && !labelSaving) setLabelEdit(null); }}>
          <div className="glass w-full sm:max-w-md animate-slide-up"
            style={{
              borderRadius: '16px 16px 0 0',
              padding: '24px 18px calc(env(safe-area-inset-bottom, 0px) + 18px)',
              position: 'relative',
            }}>
            <ModalCloseButton onClose={() => setLabelEdit(null)} disabled={labelSaving} />
            <h2 className="section-title text-xl mb-2" style={{ paddingRight: 44 }}>
              Rename {labelEdit.key === 'home' ? 'Home' : 'Away'} Team
            </h2>
            <p className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
              Rename for this game only. Leave blank to revert to the default
              "{labelEdit.key === 'home' ? 'Home' : 'Away'}".
            </p>
            <input
              type="text"
              className="input-field"
              autoFocus
              maxLength={40}
              placeholder={labelEdit.key === 'home' ? 'e.g. Dark Side' : 'e.g. Light Brigade'}
              value={labelEdit.value}
              onChange={e => setLabelEdit({ ...labelEdit, value: e.target.value })}
              onKeyDown={e => {
                if (e.key === 'Enter' && !labelSaving) saveTeamLabel();
                if (e.key === 'Escape' && !labelSaving) setLabelEdit(null);
              }}
            />
            <div className="flex gap-2 mt-4">
              <button type="button" onClick={() => setLabelEdit(null)}
                disabled={labelSaving}
                className="btn-ghost flex-1 justify-center text-sm">
                Cancel
              </button>
              <button type="button" onClick={saveTeamLabel}
                disabled={labelSaving}
                className="btn-ice flex-1 justify-center text-sm">
                {labelSaving ? <div className="spinner" style={{ width: 14, height: 14 }} /> : 'Save'}
              </button>
            </div>
            <div className="sm:hidden" aria-hidden style={{ height: 70 }} />
          </div>
        </div>
      )}

      {/* ── Chunk 7 (Apr 2026) — roster swap modal ──
          Coach taps a player in the balanced teams list; this modal
          offers "Move to <other team>" as the primary action. Dismisses
          on backdrop tap or Cancel. */}
      {rosterSwap && (
        // Mobile note: same inset-0/--mobile-h fix as the label-edit
        // modal above — kept consistent even though this modal has no
        // text input, so all three hand-rolled modals behave alike.
        <div className="fixed left-0 right-0 top-0 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ zIndex: 500, height: 'var(--mobile-h, 100dvh)' }}
          onClick={e => { if (e.target === e.currentTarget && !rosterSwapBusy) setRosterSwap(null); }}>
          <div className="glass w-full sm:max-w-md animate-slide-up"
            style={{
              borderRadius: '16px 16px 0 0',
              padding: '24px 18px calc(env(safe-area-inset-bottom, 0px) + 18px)',
              position: 'relative',
            }}>
            <ModalCloseButton onClose={() => setRosterSwap(null)} disabled={rosterSwapBusy} />
            <h2 className="section-title text-xl mb-1" style={{ paddingRight: 44 }}>
              Move {rosterSwap.player.name}
            </h2>
            <p className="text-xs text-secondary mb-4" style={{ lineHeight: 1.5 }}>
              Currently on <strong>{rosterSwap.fromLabel}</strong>. Choose where to move them.
            </p>
            {(() => {
              const targetLabel = rosterSwap.from === 'home'
                ? (game?.away_team_label || 'Away')
                : (game?.home_team_label || 'Home');
              return (
                <div className="flex gap-2">
                  <button type="button" onClick={() => setRosterSwap(null)}
                    disabled={rosterSwapBusy}
                    className="btn-ghost flex-1 justify-center text-sm">
                    Cancel
                  </button>
                  <button type="button" onClick={doRosterSwap}
                    disabled={rosterSwapBusy}
                    className="btn-ice flex-1 justify-center text-sm">
                    {rosterSwapBusy
                      ? <div className="spinner" style={{ width: 14, height: 14 }} />
                      : <>Move to {targetLabel} →</>}
                  </button>
                </div>
              );
            })()}
            <div className="sm:hidden" aria-hidden style={{ height: 70 }} />
          </div>
        </div>
      )}

      {/* ── FCFS (Apr 2026) — open / close modal ──
          Coach-only. Shown when user clicks "Accept Any Spare that Pays".
          Two states:
            - No active window → form to open (spots + recipients radio)
            - Active window → close button + status
      */}
      {fcfsOpen && (
        // Mobile note: same inset-0/--mobile-h fix as the label-edit
        // modal above. This one also has a number input (fcfsSpots),
        // so the keyboard-cutoff bug applied here too. maxHeight also
        // switched from a raw 90vh (doesn't shrink for the keyboard,
        // same Safari 17+ bug referenced in the shared Modal) to the
        // --mobile-h-relative pattern used app-wide (see
        // finance/SparesTab.jsx ~line 836).
        <div className="fixed left-0 right-0 top-0 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ zIndex: 500, height: 'var(--mobile-h, 100dvh)' }}
          onClick={e => { if (e.target === e.currentTarget && !fcfsBusy) setFcfsOpen(false); }}>
          <div className="glass w-full sm:max-w-lg animate-slide-up"
            style={{
              borderRadius: '16px 16px 0 0',
              padding: '24px 18px calc(env(safe-area-inset-bottom, 0px) + 18px)',
              maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)', overflowY: 'auto',
              position: 'relative',
            }}>
            <ModalCloseButton onClose={() => setFcfsOpen(false)} disabled={fcfsBusy} />
            {game?.fcfs_window_opened_at && !game?.fcfs_closed_at ? (
              <>
                <h2 className="section-title text-xl mb-2" style={{ paddingRight: 44 }}>First-come-first-pay Window</h2>
                <p className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
                  Active window: <strong>{game.fcfs_spots_available} spot{game.fcfs_spots_available === 1 ? '' : 's'}</strong>.
                  Spares are racing to pay. Close the window to stop new payments
                  (in-flight checkouts will still settle).
                </p>
                <div className="flex gap-2 mt-4">
                  <button type="button" onClick={() => setFcfsOpen(false)}
                    disabled={fcfsBusy}
                    className="btn-ghost flex-1 justify-center text-sm">
                    Keep Open
                  </button>
                  <button type="button" onClick={doFcfsClose}
                    disabled={fcfsBusy}
                    className="btn-penalty flex-1 justify-center text-sm">
                    {fcfsBusy ? <div className="spinner" style={{ width: 14, height: 14 }} /> : 'Close Window'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="section-title text-xl mb-2" style={{ paddingRight: 44 }}>Accept Any Spare that Pays</h2>
                <p className="text-xs text-secondary mb-4" style={{ lineHeight: 1.5 }}>
                  Opens a first-come-first-<strong style={{ color: 'var(--goal)' }}>pay</strong> window.
                  Selected spares get an email with a link to secure their spot. The first
                  spares to complete payment are confirmed — the rest get a "spots filled"
                  message at checkout. Waitlisters are emailed 5 minutes before
                  everyone else so they get a fair head start.
                </p>
                <div style={{ marginBottom: 16 }}>
                  <label className="label">How many open spots?</label>
                  <input type="number" min="1" max="30" className="input-field"
                    style={{ maxWidth: 160 }}
                    placeholder="e.g. 2"
                    value={fcfsSpots}
                    onChange={e => setFcfsSpots(e.target.value)} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label className="label">Who gets the email?</label>
                  {[
                    { k: 'all',            label: 'All spares',
                      desc: 'Waitlisters get the email now; everyone else 5 minutes later (gives waitlisters a head start).' },
                    { k: 'waitlist_only',  label: 'Waitlist only',
                      desc: 'Just spares currently on the waitlist for this game.' },
                  ].map(opt => {
                    const active = fcfsRecipients === opt.k;
                    return (
                      <div key={opt.k}
                        className="flex items-start gap-3 p-3 rounded-xl cursor-pointer"
                        style={{
                          background: active ? 'rgba(0,212,255,0.08)' : 'var(--surface-3)',
                          border: `1.5px solid ${active ? 'var(--ice-a30)' : 'var(--border)'}`,
                          marginTop: 8,
                        }}
                        onClick={() => setFcfsRecipients(opt.k)}>
                        <div style={{
                          width: 18, height: 18, borderRadius: 9, flexShrink: 0, marginTop: 2,
                          background: active ? 'var(--ice)' : 'var(--surface-3)',
                          border: `1.5px solid ${active ? 'var(--ice)' : 'var(--border)'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {active && <div style={{ width: 8, height: 8, borderRadius: 4, background: '#000' }} />}
                        </div>
                        <div style={{ flex: 1 }}>
                          <p className="text-sm font-semibold">{opt.label}</p>
                          <p className="text-xs text-secondary mt-0.5">{opt.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-2 mt-4">
                  <button type="button" onClick={() => setFcfsOpen(false)}
                    disabled={fcfsBusy}
                    className="btn-ghost flex-1 justify-center text-sm">
                    Cancel
                  </button>
                  <button type="button" onClick={doFcfsOpen}
                    disabled={fcfsBusy || !fcfsSpots}
                    className="btn-goal flex-1 justify-center text-sm">
                    {fcfsBusy ? <div className="spinner" style={{ width: 14, height: 14 }} /> : '🏁 Open Window & Send Emails'}
                  </button>
                </div>
              </>
            )}
            <div className="sm:hidden" aria-hidden style={{ height: 70 }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Drinks Duty Panel ─────────────────────────────────────────────────────────
function DrinksPanel({ teamId, gameId, isCoach, userId }) {
  const [drinks,      setDrinks]      = useState(undefined); // undefined = loading
  const [stats,       setStats]       = useState(null);
  const [teamSettings,setTeamSettings]= useState(null);
  const [acting,      setActing]      = useState(false);
  const [showAssign,  setShowAssign]  = useState(false);
  const [statsLoaded, setStatsLoaded] = useState(false);

  useEffect(() => {
    if (!teamId || !gameId) return;
    Promise.all([
      api.get(`/teams/${teamId}`).catch(() => ({ data: null })),
      api.get(`/teams/${teamId}/drinks/${gameId}`).catch(() => ({ data: null })),
    ]).then(([teamRes, drinksRes]) => {
      setTeamSettings(teamRes.data?.settings || {});
      setDrinks(drinksRes.data);   // null = no assignment yet; object = existing row
    }).catch(() => {
      setTeamSettings({});
      setDrinks(null);
    });
  }, [teamId, gameId]);

  // May 2 2026 — was: `if (statsLoaded) return;` short-circuit. After
  // assigning a user, the code calls loadStats() to refresh the
  // recommendation list (so the just-assigned person drops down the
  // ranking and someone else moves to #1). But the early-return meant
  // the second call was a no-op — coach saw the same person at #1
  // with the same Assign button, tapped again, and the system happily
  // assigned the same person to a SECOND game. Repeat → "drinks duty
  // keeps assigning the same person over and over."
  //
  // Fix: drop the cache. /drinks/stats is one query; refetching is
  // cheap relative to the wrong-data risk. statsLoaded retained as
  // an "ever fetched" flag for the loading spinner — different from
  // "is the cached data still fresh."
  const loadStats = async () => {
    try {
      const { data } = await api.get(`/teams/${teamId}/drinks/stats`);
      setStats(data);
      setStatsLoaded(true);
    } catch { toast.error('Failed to load player stats'); }
  };

  const refreshDrinks = async () => {
    try {
      const { data } = await api.get(`/teams/${teamId}/drinks/${gameId}`);
      setDrinks(data);
    } catch {}
  };

  // Not yet loaded or drinks feature disabled
  if (drinks === undefined || !teamSettings) return null;
  if (!teamSettings.drinks_enabled) return null;

  const hasAssigned  = drinks?.userId && drinks?.status !== 'ignored';
  const isIgnored    = drinks?.status === 'ignored';
  const isMe         = drinks?.userId === userId;
  const mode         = teamSettings.drinks_mode || 'volunteer';

  const volunteer = async () => {
    setActing(true);
    try {
      if (isMe) {
        // Apr 2026 — backend now allows self-removal even when
        // coach-assigned (it auto-notifies the coach so they know
        // to find a replacement). Surface that detail in the toast
        // so the player isn't surprised when the coach reaches
        // out — they should know we already pinged the coach for
        // them.
        const { data } = await api.delete(`/teams/${teamId}/drinks/${gameId}/volunteer`);
        if (data?.wasCoachAssigned) {
          toast.success('Removed — your coach has been notified to assign someone else');
        } else {
          toast.success('Removed from drinks duty');
        }
      } else {
        await api.post(`/teams/${teamId}/drinks/${gameId}/volunteer`);
        toast.success("🥤 You're on drinks!");
      }
      await refreshDrinks();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update drinks');
    }
    setActing(false);
  };

  // Apr 2026 — coach-side unassign. Hits the new DELETE endpoint
  // that fires a notification to the (now ex-) assignee. Used by
  // the new "Unassign" button below; separate from `ignore` (which
  // dismisses the alert without ever assigning anyone).
  const unassign = async () => {
    if (!hasAssigned) return;
    setActing(true);
    try {
      await api.delete(`/teams/${teamId}/drinks/${gameId}/assignment`);
      toast.success(`Removed ${drinks.name || 'player'} from drinks duty`);
      await refreshDrinks();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to unassign');
    }
    setActing(false);
  };

  const assign = async (assignUserId) => {
    setActing(true);
    try {
      await api.post(`/teams/${teamId}/drinks/${gameId}/assign`, { userId: assignUserId });
      toast.success('🥤 Drinks assigned!');
      await Promise.all([refreshDrinks(), loadStats()]);
      setShowAssign(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to assign');
    }
    setActing(false);
  };

  const ignore = async () => {
    setActing(true);
    try {
      await api.post(`/teams/${teamId}/drinks/${gameId}/ignore`);
      toast('Drinks alert dismissed for this game');
      setDrinks({ status: 'ignored' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
    setActing(false);
  };

  // v2.5.95 — recovery from the dismissed state. Hits the new
  // POST /un-ignore endpoint which deletes the row entirely, then
  // refreshes so the panel renders the no-row no-one-signed-up
  // state with Assign/Volunteer buttons available again.
  const unIgnore = async () => {
    setActing(true);
    try {
      await api.post(`/teams/${teamId}/drinks/${gameId}/un-ignore`);
      toast.success('🥤 Drinks panel restored — you can now assign someone');
      await refreshDrinks();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to recover drinks state');
    }
    setActing(false);
  };

  const openAssign = () => {
    setShowAssign(v => !v);
    loadStats();
  };

  return (
    <div className="card mb-4" style={{ borderColor: 'rgba(0,212,255,0.18)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 className="section-title text-lg">🥤 Drinks Duty</h2>
        {hasAssigned && (
          <span className="badge text-xs px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(0,232,122,0.12)', color: 'var(--goal)', border: '1px solid rgba(0,232,122,0.25)' }}>
            Covered ✓
          </span>
        )}
        {!hasAssigned && !isIgnored && (
          <span className="badge text-xs px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(255,59,59,0.12)', color: 'var(--penalty)', border: '1px solid rgba(255,59,59,0.25)' }}>
            Nobody assigned
          </span>
        )}
      </div>

      {/* Status line */}
      <div style={{ marginBottom: 14 }}>
        {hasAssigned ? (
          <p className="text-sm">
            <strong style={{ color: 'var(--goal)' }}>{drinks.name}</strong>{' '}
            {drinks.assignedBy ? 'was assigned by the coach' : 'volunteered'} to bring drinks.
            {isMe && <span style={{ color: 'var(--ice)', fontWeight: 600 }}> That's you! 🏆</span>}
          </p>
        ) : isIgnored ? (
          // v2.5.95 — improved dismissed-state messaging. Distinguishes
          // explicit coach dismissals (assignerName populated, not the
          // cron's signature) from cron-stamped rows (no assigner, but
          // coach_alert_sent=true). Coach feedback: "How is that
          // possible that the drinks were dismissed we never drinks."
          // The previous bare message provided no context — the user
          // couldn't tell whether someone explicitly dismissed (no,
          // only coaches can — the Dismiss button is gated server-
          // side AND client-side) or whether it was the v2.5.94 cron
          // bug. Now the message tells them.
          <div className="text-sm text-secondary">
            {drinks?.assignerName ? (
              <p>
                Drinks duty was <strong>dismissed by {drinks.assignerName}</strong>
                {drinks.updatedAt ? ` on ${new Date(drinks.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : ''}.
                {isCoach && ' Tap "Re-enable" below to restore the drinks panel and assign someone.'}
              </p>
            ) : (
              <>
                <p>This game's drinks state was auto-dismissed by the system.</p>
                {isCoach && (
                  <p style={{ marginTop: 6, fontSize: 12, color: 'var(--text-3)' }}>
                    Note: this state could only be set by a coach/manager (the Dismiss button is
                    gated on both client and server) — most likely a known bug fixed in v2.5.94.
                    Tap "Re-enable" below to restore the panel.
                  </p>
                )}
              </>
            )}
          </div>
        ) : (
          <p className="text-sm text-secondary">
            {mode === 'scheduled'
              ? 'No one has been scheduled for drinks yet — the coach can assign someone below.'
              : 'No one has signed up for drinks yet. Be the hero! 🦸'}
          </p>
        )}
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: (showAssign && stats) ? 16 : 0 }}>
        {/* Volunteer / "Remove myself" button.
            Apr 2026 — shown logic split:
              • volunteer mode: shown to anyone (player can volunteer
                if no one is on drinks; assignee can remove
                themselves if they were the volunteer).
              • scheduled mode: shown ONLY to the current assignee so
                they can step down. Other players don't volunteer in
                this mode — the coach picks. Without this branch
                someone who got coach-assigned in scheduled mode had
                NO way to back out.
            In both cases, the backend now sends a notification to
            coaches/managers when a coach-assigned player steps down,
            so the coach knows to find a replacement. */}
        {!isIgnored && (mode === 'volunteer' || isMe) && (
          <button
            onClick={volunteer}
            disabled={acting || (hasAssigned && !isMe)}
            className={isMe ? 'btn-ghost text-sm' : 'btn-ice text-sm'}
            style={{ opacity: (!isMe && hasAssigned) ? 0.45 : 1 }}
          >
            {acting && !showAssign
              ? '…'
              : isMe
              ? '✗ Remove myself'
              : hasAssigned
              ? '🥤 Taken'
              : '🥤 I\'ll bring drinks'}
          </button>
        )}

        {/* Coach: assign / change */}
        {isCoach && !isIgnored && (
          <button onClick={openAssign} className="btn-ghost text-sm">
            {showAssign ? 'Close' : hasAssigned ? '↻ Change' : 'Assign someone'}
          </button>
        )}

        {/* Coach: unassign — Apr 2026. Removes whoever is currently
            on drinks duty and notifies them. Only shown when there
            IS someone assigned; otherwise the "Dismiss" button
            below covers the no-one-assigned case. */}
        {isCoach && hasAssigned && (
          <button onClick={unassign} disabled={acting}
            className="text-sm font-semibold px-3 py-1.5 rounded-lg"
            style={{
              background: 'rgba(255,59,59,0.10)',
              border: '1px solid rgba(255,59,59,0.30)',
              color: 'var(--penalty)',
            }}>
            Unassign
          </button>
        )}

        {/* Coach: dismiss */}
        {isCoach && !hasAssigned && !isIgnored && (
          <button onClick={ignore} disabled={acting} className="btn-ghost text-sm"
            style={{ color: 'var(--text-2)' }}>
            Dismiss
          </button>
        )}

        {/* v2.5.95 — Coach: re-enable from dismissed state. The
            v2.5.94 cron bug left some games stuck with
            status='ignored' and no buttons (Volunteer, Assign,
            Dismiss are all hidden when isIgnored). This button is
            the recovery path: hits POST /un-ignore which deletes
            the row entirely, returning the panel to the no-row
            "no one has signed up yet" state where Assign reappears. */}
        {isCoach && isIgnored && (
          <button onClick={unIgnore} disabled={acting} className="btn-ice text-sm">
            ↻ Re-enable drinks
          </button>
        )}
      </div>

      {/* Assign panel — coach only */}
      {showAssign && (
        <div style={{
          marginTop: 4, padding: '14px 16px', borderRadius: 12,
          background: 'var(--surface-2)', border: '1px solid var(--border)',
        }}>
          {!stats ? (
            <p className="text-sm text-secondary">Loading player stats…</p>
          ) : (
            <>
              {/* ── Fairness summary banner (Apr 2026, Tom_Notes G3/G4) ──
                  Surfaces the team-level fair-share number plus a
                  count of who's still at zero. Coaches asked for a
                  one-glance "is this list balanced?" indicator. */}
              {stats.summary && stats.summary.totalGames > 0 && (
                <div className="mb-3 p-3 rounded-xl text-xs"
                  style={{
                    background: stats.summary.neverBroughtCount > 0
                      ? 'rgba(255,149,0,0.06)'
                      : 'rgba(0,232,122,0.06)',
                    border: `1px solid ${stats.summary.neverBroughtCount > 0
                      ? 'rgba(255,149,0,0.2)'
                      : 'rgba(0,232,122,0.2)'}`,
                  }}>
                  <div className="font-semibold" style={{ marginBottom: 4 }}>
                    📊 Fair share: {stats.summary.fairShare.toFixed(1)} per player
                  </div>
                  <div style={{ color: 'var(--text-2)', lineHeight: 1.4 }}>
                    {stats.summary.totalGames} drinks slot{stats.summary.totalGames !== 1 ? 's' : ''} across{' '}
                    {stats.summary.memberCount} active member{stats.summary.memberCount !== 1 ? 's' : ''}.
                    {stats.summary.neverBroughtCount > 0 && (
                      <>
                        {' '}<strong style={{ color: 'var(--puck)' }}>
                          {stats.summary.neverBroughtCount} {stats.summary.neverBroughtCount === 1 ? 'person has' : 'people have'} never brought
                        </strong>.
                      </>
                    )}
                    {stats.summary.neverBroughtCount === 0 && stats.summary.behindCount > 0 && (
                      <>
                        {' '}<strong>{stats.summary.behindCount}</strong> behind their share.
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Top 3 recommendations.
                  Apr 2026 (Tom_Notes G3/G4) — prefer the
                  deviation-based ranking when available. Falls back
                  to the legacy lowest-total list for older deploys. */}
              {(stats.recommendationsByDeviation || stats.recommendations)?.length > 0 && (
                <>
                  <p style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: 10,
                  }}>
                    🏅 Top picks — most behind on fair share
                  </p>
                  <div style={{ marginBottom: 14 }}>
                    {(stats.recommendationsByDeviation || stats.recommendations).map((r, i) => {
                      const lastLabel = r.daysSinceLast == null
                        ? 'Never brought'
                        : r.daysSinceLast === 0
                          ? 'Brought today'
                          : `${r.daysSinceLast}d ago`;
                      return (
                        <div key={r.userId} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '8px 0', borderBottom: '1px solid var(--border)',
                        }}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 14, fontWeight: 700 }}>
                              {['🥇','🥈','🥉'][i]} {r.name}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>
                              {r.historical} past · {r.upcoming} upcoming
                              {' · '}
                              <span style={{
                                color: r.daysSinceLast == null ? 'var(--puck)' : 'var(--text-2)',
                                fontWeight: r.daysSinceLast == null ? 600 : 400,
                              }}>
                                {lastLabel}
                              </span>
                              {r.deviation != null && r.deviation < 0 && (
                                <> · <span style={{ color: 'var(--puck)', fontWeight: 600 }}>
                                  owes {Math.abs(r.deviation)}
                                </span></>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => assign(r.userId)}
                            disabled={acting}
                            className="btn-ice text-xs"
                            style={{ padding: '5px 14px', flexShrink: 0 }}
                          >
                            {acting ? '…' : 'Assign'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Full roster list */}
              {stats.stats?.length > 3 && (
                <>
                  <p style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: 8,
                  }}>
                    All players
                  </p>
                  <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {stats.stats.slice(3).map(s => {
                      // Apr 2026 (G3/G4) — bucketise the deviation
                      // so the list scans quickly: one of three
                      // colour states ("ahead", "on track", "behind").
                      let devBadge = null;
                      if (s.deviation != null) {
                        if (s.deviation > 0) {
                          devBadge = { text: `+${s.deviation}`, color: 'var(--goal)', bg: 'rgba(0,232,122,0.10)' };
                        } else if (s.deviation < 0) {
                          devBadge = { text: `${s.deviation}`, color: 'var(--puck)',  bg: 'rgba(255,149,0,0.10)' };
                        } else {
                          devBadge = { text: '✓',              color: 'var(--text-2)', bg: 'var(--surface-2)' };
                        }
                      }
                      const lastLabel = s.daysSinceLast == null
                        ? 'never'
                        : s.daysSinceLast === 0
                          ? 'today'
                          : `${s.daysSinceLast}d`;
                      return (
                        <div key={s.userId} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '6px 0', borderBottom: '1px solid var(--border)', gap: 8,
                        }}>
                          <span style={{ fontSize: 13, minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {s.name}
                            <span style={{ color: 'var(--text-2)', fontSize: 11, marginLeft: 8 }}>
                              ({s.total} · {lastLabel})
                            </span>
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                            {devBadge && (
                              <span style={{
                                fontSize: 10, fontWeight: 700, padding: '2px 6px',
                                borderRadius: 6, background: devBadge.bg, color: devBadge.color,
                                minWidth: 28, textAlign: 'center',
                              }}>{devBadge.text}</span>
                            )}
                            <button
                              onClick={() => assign(s.userId)}
                              disabled={acting}
                              className="btn-ghost text-xs"
                              style={{ padding: '4px 10px' }}
                            >
                              {acting ? '…' : 'Assign'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// May 2026 — z-index bumped 50 → 500 so bottom-sheet content (Cancel /
// SEND / Jab All etc.) stops being hidden behind the mobile BottomNav,
// which is fixed at z-400. Same fix applied to the New Team modal in
// v2.5.41; this extends it to every dialog routed through GamePage's
// Modal (Send Reminder, Confirm Jab, Enter Result, Arena Change).
//
// Header is now a sticky flex row with an X close button on the right.
// Sticky inside the scrolling panel keeps the X reachable even on long
// lists like "Confirm Jab (16)" where the action buttons are far below
// the fold. Backdrop tap still works as a secondary dismiss.
//
// v2.5.46 — additionally a mobile-only bottom spacer reserves
// ~70px of clear space below the action area so Cancel/SEND/Jab All
// physically sit above where BottomNav lives, not just stacked over it.
// Without this, even with z-500 correctly applied, buttons painted
// "above the nav" still look visually clipped to the user. With the
// spacer, the action row is in genuinely clear space. Spacer is hidden
// on sm+ breakpoints (centered modal, no nav, no need for the buffer).
// ── BulkAssignModal (May 4 2026 / v2.5.69) ─────────────────────────────────
// The manual-create-teams picker. Rendered when team_creation_mode is
// 'manual' and the coach taps "Bulk Assign Players". Three buckets:
// Unassigned (initial), Home, Away. Tap a row's Home/Away/Unassigned
// chip to move it. Submit POSTs the final state to /roster/bulk-assign
// which atomically wipes any prior roster + writes the new one.
//
// Players list is derived from `inPlayers` (the GamePage's checked-in
// roster). Only checked-in players are pickable — that's the spec
// ("the players, who have checked in, to be assigned to the specific
// game"). Players without a userId (pending invites) are filtered out
// since the bulk-assign endpoint only accepts userIds.
//
// State: `assignment` is a Map<userId, 'home' | 'away' | null>. null
// means "leave unassigned". Submit collects the home/away arrays from
// the map and ignores nulls.
function BulkAssignModal({ inPlayers, onClose, onSubmit }) {
  // Filter to pickable players + initialize all to unassigned.
  // Memo so re-renders don't reset selections during the modal session.
  const pickable = (inPlayers || []).filter(p => p.userId);
  const [assignment, setAssignment] = useState(() => {
    const m = {};
    for (const p of pickable) m[p.userId] = null;
    return m;
  });
  const [submitting, setSubmitting] = useState(false);

  const setOne = (userId, team) => {
    setAssignment(prev => ({ ...prev, [userId]: team }));
  };
  const moveAll = (team) => {
    setAssignment(prev => {
      const next = { ...prev };
      for (const p of pickable) next[p.userId] = team;
      return next;
    });
  };

  // Buckets for display. Sort alphabetically within each so coach can
  // scan + spot mistakes.
  const byTeam = (team) => pickable
    .filter(p => assignment[p.userId] === team)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const homeList   = byTeam('home');
  const awayList   = byTeam('away');
  const unassigned = pickable
    .filter(p => assignment[p.userId] == null)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const onSubmitClick = async () => {
    if (homeList.length === 0 && awayList.length === 0) {
      toast.error('Pick at least one player for home or away');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(homeList.map(p => p.userId), awayList.map(p => p.userId));
    } finally {
      setSubmitting(false);
    }
  };

  // Row renderer. The chips visually communicate current state and
  // double as the action — tap "Home" to move there. The "active" chip
  // (matching this row's current assignment) is filled; others are
  // ghost. Tapping the active chip un-sets (sends to Unassigned).
  const renderRow = (p) => {
    const cur = assignment[p.userId];
    const Chip = ({ label, team, color }) => {
      const active = cur === team;
      return (
        <button
          type="button"
          onClick={() => setOne(p.userId, active ? null : team)}
          style={{
            padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
            background: active ? color : 'var(--surface-2)',
            border: `1px solid ${active ? color : 'var(--border)'}`,
            color: active ? '#fff' : 'var(--text-2)',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}>
          {label}
        </button>
      );
    };
    return (
      <div key={p.userId}
        className="flex items-center gap-2 px-3 py-2 rounded-lg"
        style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
        <span className="flex-1 text-sm font-medium truncate">{p.name || 'Player'}</span>
        <div className="flex gap-1.5">
          <Chip label="🏠 Home" team="home" color="#3a86ff" />
          <Chip label="🚌 Away" team="away" color="#ff6b35" />
        </div>
      </div>
    );
  };

  return (
    <Modal onClose={() => !submitting && onClose()} title="✋ Bulk Assign Players">
      <p className="text-sm text-secondary mb-3">
        Tap <strong>🏠 Home</strong> or <strong>🚌 Away</strong> on each
        player to assign them. Tap an active chip again to unassign.
        Players left unassigned won't be on either roster.
      </p>

      {/* Quick "move all" shortcuts. Helpful for coaches who want to
          start with everyone on one side then move outliers. ── */}
      {pickable.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-3">
          <button type="button" onClick={() => moveAll('home')}
            className="text-xs px-2.5 py-1 rounded-lg"
            style={{ background: 'rgba(58,134,255,0.10)', border: '1px solid rgba(58,134,255,0.30)', color: '#3a86ff', fontWeight: 700 }}>
            All → Home
          </button>
          <button type="button" onClick={() => moveAll('away')}
            className="text-xs px-2.5 py-1 rounded-lg"
            style={{ background: 'rgba(255,107,53,0.10)', border: '1px solid rgba(255,107,53,0.30)', color: '#ff6b35', fontWeight: 700 }}>
            All → Away
          </button>
          <button type="button" onClick={() => moveAll(null)}
            className="text-xs px-2.5 py-1 rounded-lg"
            style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-2)', fontWeight: 700 }}>
            Reset All
          </button>
        </div>
      )}

      {pickable.length === 0 && (
        <div className="p-4 rounded-xl text-center text-sm" style={{
          background: 'rgba(255,149,0,0.06)', border: '1px solid rgba(255,149,0,0.25)',
          color: 'var(--text-2)',
        }}>
          No players are checked in yet. Mark players IN first, then come back here.
        </div>
      )}

      <div className="max-h-[55dvh] overflow-y-auto space-y-3 mb-4">
        {/* Home section */}
        <div>
          <p className="text-xs font-bold mb-1.5" style={{
            textTransform: 'uppercase', letterSpacing: '0.06em', color: '#3a86ff',
          }}>
            🏠 Home ({homeList.length})
          </p>
          <div className="space-y-1.5">
            {homeList.length === 0
              ? <p className="text-xs text-secondary px-1">No players on Home yet.</p>
              : homeList.map(renderRow)}
          </div>
        </div>

        {/* Away section */}
        <div>
          <p className="text-xs font-bold mb-1.5" style={{
            textTransform: 'uppercase', letterSpacing: '0.06em', color: '#ff6b35',
          }}>
            🚌 Away ({awayList.length})
          </p>
          <div className="space-y-1.5">
            {awayList.length === 0
              ? <p className="text-xs text-secondary px-1">No players on Away yet.</p>
              : awayList.map(renderRow)}
          </div>
        </div>

        {/* Unassigned section */}
        {unassigned.length > 0 && (
          <div>
            <p className="text-xs font-bold mb-1.5" style={{
              textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)',
            }}>
              ⏳ Unassigned ({unassigned.length})
            </p>
            <div className="space-y-1.5">
              {unassigned.map(renderRow)}
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <button type="button" onClick={onClose} disabled={submitting}
          className="btn-ghost flex-1 justify-center">
          Cancel
        </button>
        <button type="button" onClick={onSubmitClick} disabled={submitting || (homeList.length === 0 && awayList.length === 0)}
          className="flex-1 justify-center"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 8, padding: '10px 18px',
            background: '#a78bfa', color: '#fff',
            fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            borderRadius: 12, border: 'none',
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.6 : 1,
          }}>
          {submitting
            ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Saving…</>
            : <>Assign {homeList.length + awayList.length} Players</>}
        </button>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  // 2026-09-16 — keyboard-safe sheet (coach report: "the message box
  // disappears when I tap it on mobile"). The sheet shrinks with the
  // keyboard (--mobile-h) but its scroll position didn't follow the
  // focused field, so a textarea at the bottom of a long list ended up
  // clipped below the sheet's new bottom edge. The hook re-scrolls the
  // focused input into view once the keyboard has settled and pins the
  // wrapper to the visible top on iOS Safari. See hooks/useKeyboardSafeSheet.js.
  const { sheetRef, vvTop } = useKeyboardSafeSheet();
  // May 4 2026 (v2.5.62) — body scroll lock while open. Coach
  // feedback: modals on mobile felt non-native because the underlying
  // page would horizontally drift during scroll. Locking body
  // overflow + touch-action keeps the page underneath stationary.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, []);
  return (
    <div className="fixed left-0 right-0 top-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{
        // May 8 2026 (v2.5.86) — was `inset-0` (top/right/bottom/left
        // all = 0). The `bottom: 0` part anchors the wrapper to the
        // bottom of the LAYOUT viewport, which on iOS Safari with
        // the keyboard up extends BEHIND the keyboard. items-end
        // then aligns the sheet to that hidden bottom, so the lower
        // half of the modal sits behind the keyboard and the user
        // can't reach the message textarea or Send button. Coach
        // report: "There's something wrong, the message screen is
        // getting cut off."
        //
        // Fix: drop the bottom anchor and instead set the wrapper's
        // height from --mobile-h, which MobileLayout updates live
        // from visualViewport.height (it shrinks when the keyboard
        // appears). items-end now aligns the sheet to the *visible*
        // bottom — just above the keyboard.
        height: 'var(--mobile-h, 100dvh)',
        // 2026-09-16 — follow the visual viewport's offset while an
        // input in this sheet is focused (iOS Safari scrolls the visual
        // viewport to reveal the field; a top:0 fixed wrapper would
        // otherwise be partly above the visible area). 0 elsewhere.
        top: vvTop,
        zIndex: 500,
        // May 4 2026 (v2.5.63) — touch-action: none blocks iOS
        // Safari's swipe-back/pull-to-refresh from triggering through
        // the dim area. See SchedulePage Modal for the full rationale.
        touchAction: 'none',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={sheetRef} className="w-full sm:max-w-md animate-slide-up modal-sheet"
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          // Top padding moved into sticky header so the header bg covers
          // scrolled content without leaving a gap above it.
          padding: '0 16px calc(env(safe-area-inset-bottom, 0px) + 20px)',
          // May 8 2026 (v2.5.86) — was `85dvh`. Same iOS Safari
          // problem the surrounding wrapper had: dvh doesn't shrink
          // when the keyboard appears (Safari 17+ documented bug
          // referenced in MobileLayout's viewport observer). Using
          // --mobile-h * 0.85 means the modal scales relative to the
          // *visible* area, so when the keyboard is up the modal
          // shrinks to fit instead of running off behind the
          // keyboard. The fallback `100dvh` is used on desktop and
          // on first paint before the JS observer has run.
          maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.85)',
          overflowY: 'auto',
          // touch-action: pan-y + overscroll-behavior: contain handled
          // by .modal-sheet class (with !important) so they override
          // the global * { touch-action: manipulation } rule.
          WebkitOverflowScrolling: 'touch',
        }}>
        <div style={{
          position: 'sticky', top: 0, zIndex: 1,
          background: 'var(--surface)',
          margin: '0 -16px', padding: '16px 16px 12px',
          display: 'flex', alignItems: 'flex-start', gap: 12,
          borderBottom: '1px solid transparent',
        }}>
          <h2 className="section-title text-xl" style={{ flex: 1, margin: 0 }}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              flex: '0 0 auto',
              width: 36, height: 36,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 999,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid var(--border)',
              color: 'var(--text-2)',
              cursor: 'pointer', padding: 0,
              WebkitTapHighlightColor: 'transparent',
            }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ paddingTop: 4 }}>{children}</div>
        {/* v2.5.46 — mobile-only nav-clearance spacer. Bottom nav is
            ~64px + safe area; 70px is the buffer + a few px of breathing
            room. Hidden on sm+ where the modal is centered with no nav. */}
        <div className="sm:hidden" aria-hidden style={{ height: 70 }} />
      </div>
    </div>
  );
}

function CoachNotes({ gameId, teamId, initialNotes, onSaved }) {
  const [notes, setNotes] = useState(initialNotes);
  const [lastSaved, setLastSaved] = useState(initialNotes); // track what's actually persisted
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [open, setOpen] = useState(!!initialNotes);

  // Fix #19: sync displayed notes when parent re-fetches game data,
  // but only if the user hasn't made unsaved edits (notes === lastSaved).
  useEffect(() => {
    if (notes === lastSaved) {
      setNotes(initialNotes ?? '');
      setLastSaved(initialNotes ?? '');
    }
  }, [initialNotes]); // eslint-disable-line react-hooks/exhaustive-deps
  const checkIcon = <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>;
  const noteIcon  = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/teams/${teamId}/games/${gameId}`, { coachNotes: notes });
      setLastSaved(notes);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onSaved?.();
    } catch { toast.error('Failed to save notes'); }
    setSaving(false);
  };

  return (
    <div className="card mb-4">
      <button onClick={() => setOpen(p => !p)} className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--puck)' }}>
          {noteIcon}
          Coach Notes
          <span className="text-xs font-normal text-secondary">(private)</span>
        </div>
        <span className="text-secondary text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="mt-3">
          <textarea
            className="input-field resize-none w-full"
            rows={4}
            placeholder="Private notes for coaches: lineup adjustments, player feedback, tactical notes…"
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
          <button onClick={save} disabled={saving || notes === lastSaved}
            className="btn-ice mt-2 text-sm"
            style={saved ? { background: 'rgba(0,255,136,0.15)', color: 'var(--goal)' } : {}}>
            {saving ? <div className="spinner" style={{ width: 13, height: 13 }} />
              : saved ? <>{checkIcon} Saved!</>
              : 'Save Notes'}
          </button>
        </div>
      )}
    </div>
  );
}
